import { v4 } from 'uuid';
import { createRepo, type RepoAsync, rememberUserLocal, forgetUserLocal, getRememberedName } from "./repoToggle";
import { IUser, IGame, uuid } from "./IUser";
import { LocalRepo } from "./LocalRepo";
import { isSkeechTurn, getSkeechWildcard } from "./rulesManager";
import { recalcAverages } from "./statsManager";
import { BoardModel } from './BoardModel';
import { Renderer } from './Renderer';
import { EventBus } from './events/EventBus';
import { AchievementsTracker } from './achievements/AchievementsTracker';
import { PhysicsEngine, Vec, estimateSpin } from './PhysicsEngine';
import { TutorialManager } from './tutorial/TutorialManager';
import { showChampionCelebration } from './Celebration';
import { LoginManager } from "./LoginManager";
import * as sw from "./serviceWorkerRegistration";
import { LobbyManager, PresenceMeta } from './LobbyManager';
import { ReadyForNext, RollInput, RollResult, GameStateSync, BallSnapshot, PathChunk, PATH_BATCH_CONFIG, BoardMessage } from './MultiPlayerModel';
import { WorldSpace } from './WorldSpace';
import { GameStateManager } from './GameStateManager';
import { sb, SupabaseRepo } from './data';
import { TournamentLobbyController } from './tournaments/TournamentLobbyController';
import { SupabaseRepoAdapter } from './repoToggle';
import { LobbyController } from './LobbyController';
import { messageManager, GameState } from './messageManager';
import { AIPlayer } from './AIPlayer';
import { initSupabaseInterceptor, setNetworkPaused } from './SupabaseInterceptor';
import { TIMING } from './constants/timing';
sw.register();

export {};

type Phase = 'normal' | 'over' | 'retribution' | 'sudden_death';

// Score history for score-by-round widget
export interface TurnScore {
  round: number;
  suddenDeathRound?: number;
  player: 0 | 1;
  points: number;
}

/**
 * Derives turn-by-turn score history from roll history arrays.
 * Groups rolls into turns of 4 balls each and sums points per turn.
 * @param p1Rolls - Player 0's individual roll scores
 * @param p2Rolls - Player 1's individual roll scores
 * @param ballsPerTurn - Number of balls per turn (default 4)
 * @returns Array of turn scores in chronological order
 */
export function deriveTurnScoreHistory(
  p1Rolls: number[] = [],
  p2Rolls: number[] = [],
  ballsPerTurn: number = 4
): TurnScore[] {
  const turnScores: TurnScore[] = [];
  const maxRounds = Math.max(
    Math.ceil(p1Rolls.length / ballsPerTurn),
    Math.ceil(p2Rolls.length / ballsPerTurn)
  );

  for (let round = 0; round < maxRounds; round++) {
    // Player 0's turn
    const p0Start = round * ballsPerTurn;
    const p0End = p0Start + ballsPerTurn;
    const p0TurnRolls = p1Rolls.slice(p0Start, p0End);
    if (p0TurnRolls.length > 0) {
      const p0Points = p0TurnRolls.reduce((sum, pts) => sum + pts, 0);
      turnScores.push({
        round: round + 1,
        player: 0,
        points: p0Points
      });
    }

    // Player 1's turn
    const p1Start = round * ballsPerTurn;
    const p1End = p1Start + ballsPerTurn;
    const p1TurnRolls = p2Rolls.slice(p1Start, p1End);
    if (p1TurnRolls.length > 0) {
      const p1Points = p1TurnRolls.reduce((sum, pts) => sum + pts, 0);
      turnScores.push({
        round: round + 1,
        player: 1,
        points: p1Points
      });
    }
  }

  return turnScores;
}

class Skeech {  
  // Repo & user
  repo: RepoAsync = createRepo();
  currentUser!: IUser;
  lastSeenTime: number = 0;
  private idleTimer: number | null = null;
  private idleWarningTimer: number | null = null;
  private idlePaused = false;
  private idleWarningShown = false;
 
  // ---------------------------
  // Game state
  // ---------------------------
  players = 1;                 // 1 or 2 (future)
  currentPlayer: 0 | 1 = 0;           // 0 or 1
  scores: [number, number] = [0, 0];   // main scores
  target = 21;                 // win target
  bustTo = 15;                 // overshoot penalty
  ballsPerTurn = 4;            // balls per player's turn
  ballsLeft = this.ballsPerTurn;
  round = 1;                   // UI only
  bustHistory: Array<{ player: 0 | 1; round: number }> = [];
  phase: Phase = 'normal';

  // Sudden death state
  suddenDeathScores: [number, number] = [0, 0];  // sudden death round scores
  suddenDeathRound = 1;                  // sudden death round counter

  // Multiplayer turn management
  matchControls: any = null;
  isMultiplayer = false;       // true when in a match
  private currentTournamentContext: { tournamentId: string; round: number; matchIndex: number } | null = null;
  private currentTournamentType: 'bracket' | 'round_robin' | null = null;  // Tournament type (round robin vs bracket)
  private tournamentPlayerRecords: Record<string, { wins: number; losses: number }> = {};  // Tournament-specific W-L
  private suddenDeathRoundJustStarted = false;  // Flag to prevent ballsLeft-- when starting new sudden death round due to tie
  isMyTurn = true;             // am I the active player?
  isHost = false;              // did I send the invite? (host = player 0, guest = player 1)
  lastForfeitTime: number = 0; // Timestamp of last forfeit event received
  phaseHistory: Array<{ phase: 'retribution' | 'sudden_death'; round: number; suddenDeathRound?: number }> = [];
  private finishGamePromise: Promise<any> = Promise.resolve();
  player1: IUser | null = null;
  player2: IUser | null = null;
  opponent: IUser | null = null;
  aiPlayer: AIPlayer | null = null;  // AI opponent for solo games
  private aiGameId: string | null = null;  // Game ID for AI challenge games
  // Prevent presence-driven lobby flashes during tournament navigation
  private suppressLobbyAutoShow: boolean = false;

  // Complete roll histories for accurate PPR calculation (all past games + current game)
  private player1CompleteRollHistory: number[] = [];
  private player2CompleteRollHistory: number[] = [];

  // Tournament-specific roll histories (all games in current tournament)
  private player1TournamentRollHistory: number[] = [];
  private player2TournamentRollHistory: number[] = [];

  // Tutorial manager tracks tutorial flags and renders overlays
  tutorialManager = new TutorialManager();

  // Observer mode
  isObserver = false;              // Am I watching as observer?
  observers: Array<{ userId: string; userName: string }> = [];  // List of current observers
  private observerMatchId: string | null = null;  // Match ID being observed

  // Path recording for smooth multiplayer ball sync (active player)
  private recordedPath: BallSnapshot[] = [];
  private pathRecordingStartTime = 0;
  private isRecordingPath = false;
  private lastPathBatchIndex = 0;
  private nextBatchDelay = PATH_BATCH_CONFIG.INITIAL_DELAY;
  private pathBatchTimer: number | null = null;
  private lastPathRecordTime = 0;  // Throttle recording to reduce jitter
  private readonly pathRecordInterval = 1000 / 30;  // Record at 30fps (every ~33ms)
  private lastRecordedPos: { x: number; y: number } | null = null;
  private stationaryFrameCount = 0;
  private needsFinalBatchSend = false;  // Track if we need to send final batch after state updates
  private pendingPPRUpdate: Promise<void> | null = null;  // Track ongoing PPR calculation

  // Path playback for passive player
  private receivedPath: BallSnapshot[] = [];
  private isPlayingPath = false;
  private pathPlaybackStartTime = 0;
  private pendingRollResult: RollResult | null = null;
  private pendingFinalState: GameStateSync | null = null;
  private playbackWaitTimer: number | null = null;
  private pendingPhaseTransition = false;
  private pendingSuddenDeathTransition = false;
  private suddenDeathAlreadySetup = false;  // Flag to prevent double setup from setTimeout
  private suddenDeathTransitionApplied = false;  // Flag to indicate turn state already updated in startSinkHandledByPhysics
  private pendingBoardMessages: BoardMessage[] = [];  // Queue of messages to display during playback
  private displayedMessageTimestamps: Set<number> = new Set();  // Track displayed messages to avoid duplicates
  private eventBus = new EventBus();
  private achievementsTracker = new AchievementsTracker(this.eventBus);

  // Ready synchronization
  private opponentReady = false;
  private waitingForOpponentReady = false;
  private pendingTurnTransition = false; // True when we need to wait for opponent to finish watching before transitioning
  private pendingTurnTransitionTimer: number | null = null; // Safety fallback timer for pending turn transition
  private opponentInRoom: boolean = true; // Presence: is the opponent currently in the match room
  private lastOpponentLeftTime: number = 0;
  // Multiplayer appearance: per-player ball colors cached at match start
  private p1BallColor: string = '#ff6b35';
  private p2BallColor: string = '#ff6b35';

  // Per-turn bookkeeping
  turnHits: number[] = [];
  turnHasFailed = false; // If true, a skroll is impossible this turn
  isSkeechPossible = false; // True if skroll is possible this turn
  skeechSuccess = false; // True if a skrol was just achieved
  wildcardHit: number | null = null;
  rollsThisGame = 0;
  lastRollPts = 0; // Points from the last roll (for syncing to opponent)
  lastRollResult: 'hole' | 'ditch' | 'stop' = 'stop'; // Result of last roll (for passive player animations)
  private lastRollPlayerIndex: 0 | 1 | null = null;
  skeechesThisGame = 0;
  consecutiveDitches = 0;
  // Track last launch speed for awards (fastest/slowest)
  private lastLaunchSpeed: number | null = null;

  // Ball physics
  ball = { pos: { x: 0, y: 0 } as Vec, vel: { x: 0, y: 0 } as Vec, r: 10, spin: 0, rolling: false, targetPos: undefined as {x: number, y: number} | undefined };  
  baseBallR = 10; //ball size base
  
  // Input
  isAiming = false;
  gesture: { points: Vec[]; start?: Vec; end?: Vec; tStart?: number; tEnd?: number } = { points: [] };

  raf = 0;

  // Test mode
  testMode = false;
  testBaseTilt = 0;  // Randomized tilt for this test session
  testParams = {
    speed: 455,
    vx: 0,
    vy: -455,
    spin: 0,
    tiltX: 0,  // Offset to add to randomized tilt
  };
  testStats = {
    totalRolls: 0,
    apollo13Count: 0,
    rollsWithMoreThan025Orbit: 0,
    rollsWithMoreThan025OrbitThatSank: 0,
    rollsWithMoreThan025OrbitThatEscaped: 0,
    rollsWithMoreThan1Orbit: 0,
    rollsWithMoreThan1OrbitThatSank: 0,
    rollsWithMoreThan1OrbitThatEscaped: 0,
    railroadCount: 0,
    lastOrbitFrames: 0,
    maxOrbitFrames: 0,
  };

  board = new BoardModel();
  world = new WorldSpace();
  renderer = new Renderer(this.board, this.world, this.players, this.ballsLeft, this.currentUser, this.ball, this.round);
  physics = new PhysicsEngine(this.board, this.ball, this.world);
  loginManager = new LoginManager(this.currentUser, this.repo);
  lobbyManager = new LobbyManager(this.repo);
  lobbyController!: LobbyController;
  gameStateManager = new GameStateManager(sb);

  myRollResult: RollResult = {
    ballIndex: 0,
    kind: 'roll_result',
    round: 0,
    result: 'timeout',
    gameState: {
        scores: [0,0],
        ballsLeft: 0,
        currentPlayer: 0,
        round: 0,
        turnHits: []
      }
  };
  opponentRollResult!: RollResult;
  pendingBallReset = false;
  private offlineWarningShown = false;

  constructor() {
    this.renderer.setTutorialManager(this.tutorialManager);
    this.tutorialManager.setTiltHandler((tilt) => {
      this.physics.tiltX = tilt;
      this.physics.amountOfRandomTilt = 0;
    });
    this.tutorialManager.setLaunchHandler(({ vx, vy, spin }) => {
      this.resetBall();
      this.ball.vel = { x: vx, y: vy };
      this.ball.spin = spin;
      this.ball.rolling = true;
      this.renderer.updateNextBallButtonState();
      const totalSpeed = Math.hypot(vx, vy);
      this.renderer.updateSpeedWidgets(totalSpeed);
    });
    this.tutorialManager.setMessageHandler((text, messageData) => this.renderer.showPositionedMessage(text, messageData));
    this.tutorialManager.setClearMessagesHandler(() => this.renderer.clearPinballMessages());
    this.tutorialManager.setProfileGuideHandler(async () => {
      if (!this.currentUser) return;
      try {
        await this.renderer.profileManager.showUserProfile(this.currentUser.id, this.currentUser.userName);
        await this.renderer.profileManager.playProfileTutorialSequence();
        console.log('after tutorial is it my turn, yet? ' + this.isMyTurn);
        while (!this.isMyTurn) {
          console.log('oops, robo was not finished!');
          await this.delay(250);
        }
        this.tutorialManager.startCurveSequence();
      } catch (error) {
        console.error('[tutorial] failed to show profile guide', error);
      }
    });
    this.tutorialManager.setInteractionHandler(blocked => this.renderer.setTutorialBlocking(blocked));
  }

  private delay(ms: number) {
    return new Promise<void>((resolve) => setTimeout(resolve, ms));
  }

  private setupIdleDetection() {
    const reset = () => this.resetIdleTimer();
    ['pointerdown', 'touchstart', 'keydown', 'mousemove'].forEach(evt => {
      window.addEventListener(evt, reset, { passive: true });
    });
    this.resetIdleTimer();
  }

  private resetIdleTimer() {
    if (this.idlePaused) return;

    // Clear any existing timers
    if (this.idleTimer !== null) {
      window.clearTimeout(this.idleTimer);
    }
    if (this.idleWarningTimer !== null) {
      window.clearTimeout(this.idleWarningTimer);
      this.idleWarningTimer = null;
    }

    // Reset warning state
    this.idleWarningShown = false;

    // Set timer to show warning at 4.5 minutes
    // Note: handleIdleWarning will check if warning should be shown (skips if not logged in or observing)
    this.idleTimer = window.setTimeout(() => this.handleIdleWarning(), TIMING.IDLE_WARNING_MS);
  }

  private handleIdleWarning() {
    if (this.idlePaused) return;
    // Don't warn if user isn't logged in or is observing a game
    if (!this.currentUser || this.isObserver) return;
    if (this.idleWarningShown) return;

    this.idleWarningShown = true;
    console.warn('[idle] Showing inactivity warning (30 seconds until pause)');

    try {
      this.renderer.showModal({
        title: 'Inactivity Warning',
        body: 'Will pause for inactivity in 30 seconds. We will stop network calls after 5 minutes idle. Tap to remain active.',
        buttonIcon: '👍',
        onClose: () => {
          // User dismissed warning - reset idle timer
          this.resetIdleTimer();
        }
      });
    } catch {}

    // Set 30-second timer to actually pause if not dismissed
    this.idleWarningTimer = window.setTimeout(() => this.handleIdleTimeout(), TIMING.IDLE_WARNING_DURATION_MS);
  }

  private handleIdleTimeout() {
    if (this.idlePaused) return;
    // Don't pause if user isn't logged in or is observing a game
    if (!this.currentUser || this.isObserver) return;

    this.idlePaused = true;
    console.warn('[idle] Pausing network after inactivity');
    setNetworkPaused(true);
    try {
      const channels = (sb as any)?.getChannels?.() || [];
      channels.forEach((ch: any) => (sb as any)?.removeChannel?.(ch));
      (sb as any)?.realtime?.disconnect?.();
    } catch (err) {
      console.warn('[idle] Failed to detach realtime channels', err);
    }
    try {
      // Update the modal to show paused state
      this.renderer.showModal({
        title: 'Paused for inactivity',
        body: 'We stopped network calls after 5 minutes idle. Tap to reload and resume.',
        buttonIcon: '⏯️',
        onClose: () => window.location.reload()
      });
    } catch {}
  }

  async init() {
    // Initialize Supabase network interceptor with renderer for modal display
    initSupabaseInterceptor(this.renderer);
    this.setupIdleDetection();

    this.renderer.setHandlers({
      showUserProfile: async (userId: string, userName: string) => {
        // Show profile for any user (including current user or opponents)
        this.renderer.profileManager.showUserProfile(userId, userName);
      },
      resetGame: async () => await this.resetGame(),
      nextBall: () => this.nextBall('ditch'),
      forgetUserLocal: () => this.handleForgetUser(),
        lobbySolo: async () => {
        this.renderer.isAIGame = true;
        (this.renderer as any).testMode = false; // Ensure test mode is off for AI Challenge
        this.renderer.hideLobby();
        this.lobbyController?.hide();
        await this.startAIChallenge(); // Start an AI challenge game
      },
      lobbyFindMatch: async () => {
        await this.lobbyManager.startMatchSearch();
      },
      getUserPreferences: async (userId: string) => {
        return await this.repo.getUserPreferences(userId);
      },
      updateUserPreferences: async (userId: string, prefs: any) => {
        return await this.repo.upsertUserPreferences(userId, prefs);
      },
      getUserData: async (userId: string) => {
        return await this.repo.getUserById(userId);
      },
      updateUserData: async (userId: string, userData: Partial<IUser>) => {
        return await this.repo.updateUser(userId, userData);
      },
      uploadProfileImage: async (userId: string, file: File) => this.handleUploadProfileImage(userId, file),
      listGamesByUser: async (userId: string) => {
        return await this.repo.listGamesByUser(userId);
      },
      calculateUserPPR: async (userId: string) => {
        return await this.repo.calculateUserPPR(userId);
      },
      calculateWinLoss: async (userId: string, options?: { excludeTournaments?: boolean; excludeAI?: boolean }) => {
        return await this.repo.calculateWinLoss(userId, options);
      },
      getUserTournamentParticipations: async (userId: string) => {
        return await this.repo.getUserTournamentParticipations(userId);
      },
      getTournamentPlayersWithStats: async (tournamentId: string) => {
        return await this.repo.getTournamentPlayersWithStats(tournamentId);
      },
      saveTournamentAwardWinners: async (tournamentId: string, awards: any) => {
        return await (this.repo as any).sb.saveTournamentAwardWinners(tournamentId, awards);
      },
      onUsernameChanged: (newUsername: string) => this.handleUsernameChanged(newUsername),
      onCurrentUserUpdated: (userData: Partial<IUser>) => this.handleCurrentUserUpdated(userData),
      lobbyInvite: (target) => this.handleLobbyInvite(target),
      refreshLobby: () => this.handleRefreshLobby(),
      update: (n: number) => this.update(n),      
      requestRematch: async () => await this.requestRematch(),
      requestRestart: async () => await this.requestRestart(),
      requestQuit: () => this.requestQuit(),
      returnToLobby: () => this.returnToLobby(),
      getGameById: async (gameId: string) => await (this.repo as any).sb.getGameById(gameId),
      createTournament: async ({ name, joinCode, maxPlayers, type }) => {
        // Try a few times in case of join_code uniqueness races
        const attempts = 4;
        let lastErr: any = null;
        let code = joinCode;
        for (let i = 0; i < attempts; i++) {
          try {
            const res = await this.repo.createTournament({
              name,
              creatorId: this.currentUser.id,
              maxPlayers: maxPlayers ?? 16,
              joinCode: code,
              type: type ?? 'bracket',
            });
            return { id: res.id, name: res.name, join_code: res.join_code };
          } catch (e: any) {
            lastErr = e;
            // if unique violation on join_code, regenerate and retry
            const message = (e?.message || '').toLowerCase();
            if (message.includes('duplicate') || message.includes('unique')) {
              // generate a new code on collision
              code = this.randomFour();
              continue;
            }
            break;
          }
        }
        throw lastErr || new Error('createTournament failed');
      },
      goToTournamentLobby: (tournamentId: string) => {
        // Proactively hide any underlying UIs to avoid visual mesh/race conditions
        // and temporarily suppress presence-driven lobby auto-show until tourney view mounts
        this.suppressLobbyAutoShow = true;
        (window as any).suppressLobbyAutoShow = true;
        try { this.renderer.hideOverlay(); } catch {}
        try { this.renderer.hideLobby(); } catch {}
        const mainLobby = document.getElementById('skeech-lobby');
        if (mainLobby) mainLobby.style.display = 'none';
        const boardRoot = document.getElementById('skeech-root');
        if (boardRoot) boardRoot.style.display = 'none';
        // Clean up any lingering tournament DOM before creating a new controller
        Array.from(document.querySelectorAll('#tourney-lobby')).forEach((el) => {
          try { el.parentElement?.removeChild(el); } catch {}
        });
        if ((this as any).tournamentLobby) {
          try { (this as any).tournamentLobby.destroy(); } catch {}
          (this as any).tournamentLobby = undefined;
        }
        (this as any).tournamentLobby = new TournamentLobbyController({
          getTournamentById: async (id: string) => await (this.repo as any).sb.getTournamentById(id),
          listTournamentPlayers: async (id: string) => await (this.repo as any).sb.listTournamentPlayers(id),
          getUserById: async (id: string) => await (this.repo as any).sb.getUserById(id),
          leaveTournament: async (tid: string, uid: string) => await (this.repo as any).sb.leaveTournament(tid, uid),
          startTournament: async (tid: string) => await (this.repo as any).sb.startTournamentWithBracket(tid),
          startRoundRobinTournament: async (tid: string) => await (this.repo as any).sb.startRoundRobinTournament(tid),
          applyBracketAutoAdvances: async (tid: string) => await (this.repo as any).sb.applyBracketAutoAdvances(tid),
          getGameById: async (gid: string) => await (this.repo as any).sb.getGameById(gid),
          recordTournamentMatchResult: async (args) => await (this.repo as any).sb.recordTournamentMatchResult(args),
          calculateUserPPRForTournament: async (tid: string, uid: string) => await (this.repo as any).sb.calculateUserPPRForTournament(tid, uid),
          calculateStandingsFromRollHistories: async (tid: string, matches: any) => await (this.repo as any).sb.calculateStandingsFromRollHistories(tid, matches),
          upsertTournamentPlayerStats: async (tid: string, uid: string, delta: any) => await (this.repo as any).sb.upsertTournamentPlayerStats(tid, uid, delta),
          resumeGame: async (gid: string) => {
            try {
              const tl = (this as any).tournamentLobby;
              if (tl && typeof tl.destroy === 'function') {
                try { tl.destroy(); } catch {}
                (this as any).tournamentLobby = undefined;
              }
              await this.loadGame(gid);
            } catch (e) {
              console.error('Failed to resume game', e);
            }
          },
          findLatestGameBetweenUsers: async (a: string, b: string) => await (this.repo as any).sb.findLatestGameBetweenUsers(a, b),
          findTournamentGame: async (a: string, b: string, tid: string, round: number, matchIdx: number) => await (this.repo as any).sb.findTournamentGame(a, b, tid, round, matchIdx),
          getTournamentMatchGame: async (tid: string, round: number, matchIdx: number) => await (this.repo as any).sb.getTournamentMatchGame(tid, round, matchIdx),
          initializeAsObserver: async (matchId: string, player1: any, player2: any, opts?: any) => {
            await this.initializeAsObserver(matchId, player1, player2, opts);
          },
          showStartMatchModal: async ({ meId, opponentId, title, matchId, tournamentId, round, matchIndex, startingPlayerId }) => {
            try {
              const me = await (this.repo as any).sb.getUserById(meId);
              const opp = await (this.repo as any).sb.getUserById(opponentId);

              // For tournament games, use tournament-specific PPR
              const myPPR = tournamentId
                ? await (this.repo as any).sb.calculateUserPPRForTournament(tournamentId, meId)
                : await this.repo.calculateUserPPR(meId);
              const oppPPR = tournamentId
                ? await (this.repo as any).sb.calculateUserPPRForTournament(tournamentId, opponentId)
                : await this.repo.calculateUserPPR(opponentId);

              const currentMeta = { userName: me?.user_name || 'You', wins: me?.wins || 0, losses: me?.losses || 0, avgPPR: myPPR };
              const oppMeta = { userName: opp?.user_name || 'Opponent', wins: opp?.wins || 0, losses: opp?.losses || 0, avgPPR: oppPPR };
              // Kick off a direct match flow in LobbyManager using the same ready protocol
              const opponentPresence = { userId: opponentId, userName: oppMeta.userName, wins: oppMeta.wins, losses: oppMeta.losses, avgPPR: oppMeta.avgPPR } as any;
              // Stash tournament context so matchStart can create the game row with tournament fields
              (this as any).pendingTournamentContext = { tournamentId, round, matchIndex };

              // Update current user with tournament PPR if applicable
              if (tournamentId) {
                this.currentUser.avgPPR = myPPR;
              }

              await this.lobbyManager.registerTournamentClick(opponentPresence, { matchId: matchId as string, titleText: title, startingPlayerId, isTournament: !!tournamentId, tournamentId });
            } catch (e) {
              console.error('Failed to show start match modal', e);
            }
          },
          showUserProfile: (userId: string, userName: string) => this.renderer.profileManager.showUserProfile(userId, userName)
        });
        (this as any).lastTournamentId = tournamentId;
        (this as any).tournamentLobby.open(tournamentId, this.currentUser.id);
      },
      joinTournamentByCode: async (code: string) => {
        // Verify + join + assign next seed
        const { tournament, player } = await this.repo.joinTournamentByCode(this.currentUser.id, code);
        return { tournamentId: tournament.id, name: tournament.name };
      },
      listMyTournaments: async () => {
        const rows = await this.repo.listUserTournaments(this.currentUser.id);
        return rows.map((t: any) => ({
          id: t.id,
          name: t.name,
          status: t.status,
          bracket_data: t.bracket_data,
          creator_id: t.creator_id,
        }));
      },
      exitObserverMode: () => this.exitObserverMode(),
      input: {
        down: (p, t) => this.onDownFromRenderer(p, t),
        move: (p, t) => this.onMoveFromRenderer(p, t),
        up:   (p, t) => this.onUpFromRenderer(p, t),
      }
    });
    
    this.lobbyManager.setHandlers({
      showLobby: (users: PresenceMeta[]) => {
        if (this.tutorialManager.isActive) return;
        const tl = document.getElementById('tourney-lobby');
        // Do not auto-show lobby if tournament view or an active match is up
        if (tl || this.matchControls || this.isMultiplayer) return;
        if (this.suppressLobbyAutoShow || (window as any).suppressLobbyAutoShow) return;
        this.renderer.showLobby(users);
      },
      showInvite: (fromName: string, accept: ()=>void, decline: ()=>void) => this.renderer.showIncomingInvite(fromName,accept, decline),
      showInviteDeclined: (fromName: string) => this.renderer.showInviteDeclined(fromName),
      showInviteCancelled: (fromName: string) => this.renderer.showInviteCancelled(fromName),
      showRestartRequest: (fromName: string, accept: () => void, decline: () => void) => {
        this.renderer.showRestartRequest(fromName, accept, decline);
      },
      onRestartAccepted: async () => {
        await this.resetGame();
      },
      showRestartAccepted: async (fromName: string) => {
        this.renderer.hideOverlay();
        this.renderer.showRestartAccepted(fromName);
        await this.resetGame();
      },
      showRestartDeclined: (fromName: string) => {
        this.renderer.hideOverlay();
        this.renderer.showRestartDeclined(fromName);
      },
      showRestartCancelled: () => {
        this.renderer.hideOverlay();
      },
      showOpponentQuit: (opponentName: string) => this.handleOpponentQuit(opponentName),
      startMatch: (matchId: string, player1: { userId: string, userName: string }, player2: { userId: string, userName: string }) => {
        // If tournament view is open, close it before entering the game
        const tl = (this as any).tournamentLobby;
        if (tl && typeof tl.destroy === 'function') {
          try { tl.destroy(); } catch {}
          (this as any).tournamentLobby = undefined;
        }
        // Ensure presence mode and UI reflect match entry on both clients
        this.lobbyManager.updateMode('in_match');
        this.renderer.hideLobby();
        this.lobbyController?.hide?.();
        this.matchStart(matchId, player1, player2);
      },
      onOpponentRoll: (rollInput: RollInput) => this.handleOpponentRoll(rollInput),      
      onPathChunk: (chunk: PathChunk) => this.handlePathChunk(chunk),
      onBoardMessage: (message: BoardMessage) => this.handleBoardMessage(message),
      showMatchSearching: (cancelSearch: () => void) => {
        this.renderer.showMatchSearching(cancelSearch);
      },
      showAIPrompt: () => {
        this.renderer.showAIPromptInSearchModal();
      },
      onMatchFound: (opponent: PresenceMeta, matchId: string) => {
        this.renderer.hideSearchingDialog();        
      },
      showMatchIntroduction: (currentPlayer: PresenceMeta, opponent: PresenceMeta, isStarting: boolean, matchId: string, onStartMatch: () => void, onCancel: () => void, titleText?: string, hideWL?: boolean) => {
        this.renderer.showMatchIntroduction(currentPlayer, opponent, isStarting, onStartMatch, onCancel, titleText, hideWL);
      },
      showMatchCancelled: (fromName: string, onClose?: () => void) => {
        this.renderer.showMatchCancelled(fromName, onClose);
      },
      onReadyForNext: (ready: ReadyForNext) => this.handleReadyForNext(ready),
      onUserLeft: (userName: string) => {
        console.log('[presence] Opponent left room:', userName);
        this.opponentInRoom = false;
        this.lastOpponentLeftTime = performance.now();
        // Safety net: if we were passively playing back their roll and they left mid-roll,
        // void the roll (no score change, no decrement). Do NOT force a turn
        // transition here; the current player keeps the turn with remaining balls.
        if (this.isMultiplayer && !this.isMyTurn && this.isPlayingPath) {
          console.log('[presence] Opponent left mid-roll; aborting path playback and transitioning turn');
          this.isPlayingPath = false;
          this.receivedPath = [];
          this.ball.rolling = false;
          // Do not apply any pending roll result; drop it.
          this.pendingRollResult = null;
          // Clean up board message queues
          this.pendingBoardMessages = [];
          this.displayedMessageTimestamps.clear();
          // Re-establish a clean pre-roll state for the current player
          this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
          this.renderer.setReserveAtLaunch(true);
          this.resetBall();
          // Persist state so the game list reflects accurate turn/ballsLeft
          this.saveGameStateToDb();
        }
      },
      onObserverJoin: (observer: { userId: string; userName: string }) => {
        console.log('[Observer] Observer joined:', observer);
        this.observers.push(observer);
      },
      onObserverLeave: (observer: { userId: string; userName: string }) => {
        console.log('[Observer] Observer left:', observer);
        this.observers = this.observers.filter(o => o.userId !== observer.userId);
      }
    });

    // Initialize testMode to false on renderer
    (this.renderer as any).testMode = false;

    const loginResult = await this.loginManager.initLoginFlow();
    this.currentUser = loginResult.user;
    if(!this.currentUser) return;
    this.lastSeenTime = new Date(localStorage.getItem('lastSeenTime') || 0).getTime();    
    this.renderer.buildDOM();
    const createdAtMs = new Date(this.currentUser.createdAt).getTime();
    const diff = Date.now() - createdAtMs;
    const shouldRunTutorial = createdAtMs > 0 && diff <= 10000;
    this.tutorialManager.setActive(shouldRunTutorial);    
    
    
    this.renderer.setCurrentUser(this.currentUser);
    const lobbyEl = document.getElementById('skeech-lobby')!;
    this.lobbyController = new LobbyController(this.repo, lobbyEl, {
        loadGame: (gameId) => this.loadGame(gameId),
        forfeitGame: (gameId, isMyTurn, updatedAt) => this.handleForfeitRequest(gameId, isMyTurn, updatedAt)
    }, this.lastSeenTime);
    if (shouldRunTutorial) {
      this.renderer.hideLobby();
      this.lobbyController?.hide();
      await this.startAIChallenge(true); // Start with tutorial
    }    
    if (!this.tutorialManager.isActive) {
      this.lobbyManager.userJoin(this.currentUser);
      await this.lobbyManager.initialSyncComplete;
      this.lobbyController.enterLobby(this.currentUser);
    }
    localStorage.setItem('lastSeenTime', new Date().toISOString());
    //this.renderer.updatePlayerCards(this.currentUser);
    //this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.currentUser, undefined, this.players, this.phase, this.bustHistory);


    // Expose app instance globally for widgets
    (window as any).skeechApp = this;

    // Force tutorial mode for every login while testing
    //this.renderer.isAIGame = true;
    (this.renderer as any).testMode = false;
    
  }

  private randomFour(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let s = '';
    for (let i = 0; i < 4; i++) s += chars[Math.floor(Math.random() * chars.length)];
    return s;
  }

  async handleForfeitRequest(gameId: string, isMyTurn: boolean, updatedAt: string) {
    const now = new Date().getTime();
    const lastMove = new Date(updatedAt).getTime();
    const hoursDiff = (now - lastMove) / (1000 * 60 * 60);   
    const canForceForfeit = !isMyTurn && hoursDiff > 48;
    const body = canForceForfeit
        ? "It has been your opponent's turn for over 48 hours. Force them to forfeit?"
        : "Are you sure you want to forfeit this game? This cannot be undone.";
    const confirmText = canForceForfeit ? "ÃƒÂ°Ã…Â¸Ã‚ÂÃ¢â‚¬Â " : "ÃƒÂ°Ã…Â¸Ã‚ÂÃ‚Â³ÃƒÂ¯Ã‚Â¸Ã‚Â";

    this.renderer.showTwoButtonDialog(
        body,
        "ÃƒÂ¢Ã‚ÂÃ…â€™",
        () => { this.renderer.hideOverlay() },
        confirmText,
        async () => {
            try {
              // Best-effort: persist whatever roll histories we have prior to marking forfeited
              const payload: any = {};
              if (this.player1?.current_game_rolls) payload.p1_roll_history = this.player1.current_game_rolls;
              if (this.player2?.current_game_rolls) payload.p2_roll_history = this.player2.current_game_rolls;
              if (Object.keys(payload).length > 0) {
                try { await this.repo.updateGame(gameId, payload); } catch (e) { console.warn('Pre-forfeit history persist failed', e); }
              }

              // Determine who is forfeiting
              // If canForceForfeit is true, the opponent is forfeiting (it's been their turn for 48+ hours)
              // Otherwise, the current user is forfeiting
              const game = await this.repo.getGameById(gameId);
              if (!game) {
                throw new Error('Game not found');
              }

              let forfeiterUserId: string;
              if (canForceForfeit) {
                // Force opponent to forfeit - opponent is whoever's turn it is (not us)
                forfeiterUserId = game.p1_user_id === this.currentUser.id ? game.p2_user_id : game.p1_user_id;
              } else {
                // Current user is forfeiting
                forfeiterUserId = this.currentUser.id;
              }

              console.log(`[handleForfeitRequest] Forfeiting game ${gameId}, forfeiter: ${forfeiterUserId}, canForceForfeit: ${canForceForfeit}`);
              await this.repo.forfeitGame(gameId, forfeiterUserId);
            } finally {
              this.renderer.hideOverlay();
              // Refresh the active games list
              await this.lobbyController.refreshGames();
            }
        }
    );
  }

  //this is a handler from the lobby manager. this will get called when something happens in the lobby that initiates a match
  async matchStart(matchId: string, player1Info: { userId: string, userName: string }, player2Info: { userId: string, userName: string }) {
    // Reset per-match trackers
    this.achievementsTracker.reset();
    this.renderer.isAIGame = false;
    (this.renderer as any).testMode = false; // Ensure test mode is off for multiplayer
    this.renderer.hideOverlay();
    this.renderer.hideLobby();
    this.lobbyController?.hide();
    await this.resetGame();    
    await this.lobbyManager.updateMode('in_match');
    this.renderer.isAIGame = false;    
    this.isMultiplayer = true;
    this.players = 2;    
    const p1 = await this.repo.getUserById(player1Info.userId);
    const p2 = await this.repo.getUserById(player2Info.userId);
    if (!p1 || !p2) {
      console.error("Could not fetch player data for match start");
      this.returnToLobby();
      return;
    }

    // Calculate W-L for both players for in-game display
    try {
      console.log('[matchStart] Calculating W-L for in-game display');
      const p1WL = await this.repo.calculateWinLoss(p1.id, { excludeTournaments: true, excludeAI: true });
      const p2WL = await this.repo.calculateWinLoss(p2.id, { excludeTournaments: true, excludeAI: true });
      p1.wins = p1WL.wins;
      p1.losses = p1WL.losses;
      p1.gamesPlayed = p1WL.gamesPlayed;
      p2.wins = p2WL.wins;
      p2.losses = p2WL.losses;
      p2.gamesPlayed = p2WL.gamesPlayed;
      console.log('[matchStart] Player 1 W-L:', p1WL);
      console.log('[matchStart] Player 2 W-L:', p2WL);
    } catch (e) {
      console.error('[matchStart] Failed to calculate W-L for in-game display', e);
    }

    this.player1 = p1;
    this.player2 = p2;
    this.opponent = this.currentUser.id === p1.id ? p2 : p1;

    console.log('[matchStart] Player assignments:', {
      currentUserId: this.currentUser.id,
      currentUserName: this.currentUser.userName,
      player1Id: p1.id,
      player1Name: p1.userName,
      player2Id: p2.id,
      player2Name: p2.userName,
      opponentId: this.opponent.id,
      opponentName: this.opponent.userName
    });
    this.isHost = this.lobbyManager.isGameHost;
    if (this.aiPlayer) {
        this.isHost = true;
    }
    // Guest (person who received invite) goes first - guest is player 0
    this.currentPlayer = 0;  // Always start with player 0 (guest or user in AI game)
    this.isMyTurn = this.aiPlayer ? true : !this.isHost;
    this.renderer.setIsMyTurn(this.isMyTurn);
    // Board color = host's preference; Ball color = per player
    try {
      const hostUserId = this.isHost ? this.currentUser.id : (this.opponent?.id || this.player2?.id);
      if (hostUserId) {
        await this.renderer.loadGamePreferences(hostUserId);
      }
      // Cache both players' ball colors
      try {
        const p1Prefs = await this.renderer.profileManager.loadGamePreferences(this.player1.id);
        const p2Prefs = await this.renderer.profileManager.loadGamePreferences(this.player2.id);
        this.p1BallColor = p1Prefs?.ballColor || this.p1BallColor;
        this.p2BallColor = p2Prefs?.ballColor || this.p2BallColor;
      } catch {}
      // Apply color for the starting roller (player 0)
      this.setBallColorForCurrentPlayer();
    } catch {}
    // Check tournament context before host-only game creation
    const tctx = (this as any).pendingTournamentContext as { tournamentId: string; round: number; matchIndex: number } | undefined;
    // Persist context for navigation/menu logic (for BOTH host and guest)
    this.currentTournamentContext = tctx ? { ...tctx } : null;

    // Load tournament data including type and W-L records
    if (tctx) {
      try {
        const tournament = await (this.repo as any).sb?.getTournamentById(tctx.tournamentId);
        if (tournament) {
          this.currentTournamentType = tournament.tournament_type as 'bracket' | 'round_robin' || 'bracket';

          // For round-robin tournaments, load player W-L records from standings
          if (this.currentTournamentType === 'round_robin' && tournament.round_robin_data?.standings) {
            const standings = tournament.round_robin_data.standings;
            standings.forEach((entry: any) => {
              this.tournamentPlayerRecords[entry.userId] = {
                wins: entry.wins ?? 0,
                losses: entry.losses ?? 0
              };
            });
            console.log('[matchStart] Loaded round-robin W-L records:', this.tournamentPlayerRecords);
          }
        }
      } catch (e) {
        console.error('[matchStart] Failed to load tournament data:', e);
      }
    }

    // Recalculate PPR based on game type
    if (tctx && (this.repo as any).sb?.getTournamentRollHistories) {
      // For tournament games, fetch complete tournament roll histories for accurate PPR tracking
      // This includes all games in the tournament (completed + in-progress)
      try {
        const [p1History, p2History] = await Promise.all([
          (this.repo as any).sb.getTournamentRollHistories(tctx.tournamentId, this.player1.id),
          (this.repo as any).sb.getTournamentRollHistories(tctx.tournamentId, this.player2.id)
        ]);

        this.player1TournamentRollHistory = p1History;
        this.player2TournamentRollHistory = p2History;

        // Calculate initial PPR from tournament histories
        const p1PPR = p1History.length > 0
          ? +(p1History.reduce((a: number, b: number) => a + b, 0) / p1History.length).toFixed(3)
          : 0;
        const p2PPR = p2History.length > 0
          ? +(p2History.reduce((a: number, b: number) => a + b, 0) / p2History.length).toFixed(3)
          : 0;

        this.player1.avgPPR = p1PPR;
        this.player2.avgPPR = p2PPR;

        // Update currentUser if they're one of the players
        if (this.currentUser.id === this.player1.id) {
          this.currentUser.avgPPR = p1PPR;
        } else if (this.currentUser.id === this.player2.id) {
          this.currentUser.avgPPR = p2PPR;
        }

        console.log('[matchStart] Loaded tournament roll histories:', {
          tournamentId: tctx.tournamentId,
          p1Rolls: p1History.length,
          p2Rolls: p2History.length,
          p1PPR,
          p2PPR
        });
      } catch (e) {
        console.error('Failed to fetch tournament roll histories at game start', e);
      }
    } else {
      // For regular multiplayer games, fetch complete roll histories for accurate PPR tracking
      // This avoids fetching all games after every roll
      try {
        // Fetch roll histories efficiently (just roll arrays, not full game data)
        const [p1History, p2History] = await Promise.all([
          (this.repo as any).sb?.getUserRollHistories?.(this.player1.id) ?? [],
          (this.repo as any).sb?.getUserRollHistories?.(this.player2.id) ?? []
        ]);

        this.player1CompleteRollHistory = p1History;
        this.player2CompleteRollHistory = p2History;

        // Calculate initial PPR from histories
        const p1PPR = p1History.length > 0
          ? +(p1History.reduce((a: number, b: number) => a + b, 0) / p1History.length).toFixed(3)
          : 0;
        const p2PPR = p2History.length > 0
          ? +(p2History.reduce((a: number, b: number) => a + b, 0) / p2History.length).toFixed(3)
          : 0;

        this.player1.avgPPR = p1PPR;
        this.player2.avgPPR = p2PPR;

        // Update currentUser if they're one of the players
        if (this.currentUser.id === this.player1.id) {
          this.currentUser.avgPPR = p1PPR;
        } else if (this.currentUser.id === this.player2.id) {
          this.currentUser.avgPPR = p2PPR;
        }

        console.log('[matchStart] Loaded roll histories:', {
          p1Rolls: p1History.length,
          p2Rolls: p2History.length,
          p1PPR,
          p2PPR
        });
      } catch (e) {
        console.error('Failed to fetch roll histories at game start', e);
      }
    }
    // Clear pending context after reading
    (this as any).pendingTournamentContext = undefined;

    if (this.isHost) {
      const initialGame: IGame = {
        id: matchId,
        isSoloGame: false,
        p1UserId: this.player1.id,
        p2UserId: this.player2.id,
        p1Score: 0,
        p2Score: 0,
        rounds: 1,
        rolls: 0,
        points: 0,
        skeeches: 0,
        timeStamp: Date.now(),
        status: 'active',
        game_state_json: this.getCurrentGameState(),
      };
      // If this match was launched from a tournament, include tournament context
      const initialWithTournament = tctx ? { ...initialGame, tournament_id: tctx.tournamentId, tournament_round: tctx.round, tournament_match: tctx.matchIndex } : initialGame;

      try {
        await this.repo.addGame(initialWithTournament as any);
        console.log('[matchStart] Game created successfully:', matchId);
      } catch (error) {
        console.error('[matchStart] Failed to create game:', matchId, error);
      }
    } else {
      console.log('[matchStart] Not host, skipping game creation. Host should create:', matchId);
    }
    
    this.world.setAspect(this.board.aspect);
    this.world.framePad       = this.board.framePad;
    this.world.moatPad        = this.board.moatPad;
    this.world.playPad        = this.board.playPad;
    this.world.slabHeightRatio= this.board.slabHeightRatio;
    // Initialize balls-left indicator before animating to launch
    this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
    this.resetBall();
    this.matchControls = await this.lobbyManager.startMatch(matchId);
    this.renderer.setCurrentMatchId(matchId);
    this.opponentInRoom = true; // Reset presence flag on (re)join
    // If match was started as a tournament game or tournament lobby is open, update renderer menu mode
    const tournamentLobbyOpen = !!(this as any).tournamentLobby;
    this.renderer.setTournamentMode(!!this.currentTournamentContext || tournamentLobbyOpen);

    // Pass tournament-specific W-L records to renderer for round-robin tournaments
    // IMPORTANT: Must be set BEFORE updatePlayerCards() so W-L displays correctly
    if (this.currentTournamentType === 'round_robin') {
      this.renderer.setTournamentPlayerRecords(this.tournamentPlayerRecords);
    }

    this.renderer.updatePlayerCards(this.player1, this.player2);

    this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
    const initialPlayerName = this.currentPlayer === 0 ? this.player1?.userName || 'Player 1' : this.player2?.userName || 'Player 2';
    const initialPlayerId = this.currentPlayer === 0 ? this.player1?.id : this.player2?.id;
    const isInitialPlayerCurrentUser = initialPlayerId === this.currentUser.id;
    this.renderer.setTurnIndicator(true, initialPlayerName, isInitialPlayerCurrentUser);
    this.renderer.startPinballLightShow();
    this.renderer.triggerGameStartRipple();
    this.renderer.showMatchIntroOnBoard(this.player1.userName, this.player2.userName, 3000);
    this.renderer.showBoard();
  }

  // this is called when resuming a previously saved game
  async loadGame(gameId: string) {
    // Reset per-match trackers when resuming
    this.achievementsTracker.reset();
    this.renderer.isAIGame = false;
    this.renderer.setCurrentMatchId(gameId);
    (this.renderer as any).testMode = false; // Ensure test mode is off when loading game
    this.renderer.hideLobby();
    this.lobbyController?.hide();
    const gameToLoad = await this.repo.getUnfinishedGameById(gameId);
    if (!gameToLoad) {
      this.renderer.showModal({ title: "Error", body: "Could not find the game to load." });
      return;
    }
    if (!gameToLoad.p1_user_id || !gameToLoad.p2_user_id) {
        this.renderer.showModal({ title: "Error", body: "This game cannot be loaded as a multiplayer match." });
        return;
    }
    const opponentId = gameToLoad.p1_user_id === this.currentUser.id ? gameToLoad.p2_user_id : gameToLoad.p1_user_id;
    const opponent = await this.repo.getUserById(opponentId);
    if (!opponent) {
        this.renderer.showModal({ title: "Error", body: "Could not load opponent data." });
        return;
    }
    this.isMultiplayer = true;
    this.players = 2;
    this.renderer.isAIGame = false;
    const p1 = await this.repo.getUserById(gameToLoad.p1_user_id);
    const p2 = await this.repo.getUserById(gameToLoad.p2_user_id);

    if (!p1 || !p2) {
      console.error("Could not fetch player data for match start");
      this.returnToLobby();
      return;
    }

    this.player1 = p1;
    this.player2 = p2;
    this.opponent = opponent;

    // Restore roll histories from saved game (critical for reconnects)
    if (gameToLoad.p1_roll_history && Array.isArray(gameToLoad.p1_roll_history)) {
      this.player1.current_game_rolls = [...gameToLoad.p1_roll_history];
    } else {
      this.player1.current_game_rolls = [];
    }
    if (gameToLoad.p2_roll_history && Array.isArray(gameToLoad.p2_roll_history)) {
      this.player2.current_game_rolls = [...gameToLoad.p2_roll_history];
    } else {
      this.player2.current_game_rolls = [];
    }

    // Also restore currentUser's roll history for live stats display
    if (this.currentUser.id === this.player1.id) {
      this.currentUser.current_game_rolls = [...this.player1.current_game_rolls];
    } else if (this.currentUser.id === this.player2.id) {
      this.currentUser.current_game_rolls = [...this.player2.current_game_rolls];
    }

    // Detect tournament context and set renderer mode
    const gameState = gameToLoad.game_state_json;
    if (gameToLoad.tournament_id && gameToLoad.tournament_round && (gameToLoad.tournament_match || gameToLoad.tournament_match === 0)) {
      this.currentTournamentContext = {
        tournamentId: gameToLoad.tournament_id,
        round: gameToLoad.tournament_round,
        matchIndex: gameToLoad.tournament_match,
      };

      // Load tournament data to get type and standings (for round robin W-L records)
      try {
        const tournament = await (this.repo as any).sb?.getTournamentById(gameToLoad.tournament_id);
        if (tournament) {
          this.currentTournamentType = tournament.tournament_type as 'bracket' | 'round_robin' || 'bracket';
          console.log('[loadGame] Tournament type:', this.currentTournamentType);

          // For round-robin tournaments, load player W-L records from standings
          if (this.currentTournamentType === 'round_robin' && tournament.round_robin_data?.standings) {
            const standings = tournament.round_robin_data.standings;
            standings.forEach((entry: any) => {
              this.tournamentPlayerRecords[entry.userId] = {
                wins: entry.wins ?? 0,
                losses: entry.losses ?? 0
              };
            });
            console.log('[loadGame] Loaded round-robin W-L records:', this.tournamentPlayerRecords);
          }
        }
      } catch (e) {
        console.error('[loadGame] Failed to load tournament data:', e);
      }

      // For tournament games, recalculate PPR to be tournament-specific
      if ((this.repo as any).sb?.calculateUserPPRForTournament) {
        try {
          const p1PPR = await (this.repo as any).sb.calculateUserPPRForTournament(gameToLoad.tournament_id, this.player1.id);
          const p2PPR = await (this.repo as any).sb.calculateUserPPRForTournament(gameToLoad.tournament_id, this.player2.id);
          this.player1.avgPPR = p1PPR;
          this.player2.avgPPR = p2PPR;
          // Update currentUser if they're one of the players
          if (this.currentUser.id === this.player1.id) {
            this.currentUser.avgPPR = p1PPR;
          } else if (this.currentUser.id === this.player2.id) {
            this.currentUser.avgPPR = p2PPR;
          }
        } catch (e) {
          console.error('Failed to calculate tournament PPR on game load', e);
        }
      }
    } else {
      // Regular multiplayer game - recalculate PPR (excludes tournaments and solo)
      this.currentTournamentContext = null;
      try {
        const p1PPR = await this.repo.calculateUserPPR(this.player1.id);
        const p2PPR = await this.repo.calculateUserPPR(this.player2.id);
        this.player1.avgPPR = p1PPR;
        this.player2.avgPPR = p2PPR;
        // Update currentUser if they're one of the players
        if (this.currentUser.id === this.player1.id) {
          this.currentUser.avgPPR = p1PPR;
        } else if (this.currentUser.id === this.player2.id) {
          this.currentUser.avgPPR = p2PPR;
        }
      } catch (e) {
        console.error('Failed to calculate regular multiplayer PPR on game load', e);
      }
    }

    // Calculate fresh W-L stats for both players ONLY for non-tournament games
    // Tournament games use tournamentPlayerRecords (round robin) or hide W-L (bracket)
    if (!this.currentTournamentContext) {
      try {
        console.log('[loadGame] Calculating fresh W-L for player cards (non-tournament game)');
        const p1WL = await this.repo.calculateWinLoss(this.player1.id, { excludeTournaments: true, excludeAI: true });
        const p2WL = await this.repo.calculateWinLoss(this.player2.id, { excludeTournaments: true, excludeAI: true });
        this.player1.wins = p1WL.wins;
        this.player1.losses = p1WL.losses;
        this.player1.gamesPlayed = p1WL.gamesPlayed;
        this.player2.wins = p2WL.wins;
        this.player2.losses = p2WL.losses;
        this.player2.gamesPlayed = p2WL.gamesPlayed;
        console.log('[loadGame] Player 1 W-L:', p1WL);
        console.log('[loadGame] Player 2 W-L:', p2WL);
      } catch (e) {
        console.error('[loadGame] Failed to calculate W-L for player cards', e);
      }
    }
    if (gameState) {
        this.scores = gameState.scores;
        this.round = gameState.round;
        this.currentPlayer = gameState.currentPlayer;
        this.ballsLeft = gameState.ballsLeft;
        this.turnHits = gameState.turnHits;
        this.phase = gameState.phase || 'normal';
        this.isSkeechPossible = gameState.isSkeechPossible || false;
        this.skeechSuccess = gameState.skeechSuccess || false;
        this.wildcardHit = gameState.wildcardHit || null;
        this.suddenDeathScores = gameState.suddenDeathScores || [0, 0];
        this.suddenDeathRound = gameState.suddenDeathRound || 1;
    }

    this.renderer.round = this.round;
    this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));

    await this.lobbyManager.updateMode('in_match');

    // Calculate isMyTurn based on the logic from ActiveGamesDisplay.ts
    if (gameState) {
        const isPlayer1 = gameToLoad.p1_user_id === this.currentUser.id;
        const myPlayerIndex = isPlayer1 ? 0 : 1;
        this.isMyTurn = gameState.currentPlayer === myPlayerIndex;
    }
    this.renderer.setIsMyTurn(this.isMyTurn);

    this.world.setAspect(this.board.aspect);
    // Ensure balls-left indicator is set before animating
    this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
    this.resetBall();
    this.matchControls = await this.lobbyManager.startMatch(gameToLoad.id);
    this.opponentInRoom = true; // Reset presence flag on (re)join
    // If we arrived from a tournament lobby, ensure tournament mode is on
    const tournamentLobbyOpen2 = !!(this as any).tournamentLobby;
    this.renderer.setTournamentMode(!!this.currentTournamentContext || tournamentLobbyOpen2);

    // Pass tournament-specific W-L records to renderer for round-robin tournaments
    // IMPORTANT: Must be set BEFORE updatePlayerCards() so W-L displays correctly
    if (this.currentTournamentType === 'round_robin') {
      this.renderer.setTournamentPlayerRecords(this.tournamentPlayerRecords);
    }

    // Determine isHost based on player order in saved game
    // In this game's conventions: player1 = guest (currentPlayer 0), player2 = host (currentPlayer 1)
    const isPlayer1 = gameToLoad.p1_user_id === this.currentUser.id;
    this.isHost = !isPlayer1;  // If you're player1, you're the guest (NOT host)
    this.renderer.updatePlayerCards(this.player1, this.player2);
    this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
    const loadedGamePlayerName = this.currentPlayer === 0 ? this.player1?.userName || 'Player 1' : this.player2?.userName || 'Player 2';
    const loadedGamePlayerId = this.currentPlayer === 0 ? this.player1?.id : this.player2?.id;
    const isLoadedGamePlayerCurrentUser = loadedGamePlayerId === this.currentUser.id;
    this.renderer.setTurnIndicator(true, loadedGamePlayerName, isLoadedGamePlayerCurrentUser);    
    
    // Ensure appearance consistency on resume: host board + per-player ball colors
    try {
      const hostUserId = this.player2?.id; // Convention: player2 is host
      if (hostUserId) {
        await this.renderer.loadGamePreferences(hostUserId);
      }
      const p1Prefs = await this.renderer.profileManager.loadGamePreferences(this.player1!.id);
      const p2Prefs = await this.renderer.profileManager.loadGamePreferences(this.player2!.id);
      this.p1BallColor = p1Prefs?.ballColor || this.p1BallColor;
      this.p2BallColor = p2Prefs?.ballColor || this.p2BallColor;
      this.setBallColorForCurrentPlayer();
    } catch {}
    if (this.phase === 'retribution') {
        this.renderer.showRetributionOverlay();
    } else if (this.phase === 'sudden_death') {
        this.renderer.showSuddenDeathOverlay();
    }
    this.renderer.showModal({
        title: "Game Loaded",
        body: `Continuing your match vs <strong>${opponent.userName}</strong>. ${this.isMyTurn ? 'It is your turn.' : ''}`,
        button: "Let's Go!"
    });
  }

  private getDisplayScores(): number[] {
    const inSuddenDeathContext =
      this.phase === 'sudden_death' ||
      (this.phase === 'over' && this.phaseHistory.some(entry => entry.phase === 'sudden_death'));
    return inSuddenDeathContext ? this.suddenDeathScores : this.scores;
  }

  // Appearance helpers: keep ball color in sync with the active roller
  private setBallColorForCurrentPlayer() {
    const color = this.currentPlayer === 0 ? this.p1BallColor : this.p2BallColor;
    (this.renderer as any).ballColor = color;
    try { this.renderer.setBallsLeft(Math.max(0, this.ballsLeft)); } catch {}
  }

  private computeIsMyTurnForCurrentPlayer(): boolean {
    const myIndex: 0 | 1 = this.isHost ? 1 : 0;
    return myIndex === this.currentPlayer;
  }

  private setBallColorForIndex(index: number) {
    const color = index === 0 ? this.p1BallColor : this.p2BallColor;
    (this.renderer as any).ballColor = color;
    try { this.renderer.setBallsLeft(Math.max(0, this.ballsLeft)); } catch {}
  }

  private getCurrentPlayerName(): string {
    // For multiplayer games, return the appropriate player name based on currentPlayer
    if (this.isMultiplayer) {
      return this.currentPlayer === 0 ? this.player1?.userName || 'Player 1' : this.player2?.userName || 'Player 2';
    }
    // For solo games, always return current user name
    return this.currentUser?.userName || 'Player';
  }

  // Core physics + rules
  update(dt: number) {
    const now = performance.now();

    // Record path for active player in multiplayer (throttled to 30fps)
    // Also record during bounce animation (ball.rolling is false but we still need to capture the animation)
    const shouldRecord = this.ball.rolling || this.physics.bounce.active;
    if (this.isMultiplayer && this.isMyTurn && shouldRecord && this.isRecordingPath) {
      if (now - this.lastPathRecordTime >= this.pathRecordInterval) {
        const elapsedSec = (now - this.pathRecordingStartTime) / 1000;

        // Check if ball position has changed
        const posChanged = !this.lastRecordedPos ||
                          Math.abs(this.ball.pos.x - this.lastRecordedPos.x) > 0.01 ||
                          Math.abs(this.ball.pos.y - this.lastRecordedPos.y) > 0.01;

        if (posChanged) {
          // Ball is moving - record this frame
          const snapshot = {
            t: elapsedSec,
            x: this.ball.pos.x,
            y: this.ball.pos.y,
            vx: this.ball.vel.x,
            vy: this.ball.vel.y,
            spin: this.ball.spin,
            r: this.ball.r,
            bounceActive: this.physics.bounce.active
          };
          this.recordedPath.push(snapshot);

          this.lastRecordedPos = { x: this.ball.pos.x, y: this.ball.pos.y };
          this.stationaryFrameCount = 0;
        } else {
          // Ball hasn't moved - increment stationary counter
          this.stationaryFrameCount++;

          // If ball hasn't moved for 2+ frames, stop recording
          if (this.stationaryFrameCount >= 2) {
          // console.log('[Path Recording] Ball stationary for', this.stationaryFrameCount, 'frames - ending recording');
            this.isRecordingPath = false;
            // Don't send final batch yet - wait for game state to update in nextBall()
            this.needsFinalBatchSend = true;
          }
        }

        this.lastPathRecordTime = now;
      }
    }

    // In multiplayer, only run physics callbacks for the active player's rolls
    // The waiting player uses path playback instead of physics simulation
    // Exception: AI games need physics callbacks for both players since AI is local
    // Observers always use path playback regardless of other flags
    if ((!this.isMultiplayer || this.isMyTurn || this.aiPlayer) && !this.isObserver) {
      if (this.currentUser) {
        this.renderer.setStats(this.currentUser);
      }
      this.renderer.setGameState({ consecutiveDitches: this.consecutiveDitches });
      // Active player or solo - run full physics with callbacks
      this.physics.step(dt, {
      onDitch: () => {
        // Observers don't process physics callbacks - they just watch path playback
        if (this.isObserver) {
          return;
        }
        this.trackTestRollStats();
        this.physics.logBypassesForCompletedRoll();
        this.consecutiveDitches++;
        this.turnHasFailed = true;
        this.isSkeechPossible = false;
        this.lastRollResult = 'ditch';

        // Stop path recording BEFORE ditch handling
        if (this.isRecordingPath) {
          this.isRecordingPath = false;
          // Cancel any pending timer to prevent sending stale state
          if (this.pathBatchTimer !== null) {
            clearTimeout(this.pathBatchTimer);
            this.pathBatchTimer = null;
          }
          // Don't send final batch yet - wait for game state to update in nextBall()
        this.needsFinalBatchSend = true;          
        }

        if (this.tutorialManager.isActive) {
          this.tutorialManager.handleDitch();
        }

        this.nextBall('ditch');
      },
      onStopped: () => {
        // Observers don't process physics callbacks - they just watch path playback
        if (this.isObserver) {
          return;
        }
        this.trackTestRollStats();
        this.physics.logBypassesForCompletedRoll();
        this.turnHasFailed = true;
        this.isSkeechPossible = false;
        this.lastRollResult = 'stop';

        // Stop path recording when ball stops rolling
        if (this.isRecordingPath) {
          this.isRecordingPath = false;
          // Cancel any pending timer to prevent sending stale state
          if (this.pathBatchTimer !== null) {
            clearTimeout(this.pathBatchTimer);
            this.pathBatchTimer = null;
          }
          // Don't send final batch yet - wait for game state to update in nextBall()
          this.needsFinalBatchSend = true;
        }

        this.nextBall('stop');
      },
      onSink: (_cx,_cy,_r, pts) => {
        // Observers don't process physics callbacks - they just watch path playback
        if (this.isObserver) {
          return;
        }
        this.trackTestRollStats();
        this.consecutiveDitches = 0;
        this.myRollResult.result = "hole";
        this.lastRollResult = 'hole';
        if(this.myRollResult.result === "hole") {
          this.myRollResult.cx = _cx;
          this.myRollResult.cy = _cy;
          this.myRollResult.pts = pts;
        }

        // Stop path recording BEFORE sink animation starts (to avoid recording sink frames)
        if (this.isRecordingPath) {
          this.isRecordingPath = false;
          // Cancel any pending timer to prevent sending stale state
          if (this.pathBatchTimer !== null) {
            clearTimeout(this.pathBatchTimer);
            this.pathBatchTimer = null;
          }
          // Don't send final batch yet - wait for game state to update in onScore()
          this.needsFinalBatchSend = true;          
        }

        if (this.tutorialManager.isActive) {
          this.tutorialManager.handleSink(pts);
        }

        // Show floater at ball's current position, not hole center
        const { play: playPx } = this.board.rects(this.renderer.canvas, this.renderer.dpr);
        const { play: playW } = this.world.worldRects();
        const screenPos = this.world.worldToScreen(this.ball.pos, playPx, playW);
        this.renderer.notifyScored(screenPos.x, screenPos.y, pts);

        if (pts === 5) {
          this.triggerScoreMessage(pts);
          this.renderer.triggerScoringRipple();
        } else if (pts === 3) {
          this.triggerScoreMessage(pts);
        } else if (pts === 1) {
          this.triggerScoreMessage(pts);
        }

        this.startSinkHandledByPhysics(pts); }
      });
    } else {
      // Passive player - use ONLY path playback (no physics simulation)
      if (this.isPlayingPath) {
        this.updatePathPlayback();
      }
      // If not playing path yet, ball just sits at initial position until first chunk arrives

      // Run sink animation even for passive player (doesn't require physics.step)
      // BUT: Don't modify ball.r during path playback - radius comes from path snapshots
      if (this.physics.sink.active) {
        this.physics.sink.t += dt / this.physics.sink.dur;
        const t = Math.min(this.physics.sink.t, 1);
        const e = t * t; // ease-in
        this.ball.pos.x = this.physics.sink.cx;
        this.ball.pos.y = this.physics.sink.cy;
        // Only modify radius if not playing path (path snapshots provide radius including bounce animation)
        if (!this.isPlayingPath) {
          this.ball.r = Math.max(1, this.physics.sink.r * (1 - e));
        }
        if (t >= 1) {
          this.physics.sink.active = false;
        }
      }

      // Run bounce animation for passive player (from path snapshots)
      // The path snapshots already contain the animated radius values
      // We just need to not override them with other animations
      // NOTE: The radius interpolation happens in updatePathPlayback()
      // This is just to ensure bounce animation state is properly handled
    }         
  }

  startSinkHandledByPhysics(pts:number) {
    const previousPhase = this.phase;
    if (pts > 0) {
      this.onScore(pts);
    } else {
      this.trackRoll(pts); // Track ditch (0 pts) for PPR calculation
      this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
      this.turnHasFailed = true;
    }
    this.ball.rolling = false;

    const phaseChanged = previousPhase !== this.phase;

    // For phase transitions (retribution/sudden_death/over), we need to send the final batch
    // with the correct phase AND turn state, so do the turn transition NOW before sending
    if (phaseChanged && this.phase === 'retribution') {
      // Set up retribution turn state immediately (don't wait for setTimeout)
      this.currentPlayer = this.currentPlayer === 0 ? 1 : 0;
      this.isMyTurn = this.computeIsMyTurnForCurrentPlayer();
      this.ballsLeft = this.ballsPerTurn;
      this.turnHits = [];
      this.turnHasFailed = false;

      // Send final batch with updated phase AND retribution turn state
      if (this.isMultiplayer && this.needsFinalBatchSend) {
        this.needsFinalBatchSend = false;
        // Wait for PPR to finish calculating before sending final batch
        if (this.pendingPPRUpdate) {
          this.pendingPPRUpdate.then(() => {
            this.pendingPPRUpdate = null;
            this.sendPathBatch();
          });
        } else {
          this.sendPathBatch();
        }
      }
      return; // handleReached21's setTimeout will show overlays and complete setup
    }

    // For sudden death, set up first player's turn state before sending batch
    if (phaseChanged && this.phase === 'sudden_death') {
      // Sudden death always starts with player 0
      this.currentPlayer = 0;
      const myIndex: 0 | 1 = this.isHost ? 1 : 0;
      this.isMyTurn = myIndex === this.currentPlayer;
      this.ballsLeft = this.ballsPerTurn;
      this.turnHits = [];
      this.turnHasFailed = false;
      // Initialize sudden death scores
      this.suddenDeathScores = [0, 0];
      this.suddenDeathRound = 1;

      // Set flag to prevent setTimeout in handleReached21 from running startSuddenDeath again
      this.suddenDeathAlreadySetup = true;

      console.log('[SUDDEN_DEATH] Active player setup:', {
        currentPlayer: this.currentPlayer,
        isHost: this.isHost,
        isMyTurn: this.isMyTurn,
        ballsLeft: this.ballsLeft,
        flagSet: this.suddenDeathAlreadySetup
      });

      // Send final batch with updated phase AND sudden death turn state
      if (this.isMultiplayer && this.needsFinalBatchSend) {
        this.needsFinalBatchSend = false;
        if (this.pendingPPRUpdate) {
          this.pendingPPRUpdate.then(() => {
            this.pendingPPRUpdate = null;
            this.sendPathBatch();
          });
        } else {
          this.sendPathBatch();
        }
      }
      return; // handleReached21's setTimeout will show overlays and complete setup
    }

    // For game over, send final batch but don't modify game state
    if (phaseChanged && this.phase === 'over') {
      if (this.isMultiplayer && this.needsFinalBatchSend) {
        this.needsFinalBatchSend = false;
        if (this.pendingPPRUpdate) {
          this.pendingPPRUpdate.then(() => {
            this.pendingPPRUpdate = null;
            this.sendPathBatch();
          });
        } else {
          this.sendPathBatch();
        }
      }
      return;
    }

    // Handle sudden death turn transitions
    // IMPORTANT: If skeech happened, onScore() → handleReached21() can trigger:
    // 1. P0 skeech: handleSuddenDeathTransition() changes P0→P1, ballsLeft=4
    // 2. P1 skeech (tied): evaluateSuddenDeathRound() → startSuddenDeath() changes P1→P0, ballsLeft=4
    // We detect this by checking if ballsLeft=4 AND someone has 21 points (skeech just happened)
    // Don't trigger on fresh rounds where scores are reset to [0,0]
    const didSuddenDeathTransition = this.phase === 'sudden_death' &&
      this.ballsLeft === this.ballsPerTurn &&
      this.suddenDeathScores.some(score => score === this.target);

    if (didSuddenDeathTransition) {
      // Transition already happened in onScore() via skeech
      this.suddenDeathTransitionApplied = true; // Flag to prevent nextBall() from decrementing

      console.log('[startSinkHandledByPhysics] Sudden death transition detected (already done in onScore):', {
        currentPlayer: this.currentPlayer,
        isHost: this.isHost,
        isMyTurn: this.isMyTurn,
        ballsLeft: this.ballsLeft,
        sdScores: [this.suddenDeathScores[0], this.suddenDeathScores[1]]
      });
    }

    // Normal case: proceed with nextBall (which will send the batch with updated state)
    this.nextBall(pts > 0 ? 'hole' : 'ditch');
  }

  private trackRoll(pts: number) {
    this.lastRollPts = pts; // Store for syncing to opponent
    this.lastRollPlayerIndex = this.currentPlayer;
    try {
      console.log('[trackRoll]', {
        pts,
        playerIndex: this.currentPlayer,
        ballsLeft: this.ballsLeft,
        round: this.round,
        phase: this.phase
      });
    } catch {}

    let playerToUpdate: IUser | null = null;
    if (this.isMultiplayer) {
      playerToUpdate = this.currentPlayer === 0 ? this.player1 : this.player2;
    } else if (this.aiPlayer) {
      // AI Challenge mode: track rolls for both human and AI
      playerToUpdate = this.currentPlayer === 0 ? this.player1 : this.player2;
    } else {
      // Solo mode: only track human player
      playerToUpdate = this.currentUser;
    }

    if (playerToUpdate) {
      if (!playerToUpdate.current_game_rolls) {
        playerToUpdate.current_game_rolls = [];
      }      
      playerToUpdate.current_game_rolls.push(pts);      

      // For multiplayer, DON'T use recalcAverages - it calculates from current game only (wrong!)
      // Instead, wait for updatePersistentPPRAfterRoll to calculate from DB (correct)
      if (this.isMultiplayer) {
        // Store the promise so nextBall can await it before sending final batch
        this.pendingPPRUpdate = this.updatePersistentPPRAfterRoll(playerToUpdate)
          .catch(err => console.error('PPR persist error:', err));
      } else {
        // Solo games can use recalcAverages since it's just one game
        recalcAverages(playerToUpdate);
        this.renderer.updatePlayerCards(this.player1, this.player2);
      }
    }
    // Emit roll event for achievements/awards
    this.eventBus.emit('roll_completed', { points: pts, result: pts > 0 ? 'hole' as const : 'ditch' as const, speed: this.lastLaunchSpeed ?? undefined, orbits: this.physics.maxOrbitsThisRoll, popUps: this.physics.popCountThisRoll } as any);
  }

  private async updatePersistentPPRAfterRoll(player: IUser) {
    try {
      if (!this.isMultiplayer) return; // Only persist in multiplayer
      // NOTE: Roll history is now saved in saveGameStateToDb() to combine with game_state_json
      // This reduces DB calls from 2 per roll to 1 per roll

      // Calculate PPR from complete roll history (past games + current game)
      // This is accurate without fetching from DB on every roll
      const tournamentId = this.currentTournamentContext?.tournamentId;

      if (tournamentId) {
        // For tournament games: use complete tournament roll history (all tournament games)
        const isPlayer1 = this.player1 && player.id === this.player1.id;
        let tournamentHistory = isPlayer1 ? this.player1TournamentRollHistory : this.player2TournamentRollHistory;

        // Safety check: if history is missing (reconnection?), re-fetch it
        if (!tournamentHistory || tournamentHistory.length === 0) {
          console.warn('[updatePersistentPPRAfterRoll] Tournament roll history missing, re-fetching from DB');
          tournamentHistory = await (this.repo as any).sb?.getTournamentRollHistories?.(tournamentId, player.id) ?? [];
          if (isPlayer1) {
            this.player1TournamentRollHistory = tournamentHistory;
          } else {
            this.player2TournamentRollHistory = tournamentHistory;
          }
        }

        // Append the new roll to tournament history
        const lastRoll = player.current_game_rolls?.[player.current_game_rolls.length - 1];
        if (typeof lastRoll === 'number' && Number.isFinite(lastRoll)) {
          tournamentHistory.push(lastRoll);
        }

        // Calculate PPR from complete tournament history (all tournament games + current game)
        if (tournamentHistory.length > 0) {
          const sum = tournamentHistory.reduce((a, b) => a + b, 0);
          player.avgPPR = +(sum / tournamentHistory.length).toFixed(3);
        } else {
          player.avgPPR = 0;
        }
      } else {
        // For non-tournament games: use complete roll history (efficient!)
        const isPlayer1 = this.player1 && player.id === this.player1.id;
        let completeHistory = isPlayer1 ? this.player1CompleteRollHistory : this.player2CompleteRollHistory;

        // Safety check: if history is missing (reconnection?), re-fetch it
        if (!completeHistory || completeHistory.length === 0) {
          console.warn('[updatePersistentPPRAfterRoll] Roll history missing, re-fetching from DB');
          completeHistory = await (this.repo as any).sb?.getUserRollHistories?.(player.id) ?? [];
          if (isPlayer1) {
            this.player1CompleteRollHistory = completeHistory;
          } else {
            this.player2CompleteRollHistory = completeHistory;
          }
        }

        // Append the new roll to complete history
        const lastRoll = player.current_game_rolls?.[player.current_game_rolls.length - 1];
        if (typeof lastRoll === 'number' && Number.isFinite(lastRoll)) {
          completeHistory.push(lastRoll);
        }

        // Calculate PPR from complete history (all past games + current game)
        if (completeHistory.length > 0) {
          const sum = completeHistory.reduce((a, b) => a + b, 0);
          player.avgPPR = +(sum / completeHistory.length).toFixed(3);
        } else {
          player.avgPPR = 0;
        }

        // NOTE: We do NOT update the users table here - that happens at game end only
      }

      // Refresh UI bits that display PPR
      // Pass tournament-specific W-L records to renderer for round-robin tournaments
      if (this.currentTournamentType === 'round_robin') {
        this.renderer.setTournamentPlayerRecords(this.tournamentPlayerRecords);
      }
      this.renderer.updatePlayerCards(this.player1, this.player2);
    } catch (e) {
      console.error('Failed to update persistent PPR after roll:', e);
    }
  }

  onScore(pts: number) {
    if (this.testMode) {
      return;
    }
    if (this.phase === 'sudden_death') {
      this.suddenDeathScores[this.currentPlayer] += pts;
    } else {
      this.scores[this.currentPlayer] += pts;
    }
    this.turnHits.push(pts);

    // CRITICAL: Track roll and update stats BEFORE any early returns
    // This ensures the final roll is saved to DB even for walk-off wins
    let playerToUpdate: IUser | null = null;
    if (this.isMultiplayer) {
        playerToUpdate = this.currentPlayer === 0 ? this.player1 : this.player2;
    } else {
        playerToUpdate = this.currentUser;
    }

    if (playerToUpdate && playerToUpdate.id !== 'ai_robo_roller') {
        playerToUpdate.pointsTotal += pts;
    }

    this.trackRoll(pts);

    // Now update UI and check for game-ending conditions
    const displayScores = this.getDisplayScores();
    const suddenDeathWalkoff = this.phase === 'sudden_death' && this.currentPlayer === 1 && this.suddenDeathScores[1] > this.suddenDeathScores[0];
    this.renderer.updateHUD(this.currentPlayer, displayScores, this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);

    if (suddenDeathWalkoff) {
      console.log('[onScore] Sudden death walk-off detected - trackRoll already called for pts:', pts);
      // Trigger immediate game end via evaluation after reflecting the final score on the board
      this.currentPlayer = 0; // Reset for evaluation
      this.evaluateSuddenDeathRound();
      return; // Skip remaining processing since game ended
    }

    if (this.turnHits.length === 1 && pts > 0 && !this.turnHasFailed) {
      this.isSkeechPossible = true;
    }

    this.updateWildcardDuringTurn();

    const counts = new Map<number, number>();
    for (const hit of this.turnHits) {
      counts.set(hit, (counts.get(hit) || 0) + 1);
    }
    const hasTripleDuplicate = Array.from(counts.values()).some(count => count >= 3);
    if (hasTripleDuplicate) {
      this.isSkeechPossible = false;
      this.wildcardHit = null;
    }

    if (isSkeechTurn(this.turnHits)) {
        this.skeechSuccess = true;
        this.wildcardHit = getSkeechWildcard(this.turnHits);
        this.skeechesThisGame++;
        this.eventBus.emit('skeech', { playerIndex: this.currentPlayer });

        if (this.phase === 'sudden_death') {
          this.suddenDeathScores[this.currentPlayer] = this.target;
        } else {
          this.scores[this.currentPlayer] = this.target;
        }

        // Update HUD to show the correct skeech score (21) instead of the sum of individual balls
        const displayScores = this.getDisplayScores();
        this.renderer.updateHUD(this.currentPlayer, displayScores, this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);

        console.log('[onScore] Skeech detected - trackRoll already called for turn hits:', this.turnHits);
        this.handleReached21('skeech');
        return;
    }

    if (this.phase !== 'sudden_death' && this.phase !== 'over' && this.scores[this.currentPlayer] > this.target) {
      const playerName = this.currentPlayer === 0 ?
        (this.player1?.userName || this.currentUser.userName) :
        (this.player2?.userName || this.opponent?.userName || 'Opponent');
      this.renderer.showBustOnBoard(playerName, this.bustTo, 2000);
      this.scores[this.currentPlayer] = this.bustTo;
      this.recordBustForCurrentPlayer();
      this.eventBus.emit('bust', { playerIndex: this.currentPlayer, newScore: this.bustTo });
    }

    if (this.phase === 'retribution' && this.scores[this.currentPlayer] === this.target) {
      this.endRetributionPhase();

      // Send a clean phase transition message (timeout) instead of reusing stale myRollResult
      // This prevents the passive player from processing old scoring data as their own
      if (this.matchControls) {
        const phaseTransitionMessage: RollResult = {
          kind: 'roll_result',
          round: this.round,
          ballIndex: 0,
          result: 'timeout',
          gameState: {
            scores: [this.scores[0], this.scores[1]],
            ballsLeft: this.ballsLeft,
            currentPlayer: this.currentPlayer,
            round: this.round,
            turnHits: [...this.turnHits],
            isSkeechPossible: this.isSkeechPossible,
            skeechSuccess: this.skeechSuccess,
            wildcardHit: this.wildcardHit,
            phase: this.phase,
            suddenDeathScores: [this.suddenDeathScores[0], this.suddenDeathScores[1]],
            suddenDeathRound: this.suddenDeathRound
          }
        };
        // REMOVED: State now sent with path chunk finalGameState
        // this.matchControls.sendRollResult(phaseTransitionMessage);
      }
      return;
    }

    if (this.phase === 'normal' && this.scores[this.currentPlayer] === this.target) {
      this.handleReached21('traditional');
    }

    this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);

    // NOTE: Don't send path batch here - nextBall() will be called after this and will send it
  }

  private startRetributionPhase() {
    // REMOVED: Old guard system
    // this.dropOpponentResultsUntil = performance.now() + 1500;

    // For multiplayer, turn switch already happened in startSinkHandledByPhysics (to send correct state)
    // Only switch if ballsLeft is not already 4 (indicating setup wasn't done yet)
    if (this.ballsLeft !== this.ballsPerTurn) {
      this.currentPlayer = this.currentPlayer === 0 ? 1 : 0;
    }
    console.log('[PHASE] startRetributionPhase enter', { time: new Date().toISOString(), phase: this.phase, currentPlayer: this.currentPlayer, isHost: this.isHost, isMyTurn: this.isMyTurn, scores: this.scores.slice(), ballsLeft: this.ballsLeft, pendingTurnTransition: this.pendingTurnTransition, hasTimer: !!this.pendingTurnTransitionTimer });
    this.isMyTurn = this.computeIsMyTurnForCurrentPlayer();
    this.renderer.setIsMyTurn(this.isMyTurn);
    const retributionPlayerName = this.currentPlayer === 0 ? this.player1?.userName || 'Player 1' : this.player2?.userName || 'Player 2';
    const isRetributionPlayerCurrentUser = this.isMyTurn; // isMyTurn is correctly calculated above
    this.renderer.setTurnIndicator(true, retributionPlayerName, isRetributionPlayerCurrentUser);
    // Update ball color for retribution roller
    this.setBallColorForCurrentPlayer();
    this.ballsLeft = this.ballsPerTurn;
    this.turnHits = [];
    this.turnHasFailed = false;
    this.isSkeechPossible = false;
    this.skeechSuccess = false;
    this.wildcardHit = null;
    // Initialize balls-left indicator before animating
    this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
    // Clear any skeech lighting at the start of a new phase/turn
    try { this.renderer.clearBoardLighting(); } catch {}
    try { this.renderer.resetTrackers(); } catch {}
    // Ensure reserve so rack shows 3 lights when ball sits at launch
    this.renderer.setReserveAtLaunch(true);
    // Update tilt direction for retribution player
    if (this.isMultiplayer || this.aiPlayer) {
      this.physics.setTiltDirection(this.currentPlayer);
      this.physics.jitterTilt();
    }
    this.resetBall();
    this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
    this.renderer.showRetributionOverlay();
    this.renderer.showRetributionOnBoard(retributionPlayerName ?? '', 2000);    
    this.renderer.startPinballLightShow();
    setTimeout(() => {
      if (!this.isMyTurn) {        
        this.triggerAITurnIfNeeded();
      }
      // Show turn start message to both players
      setTimeout(() => {
        this.triggerTurnStartMessage();
      }, 500);
    }, 2000);
    this.saveGameStateToDb();
  }

  private endRetributionPhase() {    
    if (this.phase !== 'retribution') {
    console.log('[PHASE] endRetributionPhase called', { time: new Date().toISOString(), phase: this.phase, scores: this.scores.slice(), suddenDeathScores: this.suddenDeathScores.slice(), currentPlayer: this.currentPlayer });
      return;
    }    
    this.renderer.hideRetributionOverlay();        
    const originalWinner = this.scores[0] === 21 ? 0 : 1;
    const retributionPlayer = originalWinner === 0 ? 1 : 0;
    const retributionScore = this.scores[retributionPlayer];

    if (retributionScore === 21) {
      this.phase = 'sudden_death';
      this.recordPhaseEntry('sudden_death', this.round, this.suddenDeathRound);
      this.startSuddenDeath();
    } else {
      this.phase = 'over';
      this.finishGamePromise = this.finishGameSave();

      const winner = originalWinner === 0 ? this.player1 : this.player2;
      const loser = originalWinner === 0 ? this.player2 : this.player1;
      this.triggerRetributionFailedMessage();

      setTimeout(() => {
        const winnerScore = 21;
        const loserScore = retributionScore;
        this.renderer.endGame(winner!, loser || null, winnerScore, loserScore, this.currentUser);
      }, 2000);
    }
    // Skip roll history since we're just updating phase state
    this.saveGameStateToDb();
  }

  private startSuddenDeath() {
    console.log('[SD_START] === STARTING SUDDEN DEATH ===', {
      suddenDeathRound: this.suddenDeathRound,
      ballsLeft: this.ballsLeft,
      ballsPerTurn: this.ballsPerTurn,
      currentPlayer: this.currentPlayer,
      isHost: this.isHost,
      isMyTurn: this.isMyTurn,
      suddenDeathAlreadySetup: this.suddenDeathAlreadySetup,
      suddenDeathRoundJustStarted: this.suddenDeathRoundJustStarted
    });

    this.renderer.hideRetributionOverlay();
    this.renderer.showSuddenDeathOverlay();

    this.recordPhaseEntry('sudden_death', this.round, this.suddenDeathRound);

    // If state was already set up in startSinkHandledByPhysics, skip ONLY the turn state setup
    // BUT: ALWAYS reset scores to [0, 0] and player to 0 at the start of each sudden death round
    this.suddenDeathScores = [0, 0];

    if (this.suddenDeathAlreadySetup) {
      console.log('[SD_START] SKIPPING turn setup - already set up in startSinkHandledByPhysics', {
        keepingCurrentPlayer: this.currentPlayer,
        keepingBallsLeft: this.ballsLeft
      });
      this.suddenDeathAlreadySetup = false; // Reset flag for next sudden death round
      // currentPlayer and ballsLeft were already set in startSinkHandledByPhysics
    } else {
      // Initialize turn state: start with player 0
      console.log('[SD_START] INITIALIZING turn state from scratch');
      this.currentPlayer = 0;
      this.ballsLeft = this.ballsPerTurn;
      console.log('[SD_START] Set currentPlayer=0, ballsLeft=' + this.ballsPerTurn);
    }

    // ALWAYS update isMyTurn and UI state
    this.isMyTurn = this.computeIsMyTurnForCurrentPlayer();
    this.renderer.setIsMyTurn(this.isMyTurn);
    console.log('[startSuddenDeath] Updated isMyTurn:', {
      currentPlayer: this.currentPlayer,
      isHost: this.isHost,
      isMyTurn: this.isMyTurn
    });
    const suddenDeathPlayerName = this.currentPlayer === 0 ? this.player1?.userName || 'Player 1' : this.player2?.userName || 'Player 2';
    const isSuddenDeathPlayerCurrentUser = this.isMyTurn; // isMyTurn is correctly calculated above
    this.renderer.setTurnIndicator(true, suddenDeathPlayerName, isSuddenDeathPlayerCurrentUser);
    // Update ball color for sudden-death starting roller
    this.setBallColorForCurrentPlayer();

    this.ballsLeft = this.ballsPerTurn;
    this.turnHits = [];
    this.turnHasFailed = false;
    this.isSkeechPossible = false;
    this.skeechSuccess = false;
    this.wildcardHit = null;
    // Initialize balls-left indicator before animating
    this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
    // Clear any skeech lighting at the start of sudden death
    try { this.renderer.clearBoardLighting(); } catch {}
    try { this.renderer.resetTrackers(); } catch {}
    // Ensure reserve at turn start
    this.renderer.setReserveAtLaunch(true);
    // Update tilt direction for sudden death (starts with player 0)
    if (this.isMultiplayer || this.aiPlayer) {
      this.physics.setTiltDirection(this.currentPlayer);
      this.physics.jitterTilt();
    }
    this.resetBall();
    this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
    const activePlayerName = this.isMyTurn ? this.currentUser.userName : this.opponent?.userName;
    this.renderer.showSuddenDeathOnBoard(this.suddenDeathRound, activePlayerName ?? '', 4000);    
    this.renderer.startPinballLightShow();
    // Auto-start after message duration
    setTimeout(() => {
      if (!this.isMyTurn) {        
        this.triggerAITurnIfNeeded();
      }
      // Show turn start message to both players
      setTimeout(() => {
        this.triggerTurnStartMessage();
      }, 500);
    }, 2500);
    // Skip roll history since we're just starting sudden death phase
    this.saveGameStateToDb();
  }

  private handleSuddenDeathTransition() {
    console.log('[SD_TRANSITION] === HANDLING SUDDEN DEATH TRANSITION ===', {
      suddenDeathRound: this.suddenDeathRound,
      currentPlayer: this.currentPlayer,
      ballsLeft: this.ballsLeft,
      player0Score: this.suddenDeathScores[0],
      player1Score: this.suddenDeathScores[1]
    });

    if (this.currentPlayer === 0) {
      console.log('[SD_TRANSITION] Switching from Player 0 to Player 1');
      this.currentPlayer = 1;
      this.ballsLeft = this.ballsPerTurn;
      this.resetTurnState();
      this.isMyTurn = this.computeIsMyTurnForCurrentPlayer();
      this.renderer.setIsMyTurn(this.isMyTurn);
      // Update ball color for player 1 in sudden death
      this.setBallColorForCurrentPlayer();
      // Initialize balls-left indicator before animating
      this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
      // Reserve a light for launch ball at start of turn
      this.renderer.setReserveAtLaunch(true);
      // Update tilt direction for player 1 in sudden death
      if (this.isMultiplayer || this.aiPlayer) {
        this.physics.setTiltDirection(this.currentPlayer);
        this.physics.jitterTilt();
      }
      this.resetBall();
      this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
      
      const suddenDeathPlayerName = this.player2?.userName || 'Player 2';
      const isSuddenDeathPlayerCurrentUser = this.isMyTurn; // isMyTurn already calculated correctly above
      this.renderer.setTurnIndicator(true, suddenDeathPlayerName, isSuddenDeathPlayerCurrentUser);

      if (this.isMyTurn) {
        // It's my turn - show turn message and set deadline
        this.renderer.showPinballMessage(`SUDDEN DEATH ROUND ${this.suddenDeathRound}`, 0.5, 0.35, {
          color: 'red',
          effect: 'pulse',
          fontSize: 20,
          duration: 2500
        });        
      } else {        
        this.triggerAITurnIfNeeded();
      }
    } else {      
      console.log('[SD_TRANSITION] Player 1 finished or continuing turn', {
        ballsLeft: this.ballsLeft,
        player0Score: this.suddenDeathScores[0],
        player1Score: this.suddenDeathScores[1]
      });

      const player0Score = this.suddenDeathScores[0];
      const player1Score = this.suddenDeathScores[1];

      if (player1Score > player0Score) {// Walk-off winner! Player 1 has exceeded Player 0's score
        console.log('[SD_TRANSITION] Walk-off winner - Player 1 won!');
        this.evaluateSuddenDeathRound();
      } else if (this.ballsLeft === 0) {
        console.log('[SD_TRANSITION] Player 1 finished all balls - evaluating round');
        this.evaluateSuddenDeathRound();
      } else {
        console.log('[SD_TRANSITION] Player 1 continuing turn, ballsLeft=' + this.ballsLeft);
        //console.log('ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ‚Â CONTINUING TURN: ballsLeft =', this.ballsLeft, 'currentPlayer =', this.currentPlayer);
      }
      // Otherwise, Player 1 continues their turn (don't reset or evaluate)
    }
  }

  private evaluateSuddenDeathRound() {
    console.log('[SD_EVAL] === EVALUATING SUDDEN DEATH ROUND ===', {
      suddenDeathRound: this.suddenDeathRound,
      player0Score: this.suddenDeathScores[0],
      player1Score: this.suddenDeathScores[1],
      currentPlayer: this.currentPlayer,
      ballsLeft: this.ballsLeft,
      suddenDeathAlreadySetup: this.suddenDeathAlreadySetup
    });

    const player0Score = this.suddenDeathScores[0];
    const player1Score = this.suddenDeathScores[1];
    const suddenDeathDisplayScores: [number, number] = [this.suddenDeathScores[0], this.suddenDeathScores[1]];

    if (player0Score === player1Score) {
      console.log('[SD_EVAL] TIE DETECTED - Starting new sudden death round', {
        oldRound: this.suddenDeathRound,
        newRound: this.suddenDeathRound + 1
      });
      this.suddenDeathRound += 1;
      this.suddenDeathRoundJustStarted = true; // Prevent nextBall() from decrementing ballsLeft
      this.suddenDeathAlreadySetup = false; // CRITICAL FIX: Ensure flag is false for new round after tie
      console.log('[SD_EVAL] Cleared suddenDeathAlreadySetup flag before starting new round');
      this.startSuddenDeath(); // This will reset scores and start with player 0 again
    } else if (player1Score > player0Score) {
      const winner = this.player2;
      const loser = this.player1;
      this.renderer.updateHUD(this.currentPlayer, suddenDeathDisplayScores, this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
      this.renderer.hideSuddenDeathOverlay();
      this.phase = 'over';
      this.finishGamePromise = this.finishGameSave();
      
      setTimeout(() => {
        this.renderer.endGame(winner!, loser || null, this.suddenDeathScores[1], this.suddenDeathScores[0], this.currentUser);
      }, 100);
      
      if (this.isMultiplayer && this.matchControls) {
        this.myRollResult.gameState = {
          currentPlayer: this.currentPlayer,
          scores: this.scores,
          ballsLeft: this.ballsLeft,
          round: this.round,
          turnHits: this.turnHits,
          phase: this.phase,
          suddenDeathScores: this.suddenDeathScores,
          suddenDeathRound: this.suddenDeathRound,
          isSkeechPossible: this.isSkeechPossible,
          skeechSuccess: this.skeechSuccess,
          wildcardHit: this.wildcardHit
        };
        // REMOVED: State now sent with path chunk finalGameState
        // this.matchControls.sendRollResult(this.myRollResult);
      }
    } else if (player0Score > player1Score && this.ballsLeft === 0) { // Player A wins (only after Player B finished all balls)      
      const winner = this.player1;
      const loser = this.player2;
      this.renderer.updateHUD(this.currentPlayer, suddenDeathDisplayScores, this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
      this.renderer.hideSuddenDeathOverlay();
      this.phase = 'over';
      this.finishGamePromise = this.finishGameSave();      
      setTimeout(() => {
        this.renderer.endGame(winner!, loser || null, this.suddenDeathScores[0], this.suddenDeathScores[1], this.currentUser);
      }, 100);      
      if (this.isMultiplayer && this.matchControls) {
        this.myRollResult.gameState = {
          currentPlayer: this.currentPlayer,
          scores: this.scores,
          ballsLeft: this.ballsLeft,
          round: this.round,
          turnHits: this.turnHits,
          phase: this.phase,
          suddenDeathScores: this.suddenDeathScores,
          suddenDeathRound: this.suddenDeathRound,
          isSkeechPossible: this.isSkeechPossible,
          skeechSuccess: this.skeechSuccess,
          wildcardHit: this.wildcardHit
        };
        // REMOVED: State now sent with path chunk finalGameState
        // this.matchControls.sendRollResult(this.myRollResult);
      }
    } else {
      // Player A is ahead but Player B still has balls left - keep playing!
    }
    // Skip roll history since we're just evaluating game state
    this.saveGameStateToDb();
  }

  private updateWildcardDuringTurn() {
    if (this.turnHits.length < 2) {
      this.wildcardHit = null;
      return;
    }

    const counts = new Map<number, number>();
    for (const hit of this.turnHits) {
      counts.set(hit, (counts.get(hit) || 0) + 1);
    }
    
    for (const [value, count] of Array.from(counts.entries())) {
      if (count >= 2) {
        this.wildcardHit = value;
        return;
      }
    }
  
    const required = new Set([1, 3, 5]);
    for (const hit of this.turnHits) {
      if (!required.has(hit)) {
        this.wildcardHit = hit;
        return;
      }
    }

    this.wildcardHit = null;
  }

  async nextBall(result: RollResult['result'] = 'timeout') {
    if (this.testMode) {
      setTimeout(() => this.autoRollTestBall(), 100);
      return;
    }
    // Observers should never process ball completion - they're just watching path playback
    if (this.isObserver) {      
      return;
    }
    // Early exit if game is over - don't process anything
    if (this.phase === 'over') {
      return;
    }

    // If called while path recording is active (e.g., nextBall button clicked), stop recording
    if (this.isRecordingPath) {      
      this.isRecordingPath = false;
      if (this.pathBatchTimer !== null) {
        clearTimeout(this.pathBatchTimer);
        this.pathBatchTimer = null;
      }
      this.needsFinalBatchSend = true;
      // Mark as 'stop' result to prevent sink animation on passive player (ball stopped on board, not in hole)
      this.lastRollResult = 'stop';
      // Stop the ball from rolling
      this.ball.rolling = false;
      this.ball.vel = { x: 0, y: 0 };
    }

    if (result === 'ditch' || result === 'stop') {
      this.turnHasFailed = true;
      this.isSkeechPossible = false;
      this.trackRoll(0); // Track ditch/stop as 0 points for PPR
      // Update HUD immediately so widgets reflect the ditch/timeout
      this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
      // Emit roll_completed for ditch/stop so awards (e.g., slowest/fastest, streaks) can track
      this.eventBus.emit('roll_completed', { points: 0, result, speed: this.lastLaunchSpeed ?? undefined, orbits: this.physics.maxOrbitsThisRoll, popUps: this.physics.popCountThisRoll } as any);
    }

    // Skip decrement if sudden death transition already applied the new turn state
    // OR if we just started a new sudden death round due to a tie
    if (!this.suddenDeathTransitionApplied && !this.suddenDeathRoundJustStarted) {
      this.ballsLeft--;
    } else {      
      // Clear flags after using them
      if (this.suddenDeathTransitionApplied) {
        this.suddenDeathTransitionApplied = false;
      }
      if (this.suddenDeathRoundJustStarted) {
        this.suddenDeathRoundJustStarted = false;
      }
    }
    this.rollsThisGame += 1;

    // NOTE: We don't increment rollsTotal here anymore - it's calculated from roll_history length
    // Live stats are updated when pts are scored (in onHole() where current_game_rolls is updated)
    this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
    const left = this.ballsLeft;
    this.myRollResult.result = result;
    this.myRollResult.ballIndex = 4 - this.ballsLeft;
    this.myRollResult.kind = "roll_result";
    this.myRollResult.round = this.round;

    // NOTE: We intentionally DO NOT send rollerStats (including PPR) because:
    // 1. PPR calculation is async and might not be done yet (race condition)
    // 2. Each client should fetch fresh PPR from DB for accuracy
    // 3. Wins/losses don't change mid-game anyway
    // The passive player will fetch opponent PPR separately when needed

    if (left <= 0) {
      const ptsThisRound = this.turnHits.reduce((a,b)=>a+b,0);
      const remaining = Math.max(0, this.target - this.scores[this.currentPlayer]);
      const ditches = this.turnHits.filter(v => v === 0).length;
      this.eventBus.emit('turn_completed', { playerIndex: this.currentPlayer, points: ptsThisRound, ditches });

      // In multiplayer, wait for opponent to finish watching before transitioning
      if (this.isMultiplayer && this.isMyTurn) {
        // Special-case: in retribution, a non-scoring final ball ends the game. Transition immediately
        if (this.phase === 'retribution' && (result === 'ditch' || result === 'stop')) {
          // Stop the ball and transition immediately to ensure game ends
          this.ball.rolling = false;
          this.ball.vel = { x: 0, y: 0 };
          this.handleTurnTransition();

          // REMOVED: State now sent with path chunk finalGameState
          // this.myRollResult.gameState = this.getCurrentGameState();
          // if (this.matchControls) {
          //   this.matchControls.sendRollResult(this.myRollResult);
          // }

          // Send final path batch with updated game state (before early return)
          if (this.needsFinalBatchSend) {
            this.needsFinalBatchSend = false;
            // Wait for PPR to finish calculating before sending final batch
            if (this.pendingPPRUpdate) {
              await this.pendingPPRUpdate;
              this.pendingPPRUpdate = null;
            }
            this.sendPathBatch();
          }

          // Persist state so reloads reflect the completed retribution
          // Skip roll history since handleTurnTransition already saved it
          this.saveGameStateToDb();
          this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
          return;
        }
        this.pendingTurnTransition = true;
        // If opponent has left the room or channel is gone, transition immediately next frame.
        if (!this.opponentInRoom || !this.matchControls) {
          if (this.pendingTurnTransitionTimer) { clearTimeout(this.pendingTurnTransitionTimer); this.pendingTurnTransitionTimer = null; }
          requestAnimationFrame(() => {
            if (this.pendingTurnTransition) {
          // console.log('[nextBall] Opponent absent; executing pending turn transition immediately');
              this.pendingTurnTransition = false;
              // Always call handleTurnTransition - it will properly handle retribution/sudden death endings
              this.handleTurnTransition();

              // Send updated game state to opponent (if matchControls exists) so they know it's their turn if they reconnect
              if (this.matchControls) {
                const gameState = this.getCurrentGameState();
                gameState.lastRollPts = undefined;

                // Send turn transition state via special path chunk
                const turnTransitionChunk: PathChunk = {
                  kind: 'path_chunk',
                  round: this.round,
                  ballIndex: 0,
                  snapshots: [],
                  isComplete: true,
                  finalGameState: gameState
                };
                this.matchControls.sendPathChunk(turnTransitionChunk);
                // console.log('[nextBall] Sent turn transition chunk to opponent (absent player case)');
              }
            }
          });
        } else {
          // Otherwise, set a short fallback in case ReadyForNext never arrives.
          if (this.pendingTurnTransitionTimer) { clearTimeout(this.pendingTurnTransitionTimer); this.pendingTurnTransitionTimer = null; }
          this.pendingTurnTransitionTimer = window.setTimeout(() => {
            if (this.pendingTurnTransition) {
              // console.log('[nextBall] ReadyForNext not received; auto-executing pending turn transition');
              this.pendingTurnTransition = false;
              this.pendingTurnTransitionTimer = null;
              // Always call handleTurnTransition - it will properly handle retribution/sudden death endings
              this.handleTurnTransition();

              // Send updated game state to opponent so they know it's their turn
              if (this.matchControls) {
                const gameState = this.getCurrentGameState();
                gameState.lastRollPts = undefined; // Clear to avoid duplicate tracking

                // Send turn transition state via special path chunk
                const turnTransitionChunk: PathChunk = {
                  kind: 'path_chunk',
                  round: this.round,
                  ballIndex: 0,
                  snapshots: [],
                  isComplete: true,
                  finalGameState: gameState
                };
                this.matchControls.sendPathChunk(turnTransitionChunk);
                // console.log('[nextBall] Sent turn transition chunk to opponent after fallback transition');
              }
            }
          }, 1000);
        }

        // Stop the ball to prevent physics from re-detecting ditch/hole
        this.ball.rolling = false;
        this.ball.vel = { x: 0, y: 0 };

        // REMOVED: State now sent with path chunk finalGameState
        // this.myRollResult.gameState = this.getCurrentGameState();
        // if (this.matchControls) {
        //   // console.log('SENDING ROLLRESULT (4th ball, pending transition)');
        //   this.matchControls.sendRollResult(this.myRollResult);
        // }

        this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);

        // Send final path batch with updated game state (before early return)
        if (this.needsFinalBatchSend) {
          this.needsFinalBatchSend = false;
          // Wait for PPR to finish calculating before sending final batch
          if (this.pendingPPRUpdate) {
            await this.pendingPPRUpdate;
            this.pendingPPRUpdate = null;
          }
          this.sendPathBatch();
        }

        // Before returning, persist state so refreshes include this ball
        this.saveGameStateToDb();
        // IMPORTANT: Return early to prevent further processing
        return;
      } else {
        this.handleTurnTransition();

        // REMOVED: State now sent with path chunk finalGameState
        // if (this.phase !== 'retribution' && this.phase !== 'sudden_death') {
        //   this.myRollResult.gameState = this.getCurrentGameState();
        //   if (this.matchControls) {
        //     // console.log('SENDING ROLLRESULT');
        //     this.matchControls.sendRollResult(this.myRollResult);
        //   }
        // }        
      }
    } else {
      // REMOVED: State now sent with path chunk finalGameState
      // this.myRollResult.gameState = this.getCurrentGameState();
      // if (this.matchControls) {
      //   this.matchControls.sendRollResult(this.myRollResult);
      // }

      this.resetBall();

      // If it's AI's turn (non-multiplayer AI challenge), trigger the next AI ball
      this.triggerAITurnIfNeeded();
      
      if (!this.isMultiplayer || this.isMyTurn) {
        if (this.isMultiplayer && this.isMyTurn) {
          if (this.opponentReady) {
            this.opponentReady = false;            
          } else {
            this.waitingForOpponentReady = true;
          }
        }
      }
    }
    this.saveGameStateToDb();

    this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);

    // Send final path batch with updated game state (if deferred from recording stop)
    if (this.needsFinalBatchSend) {
      this.needsFinalBatchSend = false;
      // Wait for PPR to finish calculating before sending final batch
      if (this.pendingPPRUpdate) {
        await this.pendingPPRUpdate;
        this.pendingPPRUpdate = null;
      }
      this.sendPathBatch();
    }
  }

  private getCurrentGameState(): GameStateSync {   
    const state = {
      scores: this.scores,
      ballsLeft: this.ballsLeft,
      currentPlayer: this.currentPlayer,
      round: this.round,
      turnHits: this.turnHits,
      isSkeechPossible: this.isSkeechPossible,
      skeechSuccess: this.skeechSuccess,
      wildcardHit: this.wildcardHit,
      phase: this.phase,
      suddenDeathScores: this.suddenDeathScores,
      suddenDeathRound: this.suddenDeathRound,
      lastRollPts: this.lastRollPts,
      lastRollResult: this.lastRollResult,
      lastRollPlayer: this.lastRollPlayerIndex ?? undefined,
      player1PPR: this.player1?.avgPPR,
      player2PPR: this.player2?.avgPPR,
      bustHistory: this.bustHistory,
      phaseHistory: this.phaseHistory,
    };
    return state;
  }

  private async saveGameStateToDb() {
    // Handle both multiplayer and AI challenge games
    const gameId = this.isMultiplayer ? this.matchControls?.matchId : this.aiGameId;

    if (!gameId) {
      return;
    }

    // For multiplayer: both players save state
    // For AI: only human player saves state
    const gameState = this.getCurrentGameState();

    // OPTIMIZATION: Combine game_state_json + roll_history into single PATCH
    // Reduces DB calls from 2 per roll to 1 per roll
    // Each player ONLY saves their OWN roll history, never the opponent's (multiplayer)
    // For AI games: save both histories since we're the only client
    const amIPlayer1 = this.currentUser.id === this.player1?.id;
    const payload: Partial<IGame> = {
      game_state_json: gameState,
      // Multiplayer: save only my roll history
      // AI game: save both histories
      ...(this.aiPlayer
        ? {
            p1_roll_history: this.player1?.current_game_rolls || [],
            p2_roll_history: this.player2?.current_game_rolls || []
          }
        : amIPlayer1
        ? { p1_roll_history: this.player1?.current_game_rolls || [] }
        : { p2_roll_history: this.player2?.current_game_rolls || [] })
    };

    try {
      await this.repo.updateGame(gameId, payload);
      this.offlineWarningShown = false;
    } catch (error) {
      console.warn('[saveGameStateToDb] Failed to persist game state:', error);
      // Only show offline message for multiplayer (not AI games)
      if (this.isMultiplayer) {
        this.showOfflineConnectionMessage('Connection lost; your game progress will sync once you reconnect.');
      }
    }
  }

  private showOfflineConnectionMessage(message: string) {
    if (this.offlineWarningShown) return;
    this.offlineWarningShown = true;
    try {
      this.renderer.showPositionedMessage(message, {
        positioning: {
          color: 'red',
          duration: 4200,
          mode: 'fixed',
          startX: 0.5,
          startY: 0.35,
          fontSize: 22
        },
        type: 'custom'
      });
    } catch (err) {
      console.warn('[showOfflineConnectionMessage] Failed to display message:', err);
    }
  }

  private handleTurnTransition() {
    this.consecutiveDitches = 0;

    if (this.phase === 'retribution') {
      // Guard: if retribution has just begun (no balls rolled yet), do NOT end it.
      // This prevents premature game-over when a player reaches 21 on their 4th ball
      // and a pending transition timer fires before the retribution player rolls.
      const retributionJustStarted = (this.turnHits.length === 0) && (this.ballsLeft === this.ballsPerTurn);
      if (retributionJustStarted) {
        // Persist state and exit; startRetributionPhase() manages the flow
        this.saveGameStateToDb();
        return;
      }
      const wasRetribution = true;
      this.endRetributionPhase();
      this.myRollResult.gameState = {
        scores: [this.scores[0], this.scores[1]],
        ballsLeft: this.ballsLeft,
        currentPlayer: this.currentPlayer,
        round: this.round,
        turnHits: [...this.turnHits],
        isSkeechPossible: this.isSkeechPossible,
        skeechSuccess: this.skeechSuccess,
        wildcardHit: this.wildcardHit,
        phase: this.phase,
        suddenDeathScores: [this.suddenDeathScores[0], this.suddenDeathScores[1]],
        suddenDeathRound: this.suddenDeathRound
      };
      // REMOVED: State now sent with path chunk finalGameState
      // if (this.matchControls) {
      //   this.matchControls.sendRollResult(this.myRollResult);
      // }
      this.saveGameStateToDb();
      return;
    }

    if (this.phase === 'sudden_death') {
      this.handleSuddenDeathTransition();

      if (this.isMultiplayer) {
        this.myRollResult.gameState = {
          scores: [this.scores[0], this.scores[1]],
          ballsLeft: this.ballsLeft,
          currentPlayer: this.currentPlayer,
          round: this.round,
          turnHits: [...this.turnHits],
          isSkeechPossible: this.isSkeechPossible,
          skeechSuccess: this.skeechSuccess,
          wildcardHit: this.wildcardHit,
          phase: this.phase,
          suddenDeathScores: [this.suddenDeathScores[0], this.suddenDeathScores[1]],
          suddenDeathRound: this.suddenDeathRound
        };
        // REMOVED: State now sent with path chunk finalGameState
        // if (this.matchControls) {
        //   this.matchControls.sendRollResult(this.myRollResult);
        // }
      }
      this.saveGameStateToDb();
      return;
    }

    this.resetTurnState();

    this.renderer.resetTrackers();

    // Stop any previous ball rolling animation before setting new ball count
    // Only do this if an animation is actually active to avoid clearing the count prematurely
    if (this.renderer.isRollingAnimationActive()) {
      this.renderer.stopRollingAnimation();
    }

    if (this.isMultiplayer) {
      const wasPlayer0Turn = this.currentPlayer === 0;

      if (wasPlayer0Turn) {
        this.currentPlayer = 1;
        this.isMyTurn = this.isHost;
        this.renderer.setIsMyTurn(this.isMyTurn);

        const player1Name = this.player2?.userName || 'Player 2'; // Player 1 is always player2 in our setup
        const isPlayer1CurrentUser = this.isHost; // Host is player 1
        this.renderer.setTurnIndicator(true, player1Name, isPlayer1CurrentUser);
        // Update ball color for player 1's turn
        this.setBallColorForCurrentPlayer();

        this.ballsLeft = this.ballsPerTurn;
        this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
        // Reserve a light for the launch ball at start of new turn
        this.renderer.setReserveAtLaunch(true);
        const p1Name = this.isHost ? this.currentUser?.userName : this.opponent?.userName;
        // Update tilt direction for player 1 (right tilt)
        this.physics.setTiltDirection(this.currentPlayer);
        this.physics.jitterTilt();
      } else {
        this.currentPlayer = 0;
        this.round += 1;
        this.isMyTurn = !this.isHost;
        this.renderer.setIsMyTurn(this.isMyTurn);

        const player0Name = this.player1?.userName || 'Player 1'; // Player 0 is always player1 in our setup
        const isPlayer0CurrentUser = !this.isHost; // Guest is player 0
        this.renderer.setTurnIndicator(true, player0Name, isPlayer0CurrentUser);
        // Update ball color for player 0's turn
        this.setBallColorForCurrentPlayer();

        this.ballsLeft = this.ballsPerTurn;
        this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
        // Reserve a light for the launch ball at start of new turn
        this.renderer.setReserveAtLaunch(true);
        this.renderer.round = this.round;
        const p0Name = this.isHost ? this.opponent?.userName : this.currentUser?.userName;
        // Update tilt direction for player 0 (left tilt)
        this.physics.setTiltDirection(this.currentPlayer);
        this.physics.jitterTilt();
      }
    } else {
      const wasPlayer0Turn = this.players === 2 && this.currentPlayer === 0;
      this.currentPlayer = wasPlayer0Turn ? 1 : 0;
      this.ballsLeft = this.ballsPerTurn;
      if (!wasPlayer0Turn) {
        this.round += 1;
        this.renderer.round = this.round;
      }
      // Update tilt direction for AI games
      if (this.aiPlayer) {
        this.physics.setTiltDirection(this.currentPlayer);
        this.physics.jitterTilt();
      } else if (!wasPlayer0Turn) {
        // Solo game - only jitter on new round
        this.physics.jitterTilt();
      }
      // AI challenge (non-multiplayer) turn indicator + myTurn state
      if (this.aiPlayer) {
        this.isMyTurn = (this.currentPlayer === 0); // human is player 0
        this.renderer.setIsMyTurn(this.isMyTurn);
        const playerName = this.currentPlayer === 0
          ? (this.player1?.userName || 'Player 1')
          : (this.player2?.userName || 'Player 2');
        this.renderer.setTurnIndicator(true, playerName, this.isMyTurn);
      }
    }    
    
    // Initialize balls-left indicator before animating
    this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
    this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
    // Persist latest turn owner/ballsLeft so game list reflects correct state
    // Skip roll history since no new rolls occurred during turn transition
    this.saveGameStateToDb();
    this.resetBall();
    
    if (this.isMultiplayer) {
      this.renderer.startPinballLightShow();
      const playerName = this.isMyTurn ? "YOUR TURN" : `${this.opponent?.userName?.toUpperCase()}'S TURN`;
      this.renderer.showPinballMessage(playerName, 0.5, 0.70, {
        color: this.isMyTurn ? 'amber' : 'blue',
        effect: 'glow',
        fontSize: 16,
        duration: 2000
      });      
      // Show turn start message to both players
      setTimeout(() => {
        this.triggerTurnStartMessage();
      }, 1000); // Wait 1 second after turn message
    }    
    if (this.aiPlayer) {
      this.renderer.startPinballLightShow();
    }    
    this.triggerAITurnIfNeeded();
  }

  private triggerAITurnIfNeeded() {
    if (!this.aiPlayer) {
      return; // No AI player or not in AI game
    }
    // In AI games: currentPlayer 0 = human, currentPlayer 1 = AI. isMyTurn = true means it's the human's turn. isMyTurn = false means it's the AI's turn
    if (!this.isMyTurn && this.currentPlayer === 1) {
      this.tutorialManager.notifyAITurnStarted();
      // It's the AI's turn - get the ball start position and current tilt
      const { play: playW } = this.world.worldRects();
      const launchMarginW = Math.max(this.ball.r * 1.25, 0.03 * playW.h);
      const ballStartPos = {
        x: playW.x + playW.w / 2,
        y: playW.y + playW.h - launchMarginW
      };

      // Stop pinball light show when AI starts its turn
      if (this.renderer.isPinballShowActive()) {
        this.renderer.stopPinballLightShow();
      }

      // Trigger AI move with current game state
      this.aiPlayer.startTurn(
        this.round,
        this.ballsPerTurn - this.ballsLeft, // current ball index (0-3)
        ballStartPos,
        this.physics.tiltX
      );
    }
  }

  private syncWithOpponentResult(localResult: string, localPts?: number, localCx?: number, localCy?: number) {
    if (localResult === "hole" && localPts && localCx && localCy) {      
      const { play: playPx } = this.board.rects(this.renderer.canvas, this.renderer.dpr);
      const { play: playW } = this.world.worldRects();
      const screenPos = this.world.worldToScreen({ x: localCx, y: localCy }, playPx, playW);
      this.renderer.notifyScored(screenPos.x, screenPos.y, localPts);
    } else if (localResult === "ditch") {
      console.log(`Passive player ditch detected`);
    } else if (localResult === "stop") {
      console.log(`Passive player stop detected`);
    } else if (localResult === "timeout") {
      console.log(`Passive player timeout detected`);
    }    
    if (this.pendingBallReset) { // If there's a pending ball reset (from network message arriving while physics was running), execute it now
      this.pendingBallReset = false;
      this.resetBall();
    }
    // Send ready signal to let opponent know we're ready for next ball (observers never send signals)
    if (!this.isObserver && this.matchControls) {
      this.matchControls.sendReadyForNext({
        kind: 'ready_for_next',
        round: this.round,
        ballIndex: 4 - this.ballsLeft,
        userId: this.currentUser.id
      });
    }
  }

  private resetTurnState() {
    this.turnHits = [];
    this.turnHasFailed = false;
    this.isSkeechPossible = false;
    this.skeechSuccess = false;
    this.wildcardHit = null;
  }

  async finishGameSave() {
    console.log('[finishGameSave] ===== CALLED =====');
    console.log('[finishGameSave] Stack trace:', new Error().stack);
    console.log('[finishGameSave] Match ID:', this.matchControls?.matchId);
    console.log('[finishGameSave] Tournament context:', this.currentTournamentContext);
    console.log('[finishGameSave] Scores:', this.scores);
    console.log('[finishGameSave] Phase:', this.phase);
    console.log('[finishGameSave] Is AI game:', !!this.aiPlayer);

    // Handle AI challenge games
    if (this.aiPlayer) {
      console.log('[finishGameSave] Finalizing AI challenge game record');

      if (!this.aiGameId) {
        console.error('[finishGameSave] No aiGameId found for AI game');
        return;
      }

      const p1Rolls = this.player1?.current_game_rolls || [];
      const p2Rolls = this.player2?.current_game_rolls || [];

      // Determine winner: use sudden death scores if applicable, otherwise regular scores
      let finalScore1 = this.scores[0];
      let finalScore2 = this.scores[1];
      if (this.suddenDeathScores[0] > 0 || this.suddenDeathScores[1] > 0) {
        finalScore1 = this.suddenDeathScores[0];
        finalScore2 = this.suddenDeathScores[1];
        console.log('[finishGameSave] AI game went to sudden death:', finalScore1, 'vs', finalScore2);
      }

      // Winner ID: only set if human wins (AI doesn't have a valid user ID)
      const humanWon = finalScore1 > finalScore2;
      const totalRolls = p1Rolls.length + p2Rolls.length;

      const finalState: Partial<IGame> = {
        p1Score: this.scores[0],
        p2Score: this.scores[1],
        rounds: this.round,
        rolls: totalRolls,
        points: this.scores[0] + this.scores[1],
        skeeches: this.skeechesThisGame,
        status: 'completed',
        // Only set winnerId if human won
        ...(humanWon && this.player1?.id ? { winnerId: this.player1.id } : {}),
        game_state_json: this.getCurrentGameState(),
        p1_roll_history: p1Rolls,
        p2_roll_history: p2Rolls
      };

      console.log('[finishGameSave] Updating AI game to completed:', this.aiGameId);

      try {
        await this.repo.updateGame(this.aiGameId, finalState);
        console.log('[finishGameSave] AI challenge game finalized successfully');
      } catch (error) {
        console.error('[finishGameSave] Failed to finalize AI challenge game:', error);
      }

      return;
    }

    if (this.isMultiplayer) {
      // For multiplayer, update the existing game record to be 'completed'
      if (!this.matchControls?.matchId) {
        console.log('[finishGameSave] No matchId, returning');
        return;
      }

      // CRITICAL: Wait for any pending roll history save to complete before finalizing
      // This prevents race conditions where the last roll's DB save might overwrite
      // the finishGameSave update or vice versa
      if (this.pendingPPRUpdate) {
        console.log('[finishGameSave] Waiting for pending PPR update to complete...');
        try {
          await this.pendingPPRUpdate;
          this.pendingPPRUpdate = null;
          console.log('[finishGameSave] Pending PPR update completed');
        } catch (err) {
          console.error('[finishGameSave] Error waiting for pending PPR update:', err);
        }
      }

      const p1Rolls = this.player1?.current_game_rolls || [];
      const p2Rolls = this.player2?.current_game_rolls || [];
      console.log('[finishGameSave] player1:', this.player1?.userName, 'rolls:', p1Rolls);
      console.log('[finishGameSave] player2:', this.player2?.userName, 'rolls:', p2Rolls);

      // ARCHITECTURE: Only the active player (who rolled the winning ball) calls finishGameSave
      // The active player saves the complete game state including both players' roll histories
      // The passive player waits for the save to complete, then fetches updated W-L stats
      const isCurrentUserPlayer1 = this.currentUser.id === this.player1?.id;
      const myRolls = isCurrentUserPlayer1 ? p1Rolls : p2Rolls;

      console.log('[finishGameSave] Current user is:', this.currentUser.userName, 'isPlayer1:', isCurrentUserPlayer1, 'myRollCount:', myRolls.length);

      // Determine winner: use sudden death scores if applicable, otherwise regular scores
      let finalScore1 = this.scores[0];
      let finalScore2 = this.scores[1];
      if (this.suddenDeathScores[0] > 0 || this.suddenDeathScores[1] > 0) {
        finalScore1 = this.suddenDeathScores[0];
        finalScore2 = this.suddenDeathScores[1];
        console.log('[finishGameSave] Game went to sudden death:', finalScore1, 'vs', finalScore2);
      }

      const winnerId = finalScore1 > finalScore2 ? this.player1?.id : this.player2?.id;
      console.log('[finishGameSave] Winner determined:', winnerId, '(', finalScore1, 'vs', finalScore2, ')');

      const finalState: Partial<IGame> = {
        p1Score: this.scores[0],
        p2Score: this.scores[1],
        rounds: this.round,
        skeeches: this.skeechesThisGame,
        status: 'completed',
        winnerId: winnerId,
        game_state_json: this.getCurrentGameState(), // Save final game state including phaseHistory and suddenDeathScores
        // Only save current user's roll history (not opponent's)
        ...(isCurrentUserPlayer1 ? { p1_roll_history: p1Rolls } : { p2_roll_history: p2Rolls })
      };
      console.log('[finishGameSave] Saving finalState:', finalState);

      try {
        await this.repo.updateGame(this.matchControls.matchId, finalState);
      } catch (error) {
        // Failsafe: if game doesn't exist (host failed to create it), create it now
        console.warn('[finishGameSave] Update failed, attempting to create game record:', error);

        // SAFETY CHECK: Only create game if we have actual game data
        // Don't create garbage records from stale state
        const hasValidScores = (this.scores[0] > 0 || this.scores[1] > 0) && this.round > 0;
        const hasRollData = myRolls.length > 0;

        if (!hasValidScores || !hasRollData) {
          console.error('[finishGameSave] ABORTED: Game has no valid data. Scores:', this.scores, 'Round:', this.round, 'My rolls:', myRolls.length);
          console.error('[finishGameSave] This likely indicates stale state or a double-disconnect. Not creating game record.');
          return;
        }

        try {
          const completeGame: IGame = {
            id: this.matchControls.matchId,
            isSoloGame: false,
            p1UserId: this.player1?.id || '',
            p2UserId: this.player2?.id || '',
            p1Score: this.scores[0],
            p2Score: this.scores[1],
            rounds: this.round,
            rolls: myRolls.length, // Only count rolls we know about
            points: this.scores[0] + this.scores[1],
            skeeches: this.skeechesThisGame,
            timeStamp: Date.now(),
            status: 'completed',
            winnerId: winnerId, // Use the same winner calculation from above
            game_state_json: this.getCurrentGameState(),
            // Only include current user's roll history in failsafe creation
            ...(isCurrentUserPlayer1
              ? { p1_roll_history: p1Rolls, p2_roll_history: [] }
              : { p1_roll_history: [], p2_roll_history: p2Rolls })
          };

          // Add tournament context if applicable
          if (this.currentTournamentContext) {
            (completeGame as any).tournament_id = this.currentTournamentContext.tournamentId;
            (completeGame as any).tournament_round = this.currentTournamentContext.round;
            (completeGame as any).tournament_match = this.currentTournamentContext.matchIndex;
          }

          await this.repo.addGame(completeGame as any);
          console.log('[finishGameSave] Game created successfully as failsafe');
        } catch (createError) {
          console.error('[finishGameSave] Failed to create game record:', createError);
        }
      }

      // If this game is a tournament match, record result and refresh bracket
      try {
        const gameRow = await (this.repo as any).sb.getGameById(this.matchControls.matchId);
        const fallbackTournamentId = this.currentTournamentContext?.tournamentId;
        const hasDbTournamentCtx = gameRow && gameRow.tournament_id && gameRow.tournament_round && (gameRow.tournament_match || gameRow.tournament_match === 0);

        console.log('[finishGameSave] Tournament check:', {
          matchId: this.matchControls.matchId,
          hasDbTournamentCtx,
          currentTournamentContext: this.currentTournamentContext,
          gameRowContext: gameRow ? { tid: gameRow.tournament_id, round: gameRow.tournament_round, match: gameRow.tournament_match } : null
        });

        if (hasDbTournamentCtx) {
          // SAFETY CHECK: Verify game has roll data before recording tournament result
          const hasRollData = (gameRow.p1_roll_history && gameRow.p1_roll_history.length > 0) ||
                             (gameRow.p2_roll_history && gameRow.p2_roll_history.length > 0);

          if (!hasRollData) {
            console.error('[finishGameSave] ABORTED tournament result recording: Game has no roll data!');
            console.error('[finishGameSave] This likely indicates a false game completion. Game ID:', this.matchControls.matchId);
            console.error('[finishGameSave] Context mismatch? Current:', this.currentTournamentContext, 'Game:', gameRow);
            return;
          }

          // SAFETY CHECK: Verify the game context matches currentTournamentContext
          if (this.currentTournamentContext) {
            const contextMatches =
              gameRow.tournament_id === this.currentTournamentContext.tournamentId &&
              gameRow.tournament_round === this.currentTournamentContext.round &&
              gameRow.tournament_match === this.currentTournamentContext.matchIndex;

            if (!contextMatches) {
              console.error('[finishGameSave] ABORTED: Context mismatch!');
              console.error('[finishGameSave] Game row:', { tid: gameRow.tournament_id, round: gameRow.tournament_round, match: gameRow.tournament_match });
              console.error('[finishGameSave] Current context:', this.currentTournamentContext);
              console.error('[finishGameSave] This indicates stale tournament context. Not recording result.');
              return;
            }
          }

          // For sudden death games, use sudden death scores instead of 21-21
          let score1 = this.scores[0];  // player1's score
          let score2 = this.scores[1];  // player2's score
          if (this.phase === 'over' && (this.suddenDeathScores[0] > 0 || this.suddenDeathScores[1] > 0)) {
            score1 = this.suddenDeathScores[0];
            score2 = this.suddenDeathScores[1];
          }

          const sumRollHistory = (history?: any[] | null): number | null => {
            if (!Array.isArray(history) || history.length === 0) return null;
            let total = 0;
            for (const value of history) {
              const num = typeof value === 'number' ? value : (typeof value === 'string' ? Number(value) : NaN);
              if (Number.isFinite(num)) total += num;
            }
            return total;
          };
          const p1RollHistory = Array.isArray(gameRow.p1_roll_history) ? gameRow.p1_roll_history : [];
          const p2RollHistory = Array.isArray(gameRow.p2_roll_history) ? gameRow.p2_roll_history : [];
          const p1RollTotal = sumRollHistory(p1RollHistory);
          const p2RollTotal = sumRollHistory(p2RollHistory);
          let playerAPoints: number | null = p1RollTotal;
          let playerBPoints: number | null = p2RollTotal;

          const tournamentRow = await (this.repo as any).sb.getTournamentById(gameRow.tournament_id);
          const tournamentType = tournamentRow?.tournament_type || 'bracket';

          // For round robin, we need to map player1/player2 scores to bracket positions (playerA/playerB)
          let aScore = score1;
          let bScore = score2;
          let winnerUserId = score1 >= score2 ? this.player1?.id : this.player2?.id;

          if (tournamentType === 'round_robin') {
            // Get the round robin match to determine player positions
            let matchFound = false;
            try {
              const rrData = tournamentRow?.round_robin_data;
              console.log('[finishGameSave] Round robin processing:', {
                hasRRData: !!rrData,
                scheduleLength: Array.isArray(rrData?.schedule) ? rrData.schedule.length : 0,
                lookingFor: { round: gameRow.tournament_round, matchIndex: gameRow.tournament_match }
              });
              if (rrData?.schedule && Array.isArray(rrData.schedule)) {
                const match = rrData.schedule.find((m: any) =>
                  m.round === gameRow.tournament_round && m.matchIndex === gameRow.tournament_match
                );
                if (match) {
                  matchFound = true;
                  // CRITICAL: Use the database p1_user_id/p2_user_id to map scores correctly
                  // Do NOT use this.player1?.id because it might have changed since game creation
                  const dbP1UserId = gameRow.p1_user_id;
                  const dbP2UserId = gameRow.p2_user_id;
                  const isDbP1InPositionA = match.playerA?.userId === dbP1UserId;

                  console.log('[finishGameSave] MATCH FOUND - Round robin player position mapping:', {
                    dbP1: { id: dbP1UserId, score: score1 },
                    dbP2: { id: dbP2UserId, score: score2 },
                    matchPlayerA: { id: match.playerA?.userId, seed: match.playerA?.seed },
                    matchPlayerB: { id: match.playerB?.userId, seed: match.playerB?.seed },
                    isDbP1InPositionA,
                    beforeMapping: { aScore: score1, bScore: score2 }
                  });

                  if (isDbP1InPositionA) {
                    // database p1 is in position A, database p2 is in position B
                    aScore = score1;
                    bScore = score2;
                    winnerUserId = score1 >= score2 ? dbP1UserId : dbP2UserId;
                    playerAPoints = p1RollTotal;
                    playerBPoints = p2RollTotal;
                  } else {
                    // database p1 is in position B, database p2 is in position A
                    aScore = score2;
                    bScore = score1;
                    winnerUserId = score2 >= score1 ? dbP2UserId : dbP1UserId;
                    playerAPoints = p2RollTotal;
                    playerBPoints = p1RollTotal;
                  }

                  console.log('[finishGameSave] After position mapping:', {
                    aScore,
                    bScore,
                    winnerUserId,
                    dbP1Correct: isDbP1InPositionA ? aScore === score1 : aScore === score2,
                    dbP2Correct: isDbP1InPositionA ? bScore === score2 : bScore === score1
                  });
                } else {
                  console.warn('[finishGameSave] MATCH NOT FOUND in schedule. Looking for round', gameRow.tournament_round, 'matchIndex', gameRow.tournament_match);
                  console.warn('[finishGameSave] Available matches:', rrData.schedule.map((m: any) => ({ round: m.round, matchIndex: m.matchIndex })));
                  console.warn('[finishGameSave] USING DEFAULT MAPPING (may be incorrect!)');
                }
              } else {
                console.warn('[finishGameSave] Round robin data or schedule not found');
                console.warn('[finishGameSave] USING DEFAULT MAPPING (may be incorrect!)');
              }
            } catch (e) {
              console.warn('[finishGameSave] Failed to determine round robin player positions, using default mapping:', e);
            }
          }

          if (winnerUserId) {
            console.log('[finishGameSave] Recording tournament result:', {
              tournamentId: gameRow.tournament_id,
              round: gameRow.tournament_round,
              matchIndex: gameRow.tournament_match,
              aScore,
              bScore,
              winnerUserId
            });
            const recordResult = tournamentType === 'round_robin'
              ? (params: any) => (this.repo as any).sb.recordRoundRobinMatchResult(params)
              : (params: any) => (this.repo as any).sb.recordTournamentMatchResult(params);
            const payload: any = {
              tournamentId: gameRow.tournament_id,
              round: gameRow.tournament_round,
              matchIndex: gameRow.tournament_match,
              aScore,
              bScore,
              winnerUserId
            };
            const safePlayerAPoints = (typeof playerAPoints === 'number' && Number.isFinite(playerAPoints)) ? playerAPoints : undefined;
            const safePlayerBPoints = (typeof playerBPoints === 'number' && Number.isFinite(playerBPoints)) ? playerBPoints : undefined;
            if (safePlayerAPoints !== undefined) {
              payload.playerAPoints = safePlayerAPoints;
            }
            if (safePlayerBPoints !== undefined) {
              payload.playerBPoints = safePlayerBPoints;
            }

            const updatedTourney = await recordResult(payload);
            try {
              // If that result completed the tournament, celebrate the champion on the winner's client
              if (updatedTourney && updatedTourney.status === 'completed') {
                const winnerIdFinal = winnerUserId;
                const isChampionClient = this.currentUser?.id === winnerIdFinal;
                if (isChampionClient) {
                  const champName = (this.player1 && this.player1.id === winnerIdFinal) ? this.player1.userName
                                    : (this.player2 && this.player2.id === winnerIdFinal) ? this.player2.userName
                                    : 'Champion';
                  const tourneyName = updatedTourney.name || 'Tournament';
                  // Fire banner, confetti, and fireworks overlay
                  showChampionCelebration({ tournamentName: tourneyName, championName: champName });
                }
              }
            } catch (celebrateErr) {
              console.warn('Champion celebration failed to render:', celebrateErr);
            }
            // Persist tournament award stats for current user
            try {
              const delta = this.achievementsTracker.getTournamentDelta();
              await this.repo.upsertTournamentPlayerStats(gameRow.tournament_id, this.currentUser.id, delta);
            } catch (e) { console.warn('Failed to update tourney stats', e); }
            // If tournament view is open, refresh it
            const tl = (this as any).tournamentLobby;
            if (tl && typeof tl.refresh === 'function') {
              await tl.refresh();
            }
          }
        } else if (fallbackTournamentId) {
          // Legacy/fallback: ensure awards stats still persist even if game row lacks tournament context
          try {
            const delta = this.achievementsTracker.getTournamentDelta();
            await this.repo.upsertTournamentPlayerStats(fallbackTournamentId, this.currentUser.id, delta);
            const tl = (this as any).tournamentLobby;
            if (tl && typeof tl.refresh === 'function') {
              await tl.refresh();
            }
          } catch (e) {
            console.warn('Fallback tourney stats update failed:', e);
          }
        }
      } catch (e) {
        console.warn('Tournament advancement update failed:', e);
      }

    } else {
      // For solo games, insert a new record
      const game: IGame = {
        id: v4(), // Use proper UUID from v4 import
        isSoloGame: true,
        p1UserId: this.currentUser.id,
        p2UserId: null,
        p1Score: this.scores[0],
        p2Score: null,
        rounds: this.round,
        rolls: this.rollsThisGame,
        points: this.scores[0],
        skeeches: this.skeechesThisGame,
        timeStamp: Date.now(),
        status: 'completed',
      };
      await this.repo.addGame(game);
    }
    // Update user stats - ONLY for real multiplayer games (not solo, not AI)
    if (this.currentUser && this.isMultiplayer && this.player1 && this.player2) {
      const isAIGame = this.player1.id === 'ai_robo_roller' || this.player2.id === 'ai_robo_roller';

      if (!isAIGame) {
        // This is a real multiplayer game - count it toward permanent stats
        const u = { ...this.currentUser } as IUser;

        // Determine which player index the current user is
        const currentUserPlayerIndex = this.currentUser.id === this.player1.id ? 0 : 1;
        const currentUserPlayer = currentUserPlayerIndex === 0 ? this.player1 : this.player2;

        // Add current user's score and rolls
        u.pointsTotal += this.scores[currentUserPlayerIndex];
        u.roundsTotal += this.round;
        u.numOfSkeeches += this.skeechesThisGame;

        // rollsTotal: use rolls from the correct player object
        u.rollsTotal += (currentUserPlayer.current_game_rolls?.length || 0);

        // Clear temporary current_game_rolls (already saved to game table)
        u.current_game_rolls = [];

        console.log('[finishGameSave] Game completed. W-L will be calculated on-the-fly when viewing profiles.');

        // Update opponent's stats (points, rolls, etc. - NOT W-L)
        if (!this.currentTournamentContext) {
          const opponentUser = this.currentUser.id === this.player1.id ? this.player2 : this.player1;
          if (opponentUser && opponentUser.id !== this.currentUser.id) {
            try {
              const opponentUserData = await this.repo.getUserById(opponentUser.id);
              if (opponentUserData) {
                const opponentUpdate = { ...opponentUserData };

                // Update opponent's permanent stats (NOT W-L)
                const opponentPlayerIndex = this.currentUser.id === this.player1.id ? 1 : 0;
                opponentUpdate.pointsTotal += this.scores[opponentPlayerIndex];
                opponentUpdate.roundsTotal += this.round;
                opponentUpdate.numOfSkeeches += this.skeechesThisGame;

                // rollsTotal is now the sum of all rolls across completed games
                opponentUpdate.rollsTotal += (opponentUser.current_game_rolls?.length || 0);

                // Clear opponent's current_game_rolls (already saved to game table)
                opponentUpdate.current_game_rolls = [];

                opponentUpdate.updatedAt = Date.now();

                await this.repo.upsertUser(opponentUpdate);

                // NOTE: Opponent's avgPPR is already accurate from their last roll
                // No need to recalculate from DB - saves unnecessary query
              }
            } catch (error) {
              console.error('Failed to update opponent stats:', error);
            }
          }
        }
        u.updatedAt = Date.now();

        this.currentUser = await this.repo.upsertUser(u);

        // NOTE: avgPPR is already accurate from updatePersistentPPRAfterRoll on the last roll
        // No need to recalculate from DB here - just persist the current value
        // This saves a massive DB query that fetches all games

        // Refresh presence to broadcast updated stats to lobby
        if (this.lobbyManager) {
          this.lobbyManager.refreshPresence();
        }

        // Refresh W-L stats for player cards after game completion
        try {
          // For round robin tournaments, reload standings to get updated W-L
          if (this.currentTournamentType === 'round_robin' && this.currentTournamentContext) {
            console.log('[finishGameSave] Refreshing round-robin W-L stats from tournament standings');
            const tournament = await (this.repo as any).sb?.getTournamentById(this.currentTournamentContext.tournamentId);
            if (tournament?.round_robin_data?.standings) {
              const standings = tournament.round_robin_data.standings;
              standings.forEach((entry: any) => {
                this.tournamentPlayerRecords[entry.userId] = {
                  wins: entry.wins ?? 0,
                  losses: entry.losses ?? 0
                };
              });
              console.log('[finishGameSave] Updated tournament W-L records:', this.tournamentPlayerRecords);

              // Update renderer with fresh tournament records
              this.renderer.setTournamentPlayerRecords(this.tournamentPlayerRecords);
              this.renderer.updatePlayerCards(this.player1, this.player2);
            }
          } else if (!this.currentTournamentContext) {
            // For non-tournament games, calculate W-L from all multiplayer games
            console.log('[finishGameSave] Refreshing W-L stats for non-tournament game');
            const p1WL = await this.repo.calculateWinLoss(this.player1.id, { excludeTournaments: true, excludeAI: true });
            const p2WL = await this.repo.calculateWinLoss(this.player2.id, { excludeTournaments: true, excludeAI: true });
            this.player1.wins = p1WL.wins;
            this.player1.losses = p1WL.losses;
            this.player1.gamesPlayed = p1WL.gamesPlayed;
            this.player2.wins = p2WL.wins;
            this.player2.losses = p2WL.losses;
            this.player2.gamesPlayed = p2WL.gamesPlayed;
            console.log('[finishGameSave] Updated Player 1 W-L:', p1WL);
            console.log('[finishGameSave] Updated Player 2 W-L:', p2WL);

            // Update the player card display with fresh stats
            this.renderer.updatePlayerCards(this.player1, this.player2);
          }
          // Bracket tournaments don't show W-L, so no update needed
        } catch (e) {
          console.error('[finishGameSave] Failed to refresh W-L stats for player cards', e);
        }
      }
    }
    // Note: Achievements are automatically saved as part of tournament stats (see line 2810)
    // via this.achievementsTracker.getTournamentDelta() which includes the achievements array
  }
  
  // Pointer input
  onDownFromRenderer(p:{x:number;y:number}, t:number) {
    if (this.ball.rolling || this.phase === 'over' || this.physics.sink.active) return;
    // Convert screen touch to world coordinates
    const { play:playPx } = this.board.rects(this.renderer.canvas, this.renderer.dpr);
    const { play:playW } = this.world.worldRects();
    const startW = this.world.screenToWorld(p, playPx, playW);

    // Restrict ball launch to ±Y buffer from starting position
    const launchMarginW = Math.max(this.ball.r * 1.25, 0.03 * playW.h);
    const expectedLaunchY = playW.y + playW.h - launchMarginW;
    const yDiff = Math.abs(startW.y - expectedLaunchY);

    if (yDiff > TIMING.LAUNCH_Y_BUFFER) {
      // Touch is outside allowed Y range - reject launch
      return;
    }

    this.ball.pos = startW;
    this.isAiming = true;
    this.gesture = { points: [p], start: p, tStart: t };
  }

  onMoveFromRenderer(p:{x:number;y:number}, _t:number) { 
    if (!this.isAiming) return;
    this.gesture.points.push(p);
    if (this.gesture.points.length > 120) this.gesture.points.shift();
  }

  onUpFromRenderer(p:{x:number;y:number}, t:number) {
    if (!this.isAiming) return;
    this.isAiming = false;
    this.gesture.end = p; this.gesture.tEnd = t;    
    const pts = this.gesture.points; if (!pts.length) return;
    const N = Math.min(12, pts.length - 1);
    const a = pts[pts.length - 1 - N], b = pts[pts.length - 1];
    const dt = Math.max(0.016, (t - (this.gesture.tStart! + (pts.length - 1 - N) * 8)) / 1000);
    const vx = (b.x - a.x) / dt, vy = (b.y - a.y) / dt;
    if (vy >= -20 || this.ball.rolling) { return; }
    // DEBUG: comment out noisy logs in production to reduce main-thread work
    // console.log("velocity (y): ", vy);
    this.triggerVelocityMessage(vy);
    const spin = estimateSpin(pts);
    const vScaled = { x: vx * this.physics.speedScale, y: vy * this.physics.speedScale };        
    const startW = { x: this.ball.pos.x, y: this.ball.pos.y }; // Normalize to play rect units
    const { play:playPx } = this.board.rects(this.renderer.canvas, this.renderer.dpr);
    const { play:playW  } = this.world.worldRects();
    const velW = {
      x: (vx * this.physics.speedScale) * (playW.w / playPx.w),
      y: (vy * this.physics.speedScale) * (playW.w / playPx.w), // use width scale consistently
    };
    const totalSpeed = Math.hypot(velW.x, velW.y);
    // console.log(`Initial speed: ${totalSpeed}`);
    this.renderer.updateSpeedWidgets(totalSpeed);    
    this.lastLaunchSpeed = totalSpeed;
    messageManager.reserveMessageSlot('velocity', velW.y);
    this.ball.pos = startW;
    this.ball.vel = velW;
    this.ball.spin = spin;
    this.ball.rolling = true;
    this.renderer.updateNextBallButtonState();
    
    // Reset per-roll physics counters for awards
    try { this.physics.popCountThisRoll = 0; } catch {}

    // Remember which player launched this roll (used for passive roll history sync)
    this.lastRollPlayerIndex = this.currentPlayer;

    // Start path recording for multiplayer
    if (this.isMultiplayer && this.isMyTurn) {
      this.recordedPath = [];
      this.pathRecordingStartTime = performance.now();
      this.lastPathRecordTime = 0;  // Reset throttle timer
      this.lastRecordedPos = null;  // Reset position tracking
      this.stationaryFrameCount = 0;  // Reset stationary counter
      this.isRecordingPath = true;
      this.startPathBatching();
    }

    // Send WORLD coordinates for deterministic physics
    const rollInput: RollInput = {
      kind: 'roll_input',
      round: this.round,
      ballIndex: (this.ballsPerTurn - this.ballsLeft), // 0..3 this turn
      start: { x: this.ball.pos.x, y: this.ball.pos.y }, // World coordinates
      velFracPerSec: { x: velW.x, y: velW.y }, // World velocity (already converted above)
      spin,
      tiltX: this.physics.tiltX,
      t0: performance.now(),
    };
    if (this.matchControls) {
      this.matchControls.sendRollInput(rollInput);
    }

    // Log roll parameters for test mode reproduction
  //   console.log(`?? ROLL PARAMETERS (copy to test mode):
  // vx: ${velW.x.toFixed(4)}
  // vy: ${velW.y.toFixed(4)}
  // spin: ${spin.toFixed(6)}
  // tiltX: ${this.physics.tiltX.toFixed(6)}
  // speed: ${totalSpeed.toFixed(2)} px/s
  // ballIndex: ${(this.ballsPerTurn - this.ballsLeft)}
  // round: ${this.round}`);
  }

  async startAIChallenge(tutorial: boolean = false) {
    this.tutorialManager.setActive(tutorial);
    if (tutorial) {
      this.tutorialManager.showPointer();
    } else {
      this.tutorialManager.hidePointer();
    }
    await this.resetGame();
    await this.lobbyManager.updateMode('solo');
    this.aiPlayer = new AIPlayer();
    this.aiPlayer.setMoveCallback((rollInput: RollInput) => {
      this.handleAIMove(rollInput);
    });
    this.isMultiplayer = false;// true;  // Use multiplayer logic but with AI
    this.players = 2;
    this.player1 = this.currentUser;

    // For AI games, reset avgPPR to 0 so it shows game-scoped PPR only
    // (will be calculated from current_game_rolls by recalcAverages after first roll)
    this.player1.avgPPR = 0;

    const aiInfo = this.aiPlayer.getPlayerInfo();
    this.player2 = {
        id: aiInfo.userId,
        userName: aiInfo.userName,
        wins: 0,
        losses: 0,
        numOfSkeeches: 0,
        pointsTotal: 0,
        rollsTotal: 0,
        roundsTotal: 0,
        gamesPlayed: 0,
        avgPPR: 0,
        current_game_rolls: [], // Initialize for PPR calculation
        createdAt: Date.now(),
        updatedAt: Date.now(),
    };
    this.opponent = this.player2;
    this.isHost = false;  // Human is guest, AI is host in turn calculation logic

    // Guest (player1 = human) goes first
    this.currentPlayer = 0;
    this.isMyTurn = true;  // Human starts first (guest when currentPlayer === 0)

    // Create initial AI game record
    this.aiGameId = v4();
    const initialAIGame: IGame = {
      id: this.aiGameId,
      isSoloGame: true,
      p1UserId: this.player1.id,
      p2UserId: null, // AI player is not a real user, so use null
      p1Score: 0,
      p2Score: 0,
      rounds: 1,
      rolls: 0,
      points: 0,
      skeeches: 0,
      timeStamp: Date.now(),
      status: 'active',
      game_state_json: this.getCurrentGameState(),
    };

    try {
      await this.repo.addGame(initialAIGame as any);
      console.log('[startAIChallenge] AI game record created:', this.aiGameId);
    } catch (error) {
      console.error('[startAIChallenge] Failed to create AI game record:', error);
    }

    await this.renderer.loadGamePreferences(this.currentUser.id);
    this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
    (window as any).game = this;
    this.renderer.startPinballLightShow();
    if (tutorial) {
      this.renderer.showTutorialIntroOnBoard(3000, () => this.tutorialManager.onIntroComplete());
    } else {
      this.renderer.showMatchIntroOnBoard(this.currentUser.userName, this.aiPlayer.userName, 5000);
    }
    this.renderer.triggerGameStartRipple();
    setTimeout(() => {
      this.renderer.showBoard();
      this.renderer.setIsMyTurn(true);
      this.renderer.setTurnIndicator(true, this.currentUser.userName, true); // Turn on "YOUR TURN" lights
      this.renderer.triggerGameStartRipple();
      setTimeout(() => {
        this.triggerTurnStartMessage();
      }, 500);
    }, 1000);
    // Test mode keyboard shortcut (z)
    window.addEventListener('keydown', (e) => {
      if (e.key.toLowerCase() === 'z') {
        this.testMode = !this.testMode;
        (this.renderer as any).testMode = this.testMode; // Pass to renderer for widgets

        // Force HUD update to refresh widget visibility immediately
        this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.currentUser, undefined, this.players, this.phase, this.bustHistory);

        if (this.testMode) {
          console.log('🔬 TEST MODE ENABLED - Auto-rolling balls. Press Z to stop.');
          // Randomize tilt once when entering test mode
          this.physics.jitterTilt();
          this.testBaseTilt = this.physics.tiltX;  // Store the randomized base tilt
          this.testStats = {
            totalRolls: 0,
            apollo13Count: 0,
            rollsWithMoreThan025Orbit: 0,
            rollsWithMoreThan025OrbitThatSank: 0,
            rollsWithMoreThan025OrbitThatEscaped: 0,
            rollsWithMoreThan1Orbit: 0,
            rollsWithMoreThan1OrbitThatSank: 0,
            rollsWithMoreThan1OrbitThatEscaped: 0,
            railroadCount: 0,
            lastOrbitFrames: -1,
            maxOrbitFrames: 0
          };
          // Kick off the first ball immediately
          setTimeout(() => this.autoRollTestBall(), 100);
        }
      }
    });
  }  
  //Handle AI move by applying the RollInput to the game state. Uses the same velocity conversion logic as human input
  
  private handleAIMove(rollInput: RollInput) {    
    if (this.tutorialManager.isActive){
      rollInput.tiltX = -50;
    }
    this.physics.tiltX = rollInput.tiltX;    
    const { play: playPx } = this.board.rects(this.renderer.canvas, this.renderer.dpr);
    const { play: playW } = this.world.worldRects();    
    const velW = {
      x: rollInput.velFracPerSec.x * (playW.w / playPx.w),
      y: rollInput.velFracPerSec.y * (playW.w / playPx.w)
    };    
    this.ball.pos = { x: rollInput.start.x, y: rollInput.start.y };
    this.ball.vel = velW;
    this.ball.spin = rollInput.spin;
    this.ball.rolling = true;
    this.renderer.updateNextBallButtonState();    

    // Update speed widget for AI rolls
    const totalSpeed = Math.hypot(velW.x, velW.y);
    console.log(`AI Initial speed: ${totalSpeed}`);
    this.renderer.updateSpeedWidgets(totalSpeed);
  }

  async resetGame() {
    this.renderer.reset();
    this.renderer.hideRetributionOverlay();
    this.renderer.hideSuddenDeathOverlay();
    this.renderer.hideOverlay();
    this.renderer.clearPinballMessages();
    if (messageManager) {
      messageManager.clearCurrentMessage();
    }
    this.scores = [0, 0];
    this.round = 1;
    this.currentPlayer = 0;
    this.ballsLeft = this.ballsPerTurn;
    this.turnHits = [];
    this.turnHasFailed = false;
    this.isSkeechPossible = false;
    this.skeechSuccess = false;
    this.wildcardHit = null;

    // Initialize current_game_rolls for live stats tracking
    if (this.currentUser) {
      this.currentUser.current_game_rolls = [];
    }
    if (this.player1 && this.player1.id !== 'ai_robo_roller') {
      this.player1.current_game_rolls = [];
    }
    if (this.player2 && this.player2.id !== 'ai_robo_roller') {
      this.player2.current_game_rolls = [];
    }
    this.phase = 'normal';
    this.rollsThisGame = 0;
    this.bustHistory = [];
    this.phaseHistory = [];
    this.suddenDeathScores = [0, 0];
    this.suddenDeathRound = 1;
    this.pendingBallReset = false;
    this.opponentRollResult = null as any;
    this.opponentReady = false;
    this.lastForfeitTime = 0;
    if (!this.isMultiplayer) {
      await this.renderer.loadGamePreferences();
    }
    this.renderer.resize();
    // Initialize balls-left indicator before animating to launch
    this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
    this.resetBall();
    // Set tilt direction based on current player (for multiplayer/AI games)
    if (this.isMultiplayer || this.aiPlayer) {
      this.physics.setTiltDirection(this.currentPlayer);
    }
    this.physics.resetTilt();
    if (this.tutorialManager.isActive) {
      this.tutorialManager.applyTilt();
    } else {
      this.physics.jitterTilt();
    }    
    this.renderer.round = 1;
    // balls-left already set prior to resetBall
    this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
    if (this.aiPlayer) {
      this.renderer.setTurnIndicator(true, this.currentUser.userName, true);
    } else if (this.isMultiplayer) {
      // Recalculate isMyTurn for rematch scenarios
      // Player 0 always goes first, and player 0 is the non-host (guest)
      // But observers always remain passive
      if (!this.isObserver) {
        this.isMyTurn = !this.isHost;
      }
      this.renderer.setIsMyTurn(this.isMyTurn);

      const startingPlayerName = this.currentPlayer === 0 ? this.player1?.userName || 'Player 1' : this.player2?.userName || 'Player 2';
      const startingPlayerId = this.currentPlayer === 0 ? this.player1?.id : this.player2?.id;
      const isStartingPlayerCurrentUser = startingPlayerId === this.currentUser.id;
      this.renderer.setTurnIndicator(true, startingPlayerName, isStartingPlayerCurrentUser);
    }
  }

  async requestRematch() {
    await this.finishGamePromise;
    if (this.players === 1) {
      // For solo games, just reset the game
      await this.resetGame();
    } else {
      // For multiplayer, reuse the invite logic to send a rematch request
      const opponent = this.currentUser.id === this.player1?.id ? this.player2 : this.player1;
      if (!opponent) {
        console.error("Cannot request rematch, opponent not found.");
        return;
      }

      const from = { userId: this.currentUser?.id, userName: this.currentUser?.userName };
      const to = { userId: opponent.id, userName: opponent.userName };

      if (!from.userId || !from.userName) {
        console.error("Cannot send rematch request, current user is not defined.");
        return;
      }

      // Send rematch invitation (reusing the invite system)
      this.lobbyManager.sendInvite({
        kind: 'invite',
        matchId: v4(),
        from: from,
        to: to
      });

      // Show waiting overlay with rematch-specific messaging
      this.renderer.showOverlay(`Requesting rematch with ${opponent.userName}...`, "?", () => {
        // Send cancellation message
        this.lobbyManager.sendInvite({
          kind: 'invite_cancel',
          from: from,
          to: to
        });
        // Cancel and return to lobby
        this.renderer.hideOverlay();
        this.returnToLobby();
      });
    }
  }

  async requestRestart() {
    if (this.isMultiplayer && this.matchControls) {      
      const opponent = this.currentUser.id === this.player1?.id ? this.player2 : this.player1;
      if (!opponent || !this.matchControls?.matchId) {
        console.error("No opponent or match ID found for restart request");
        return;
      }

      this.renderer.showOverlay(`Requesting restart with ${opponent.userName}...`, "ÃƒÂ¢Ã‚ÂÃ…â€™", () => {
        this.lobbyManager.sendRestartCancel(this.matchControls.matchId, { userId: opponent.id, userName: opponent.userName });
        this.renderer.hideOverlay();
      });

      await this.lobbyManager.sendRestartRequest(this.matchControls.matchId, { userId: opponent.id, userName: opponent.userName });
    } else {
      // Handle AI games and solo practice
      if (this.aiPlayer) {
        this.aiPlayer.cancelTurn();

        // If game is over, start fresh AI challenge (creates new game record)
        // Otherwise, just reset current game (mid-game restart)
        if (this.renderer.isGameOver) {
          await this.startAIChallenge(false);
        } else {
          await this.resetGame();
          // For mid-game AI restart, reset PPR to 0 so it's game-scoped only
          if (this.player1) this.player1.avgPPR = 0;
          if (this.player2) this.player2.avgPPR = 0;
          // Update UI to reflect the reset PPR values
          this.renderer.updatePlayerCards(this.player1, this.player2);
        }
      } else {
        // Solo practice mode (no AI)
        await this.resetGame();
      }
    }
  }

  async initializeAsObserver(matchId: string, player1: IUser, player2: IUser, opts?: { tournamentId?: string; round?: number; matchIndex?: number }) {
    console.log('[Observer Mode] Initializing as observer for match:', matchId);

    // Set observer mode flags
    this.isObserver = true;
    this.isMultiplayer = true;
    this.players = 2;
    this.observerMatchId = matchId;
    this.isMyTurn = false; // Observers are always passive

    // Store tournament context if provided
    if (opts?.tournamentId) {
      this.currentTournamentContext = {
        tournamentId: opts.tournamentId,
        round: opts.round!,
        matchIndex: opts.matchIndex!
      };

      // Load tournament data for observers
      try {
        const tournament = await (this.repo as any).sb?.getTournamentById(opts.tournamentId);
        if (tournament) {
          this.currentTournamentType = tournament.tournament_type as 'bracket' | 'round_robin' || 'bracket';

          // For round-robin tournaments, load player W-L records from standings
          if (this.currentTournamentType === 'round_robin' && tournament.round_robin_data?.standings) {
            const standings = tournament.round_robin_data.standings;
            standings.forEach((entry: any) => {
              this.tournamentPlayerRecords[entry.userId] = {
                wins: entry.wins ?? 0,
                losses: entry.losses ?? 0
              };
            });
            console.log('[Observer] Loaded round-robin W-L records:', this.tournamentPlayerRecords);
          }
        }
      } catch (e) {
        console.error('[Observer] Failed to load tournament data:', e);
      }
    }

    // Hide UI elements
    this.renderer.hideOverlay();
    this.renderer.hideLobby();
    this.lobbyController?.hide();

    // Reset game state
    await this.resetGame();

    // Set matchId AFTER reset (reset clears it to null)
    this.renderer.setCurrentMatchId(matchId);

    // Set player info
    this.player1 = player1;
    this.player2 = player2;

    // Fetch current game state from database
    try {
      const game = await this.repo.getUnfinishedGameById(matchId);
      console.log('[Observer Mode] Fetched game record:', {
        matchId,
        hasGame: !!game,
        status: game?.status,
        p1_roll_history_length: game?.p1_roll_history?.length,
        p2_roll_history_length: game?.p2_roll_history?.length,
        time_stamp: game?.time_stamp,
        tournament_id: game?.tournament_id
      });
      if (game?.game_state_json) {
        const state = game.game_state_json as GameStateSync;
        console.log('[Observer Mode] Loaded game state from database:', state);

        // Apply the game state
        this.scores = [state.scores[0], state.scores[1]];
        this.ballsLeft = state.ballsLeft;
        this.currentPlayer = state.currentPlayer;
        this.round = state.round;
        this.turnHits = [...state.turnHits];
        this.isSkeechPossible = state.isSkeechPossible || false;
        this.skeechSuccess = state.skeechSuccess || false;
        this.wildcardHit = state.wildcardHit || null;
        this.phase = state.phase || 'normal';

        if (state.suddenDeathScores) {
          this.suddenDeathScores = [state.suddenDeathScores[0], state.suddenDeathScores[1]];
        }
        if (state.suddenDeathRound !== undefined) {
          this.suddenDeathRound = state.suddenDeathRound;
        }

        // Update PPR from game state
        if (state.player1PPR !== undefined && this.player1) {
          this.player1.avgPPR = state.player1PPR;
        }
        if (state.player2PPR !== undefined && this.player2) {
          this.player2.avgPPR = state.player2PPR;
        }

        // Initialize roll histories from database (fixes observer scoring summary)
        if (this.player1) {
          this.player1.current_game_rolls = Array.isArray(game.p1_roll_history)
            ? [...game.p1_roll_history]
            : [];
          console.log('[Observer Mode] Loaded P1 roll history:', this.player1.current_game_rolls);
        }
        if (this.player2) {
          this.player2.current_game_rolls = Array.isArray(game.p2_roll_history)
            ? [...game.p2_roll_history]
            : [];
          console.log('[Observer Mode] Loaded P2 roll history:', this.player2.current_game_rolls);
        }
        console.log('[Observer Mode] matchId:', matchId);

      } else {
        console.warn('[Observer Mode] No game state found in database, using defaults');
      }
    } catch (e) {
      console.error('[Observer Mode] Failed to fetch game state:', e);
    }

    // Join match channel as observer
    try {
      this.matchControls = await this.lobbyManager.joinMatchAsObserver(matchId);
      console.log('[Observer Mode] Joined match channel as observer');
    } catch (e) {
      console.error('[Observer Mode] Failed to join match channel:', e);
      throw new Error('Failed to join match as observer');
    }

    // Sync observer flag to renderer
    this.renderer.isObserver = true;
    this.renderer.showObserverBadge();

    // Pass tournament-specific W-L records to renderer for round-robin tournaments
    // IMPORTANT: Must be set BEFORE updatePlayerCards() so W-L displays correctly
    if (this.currentTournamentType === 'round_robin') {
      this.renderer.setTournamentPlayerRecords(this.tournamentPlayerRecords);
    }

    // Update renderer with player info and current game state
    this.renderer.updatePlayerCards(player1, player2);

    this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, player1, player2, this.players, this.phase, this.bustHistory);
    this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
    this.renderer.round = this.round;

    // Set turn indicator based on current player from database
    const currentPlayerName = this.currentPlayer === 0 ? player1.userName : player2.userName;
    this.renderer.setTurnIndicator(true, currentPlayerName, false); // Never show "YOUR TURN" for observers

    // Set tournament mode if applicable
    if (this.currentTournamentContext) {
      this.renderer.setTournamentMode(true);
    }

    // Show game board (rendering loop starts via update handler)
    this.renderer.showBoard();

    console.log('[Observer Mode] Observer mode initialized successfully');
    console.log('[Observer Mode] Waiting for game state updates from players...');
  }

  exitObserverMode() {
    if (!this.isObserver) return;

    console.log('[Observer Mode] Exiting observer mode');

    // Clean up observer state
    this.isObserver = false;
    this.observers = [];

    // Leave match channel
    if (this.matchControls && this.observerMatchId) {
      this.lobbyManager.leaveMatchAsObserver(this.observerMatchId);
      this.matchControls = null;
    }
    this.observerMatchId = null;

    // Reset renderer observer flag and hide badge
    this.renderer.isObserver = false;
    this.renderer.hideObserverBadge();

    // Reset multiplayer state
    this.isMultiplayer = false;
    this.players = 1;

    // Reset lobby status to 'waiting' (important for tournament exits)
    this.lobbyManager.updateMode('waiting');

    // Return to tournament bracket
    if (this.currentTournamentContext?.tournamentId) {
      const tid = this.currentTournamentContext.tournamentId;
      this.returnToTournamentBracket(tid);
    } else {
      // Fallback to lobby if no tournament context
      this.returnToLobby();
    }

    console.log('[Observer Mode] Exited observer mode');
  }

  private returnToTournamentBracket(tournamentId: string) {
    console.log('[Observer Mode] Returning to tournament bracket:', tournamentId);

    // Recreate the tournament lobby (same logic as goToTournamentLobby handler)
    this.suppressLobbyAutoShow = true;
    (window as any).suppressLobbyAutoShow = true;

    try { this.renderer.hideOverlay(); } catch {}
    try { this.renderer.hideLobby(); } catch {}

    const mainLobby = document.getElementById('skeech-lobby');
    if (mainLobby) mainLobby.style.display = 'none';

    const boardRoot = document.getElementById('skeech-root');
    if (boardRoot) boardRoot.style.display = 'none';

    // Clean up any lingering tournament DOM before creating a new controller
    Array.from(document.querySelectorAll('#tourney-lobby')).forEach((el) => {
      try { el.parentElement?.removeChild(el); } catch {}
    });

    if ((this as any).tournamentLobby) {
      try { (this as any).tournamentLobby.destroy(); } catch {}
      (this as any).tournamentLobby = undefined;
    }

    // Navigate to tournament lobby using the same pattern as goToTournamentLobby
    // This will recreate the tournament controller
    const tournamentLobbyController = this.createTournamentLobbyController();
    (this as any).tournamentLobby = tournamentLobbyController;
    (this as any).lastTournamentId = tournamentId;
    tournamentLobbyController.open(tournamentId, this.currentUser.id);
  }

  private createTournamentLobbyController(): any {
    return new TournamentLobbyController({
      getTournamentById: async (id: string) => await (this.repo as any).sb.getTournamentById(id),
      listTournamentPlayers: async (id: string) => await (this.repo as any).sb.listTournamentPlayers(id),
      getUserById: async (id: string) => await (this.repo as any).sb.getUserById(id),
      leaveTournament: async (tid: string, uid: string) => await (this.repo as any).sb.leaveTournament(tid, uid),
      startTournament: async (tid: string) => await (this.repo as any).sb.startTournamentWithBracket(tid),
      applyBracketAutoAdvances: async (tid: string) => await (this.repo as any).sb.applyBracketAutoAdvances(tid),
      getGameById: async (gid: string) => await (this.repo as any).sb.getGameById(gid),
      recordTournamentMatchResult: async (args: any) => await (this.repo as any).sb.recordTournamentMatchResult(args),
      calculateUserPPRForTournament: async (tid: string, uid: string) => await (this.repo as any).sb.calculateUserPPRForTournament(tid, uid),
      calculateStandingsFromRollHistories: async (tid: string, matches: any) => await (this.repo as any).sb.calculateStandingsFromRollHistories(tid, matches),
      upsertTournamentPlayerStats: async (tid: string, uid: string, delta: any) => await (this.repo as any).sb.upsertTournamentPlayerStats(tid, uid, delta),
      resumeGame: async (gid: string) => {
        try {
          const tl = (this as any).tournamentLobby;
          if (tl && typeof tl.destroy === 'function') {
            try { tl.destroy(); } catch {}
            (this as any).tournamentLobby = undefined;
          }
          await this.loadGame(gid);
        } catch (e) {
          console.error('Failed to resume game', e);
        }
      },
      findLatestGameBetweenUsers: async (a: string, b: string) => await (this.repo as any).sb.findLatestGameBetweenUsers(a, b),
      findTournamentGame: async (a: string, b: string, tid: string, round: number, matchIdx: number) => await (this.repo as any).sb.findTournamentGame(a, b, tid, round, matchIdx),
      getTournamentMatchGame: async (tid: string, round: number, matchIdx: number) => await (this.repo as any).sb.getTournamentMatchGame(tid, round, matchIdx),
      initializeAsObserver: async (matchId: string, player1: any, player2: any, opts?: any) => {
        await this.initializeAsObserver(matchId, player1, player2, opts);
      },
      showStartMatchModal: async ({ meId, opponentId, title, matchId, tournamentId, round, matchIndex, startingPlayerId }: any) => {
        try {
          const me = await (this.repo as any).sb.getUserById(meId);
          const opp = await (this.repo as any).sb.getUserById(opponentId);

          const myPPR = tournamentId
            ? await (this.repo as any).sb.calculateUserPPRForTournament(tournamentId, meId)
            : await this.repo.calculateUserPPR(meId);
          const oppPPR = tournamentId
            ? await (this.repo as any).sb.calculateUserPPRForTournament(tournamentId, opponentId)
            : await this.repo.calculateUserPPR(opponentId);

          const currentMeta = { userName: me?.user_name || 'You', wins: me?.wins || 0, losses: me?.losses || 0, avgPPR: myPPR };
          const oppMeta = { userName: opp?.user_name || 'Opponent', wins: opp?.wins || 0, losses: opp?.losses || 0, avgPPR: oppPPR };
          const opponentPresence = { userId: opponentId, userName: oppMeta.userName, wins: oppMeta.wins, losses: oppMeta.losses, avgPPR: oppMeta.avgPPR } as any;
          (this as any).pendingTournamentContext = { tournamentId, round, matchIndex };

          if (tournamentId) {
            this.currentUser.avgPPR = myPPR;
          }

          await this.lobbyManager.registerTournamentClick(opponentPresence, { matchId: matchId as string, titleText: title, startingPlayerId, isTournament: !!tournamentId, tournamentId });
        } catch (e) {
          console.error('Failed to show start match modal', e);
        }
      },
      showUserProfile: (userId: string, userName: string) => this.renderer.profileManager.showUserProfile(userId, userName)
    });
  }

  async returnToLobby() {
    // Ensure game loop and animations are stopped to avoid background updates
    if (this.tutorialManager.isActive) {
      this.tutorialManager.setActive(false);
    }
    try { this.renderer.stopLoop(); } catch {}
    try { this.renderer.dispose(); } catch {}
    try { this.physics.stop(); } catch {}
    if (this.pathBatchTimer) { clearTimeout(this.pathBatchTimer); this.pathBatchTimer = null as any; }    
    this.renderer.hideRetributionOverlay();
    this.renderer.hideSuddenDeathOverlay();

    

    // Refresh user stats from DB (including PPR calculated from games)
    const freshUser = await this.repo.getUserById(this.currentUser.id);
    if (freshUser) {
      // Calculate PPR from completed games
      freshUser.avgPPR = await this.repo.calculateUserPPR(this.currentUser.id);
      this.currentUser = freshUser;
      this.lobbyManager.userJoin(this.currentUser);
      this.lobbyManager.currentUser = freshUser; // Update lobby manager's reference
    }
    

    // Only treat as tournament context if the CURRENT game is a tournament game
    const existingTourney = (this as any).tournamentLobby;
    const lastTid = (this as any).lastTournamentId;
    const isTournamentContext = !!(this.currentTournamentContext?.tournamentId);

    console.log('[returnToLobby] Tournament context:', this.currentTournamentContext);
    console.log('[returnToLobby] Will return to tournament?', isTournamentContext);

    if (this.isMultiplayer) {
      if (this.matchControls) {
        await this.matchControls.close();
        this.matchControls = null;
      }
      // Always reset lobby status when leaving match, even for tournaments
      // (UI suppression is handled separately via suppressLobbyAutoShow)
      await this.lobbyManager.updateMode('waiting');
      this.isMultiplayer = false;
      this.players = 1;
    }

    // Clean up AI player if returning from AI Challenge
    if (this.aiPlayer) {
      this.aiPlayer = null;
      this.players = 1;
      await this.lobbyManager.updateMode('waiting');
    }
    // If this was a tournament game, return to that tournament lobby; otherwise go to main lobby
    if (isTournamentContext) {
      // Temporarily suppress presence-driven lobby auto-show until tourney view mounts
      this.suppressLobbyAutoShow = true;
      (window as any).suppressLobbyAutoShow = true;
      const tid = this.currentTournamentContext?.tournamentId;
      // Reset tournament mode in renderer so menus update correctly in lobbies
      this.renderer.setTournamentMode(false);
      // Hide game board and main lobby immediately to prevent visual flash
      const gameBoard = document.getElementById('skeech-root');
      if (gameBoard) gameBoard.style.display = 'none';
      const mainLobby = document.getElementById('skeech-lobby');
      if (mainLobby) mainLobby.style.display = 'none';
      // Open tournament lobby
      if ((this as any).tournamentLobby) {
        try { (this as any).tournamentLobby.destroy(); } catch {}
        (this as any).tournamentLobby = undefined;
      }
      (this as any).tournamentLobby = new TournamentLobbyController({
        getTournamentById: async (id: string) => await (this.repo as any).sb.getTournamentById(id),
        listTournamentPlayers: async (id: string) => await (this.repo as any).sb.listTournamentPlayers(id),
        getUserById: async (id: string) => await (this.repo as any).sb.getUserById(id),
        leaveTournament: async (tournamentId: string, userId: string) => await (this.repo as any).sb.leaveTournament(tournamentId, userId),
        startTournament: async (id: string) => await (this.repo as any).sb.startTournamentWithBracket(id),
        startRoundRobinTournament: async (id: string) => await (this.repo as any).sb.startRoundRobinTournament(id),
        applyBracketAutoAdvances: async (tid: string) => await (this.repo as any).sb.applyBracketAutoAdvances(tid),
        getGameById: async (gid: string) => await (this.repo as any).sb.getGameById(gid),
        recordTournamentMatchResult: async (args: { tournamentId: string; round: number; matchIndex: number; aScore: number; bScore: number; winnerUserId: string }) => await (this.repo as any).sb.recordTournamentMatchResult(args),
        calculateUserPPRForTournament: async (tid: string, uid: string) => await (this.repo as any).sb.calculateUserPPRForTournament(tid, uid),
        calculateStandingsFromRollHistories: async (tid: string, matches: any) => await (this.repo as any).sb.calculateStandingsFromRollHistories(tid, matches),
        upsertTournamentPlayerStats: async (tid: string, uid: string, delta: any) => await (this.repo as any).sb.upsertTournamentPlayerStats(tid, uid, delta),
        findLatestGameBetweenUsers: async (a: string, b: string) => await (this.repo as any).sb.findLatestGameBetweenUsers(a, b),
        findTournamentGame: async (a: string, b: string, tid: string, round: number, matchIdx: number) => await (this.repo as any).sb.findTournamentGame(a, b, tid, round, matchIdx),
        getTournamentMatchGame: async (tid: string, round: number, matchIdx: number) => await (this.repo as any).sb.getTournamentMatchGame(tid, round, matchIdx),
        initializeAsObserver: async (matchId: string, player1: any, player2: any, opts?: any) => {
          await this.initializeAsObserver(matchId, player1, player2, opts);
        },
        resumeGame: async (gid: string) => { try { await this.loadGame(gid); } catch (e) { console.error('Failed to resume game', e); } },
        showStartMatchModal: async ({ meId, opponentId, title, matchId, tournamentId, round, matchIndex, startingPlayerId }) => {
          try {
            const me = await (this.repo as any).sb.getUserById(meId);
            const opp = await (this.repo as any).sb.getUserById(opponentId);

            // For tournament games, use tournament-specific PPR
            const myPPR = tournamentId
              ? await (this.repo as any).sb.calculateUserPPRForTournament(tournamentId, meId)
              : me?.avg_ppr || 0;
            const oppPPR = tournamentId
              ? await (this.repo as any).sb.calculateUserPPRForTournament(tournamentId, opponentId)
              : opp?.avg_ppr || 0;

            const oppMetaName = opp?.user_name || 'Opponent';
            const opponentPresence = { userId: opponentId, userName: oppMetaName, wins: opp?.wins || 0, losses: opp?.losses || 0, avgPPR: oppPPR } as any;
            (this as any).pendingTournamentContext = { tournamentId, round, matchIndex };

            // Update current user with tournament PPR if applicable
            if (tournamentId) {
              this.currentUser.avgPPR = myPPR;
            }

            await this.lobbyManager.registerTournamentClick(opponentPresence, { matchId: matchId as string, titleText: title, startingPlayerId, isTournament: !!tournamentId, tournamentId });
          } catch (e) {
            console.error('Failed to show start match modal', e);
          }
        },
        showUserProfile: (userId: string, userName: string) => this.renderer.profileManager.showUserProfile(userId, userName)
      });
      if (tid) {
        (this as any).tournamentLobby.open(tid, this.currentUser.id);
      } else {
        // Fallback to main lobby if no tournament id available
        this.renderer.showLobby(this.lobbyManager.getList());
        if (this.lobbyController) {
          await this.lobbyController.enterLobby(this.currentUser);
        }
      }
    } else {
      this.renderer.showLobby(this.lobbyManager.getList());
      if (this.lobbyController) {
        await this.lobbyController.enterLobby(this.currentUser);
      }
    }

    // Clear tournament context when returning via menu
    this.currentTournamentContext = null;
  }

  async requestQuit() {
    if (this.isMultiplayer) {
      this.renderer.showQuitGameConfirmation(
        async () => {
          const opponent = this.currentUser.id === this.player1?.id ? this.player2 : this.player1;
          if (opponent && this.matchControls?.matchId) {
            await this.lobbyManager.sendPlayerForfeit(this.matchControls.matchId, { userId: opponent.id, userName: opponent.userName });
            this.matchControls.sendMatchMessage({
              kind: 'player_forfeit_match',
              userId: this.currentUser.id,
              userName: this.currentUser.userName
            });
            await this.repo.forfeitGame(this.matchControls.matchId, this.currentUser.id);
          }
          // Return to lobby
          await this.returnToLobby();
        }
      );
    } else { // For solo games, quit immediately without confirmation
      await this.returnToLobby();
    }
  }

  resetBall() {
    // CRITICAL: Stop any active sink animation that would override ball size/position
    if (this.physics.sink.active) {
      this.physics.sink.active = false;
    }
    // Clear bypass tracking for new roll
    this.physics.bypassTracking.clear();
    this.physics.lastBallPos = null;    

    // RESTORE LOGICAL WORLD for multiplayer determinism
    const { play:WP } = this.world.worldRects();
    // Ball size as fraction of world play width for consistency
    this.ball.r = this.board.ballRadiusFrac * WP.w;
    // Position in world coordinates
    const launchMarginW = Math.max(this.ball.r * 1.25, 0.03 * WP.h);
    this.ball.pos = { x: WP.x + WP.w/2, y: WP.y + WP.h - launchMarginW };
    this.ball.vel = { x: 0, y: 0 };
    this.ball.spin = 0;
    this.ball.rolling = false;

    if (this.ballsLeft > 0) {
      // Ensure the indicator reserves one light for the launch ball
      this.renderer.setReserveAtLaunch(true);
      // Convert world coordinates to screen coordinates for the animation target
      const { play: playPx } = this.board.rects(this.renderer.canvas, this.renderer.dpr);
      const screenPos = this.world.worldToScreen(this.ball.pos, playPx, WP);
      this.renderer.startBallRollingAnimation(screenPos);
    }
  }

  prefix() { return `${this.currentUser ? this.currentUser.userName + ': ' : ''}`; }

  private triggerTurnStartMessage() {
    // Skip board messages during tutorial
    if (this.tutorialManager.isActive) return;

    if (this.currentUser) {
      const gameState = {
        consecutiveDitches: 0, // TODO: Track actual consecutive ditches
        isTurnStart: true
      };
      const messageData = messageManager.getMessageWithPriority(this.currentUser, gameState);
      if (messageData) {
        this.renderer.showPositionedMessage(messageData.text, messageData.message);

        // Send board message to passive player in multiplayer
        if (this.isMultiplayer && this.isMyTurn && this.matchControls) {
          const timestamp = this.isRecordingPath
            ? (performance.now() - this.pathRecordingStartTime) / 1000
            : 0;
          this.matchControls.sendMatchMessage({
            kind: 'board_message',
            round: this.round,
            ballIndex: this.ballsPerTurn - this.ballsLeft,
            text: messageData.text,
            messageData: messageData.message,
            timestamp
          } as BoardMessage);
        }
      }
    }
  }

  private triggerVelocityMessage(velocityY: number) {
    // Skip board messages during tutorial
    if (this.tutorialManager.isActive) return;

    if (this.currentUser) {
      const gameState = {
        consecutiveDitches: 0, // TODO: Track actual consecutive ditches
        lastVelocityY: velocityY
      };
      const messageData = messageManager.getMessageWithPriority(this.currentUser, gameState);
      if (messageData) {
        this.renderer.showPositionedMessage(messageData.text, messageData.message);

        // Send board message to passive player in multiplayer
        if (this.isMultiplayer && this.isMyTurn && this.matchControls) {
          const timestamp = this.isRecordingPath
            ? (performance.now() - this.pathRecordingStartTime) / 1000
            : 0;
          this.matchControls.sendMatchMessage({
            kind: 'board_message',
            round: this.round,
            ballIndex: this.ballsPerTurn - this.ballsLeft,
            text: messageData.text,
            messageData: messageData.message,
            timestamp
          } as BoardMessage);
        }
      }
    }
  }

  private triggerScoreMessage(score: number) {
    // Skip board messages during tutorial
    if (this.tutorialManager.isActive) return;

    if (this.currentUser) {
      const gameState = {
        consecutiveDitches: 0, // TODO: Track actual consecutive ditches
        lastScore: score
      };
      const messageData = messageManager.getMessageWithPriority(this.currentUser, gameState);
      if (messageData) {
        this.renderer.showPositionedMessage(messageData.text, messageData.message);

        // Send board message to passive player in multiplayer
        if (this.isMultiplayer && this.isMyTurn && this.matchControls) {
          const timestamp = this.isRecordingPath
            ? (performance.now() - this.pathRecordingStartTime) / 1000
            : 0;
          this.matchControls.sendMatchMessage({
            kind: 'board_message',
            round: this.round,
            ballIndex: this.ballsPerTurn - this.ballsLeft,
            text: messageData.text,
            messageData: messageData.message,
            timestamp
          } as BoardMessage);
        }
      }
    }
  }

  private triggerRetributionFailedMessage() {
    // Skip board messages during tutorial
    if (this.tutorialManager.isActive) return;

    if (this.currentUser) {
      const gameState = {
        consecutiveDitches: 0,
        retributionFailed: true
      };
      const messageData = messageManager.getMessageWithPriority(this.currentUser, gameState);
      if (messageData) {
        this.renderer.showPositionedMessage(messageData.text, messageData.message);

        // Send board message to passive player in multiplayer
        if (this.isMultiplayer && this.isMyTurn && this.matchControls) {
          const timestamp = this.isRecordingPath
            ? (performance.now() - this.pathRecordingStartTime) / 1000
            : 0;
          this.matchControls.sendMatchMessage({
            kind: 'board_message',
            round: this.round,
            ballIndex: this.ballsPerTurn - this.ballsLeft,
            text: messageData.text,
            messageData: messageData.message,
            timestamp
          } as BoardMessage);
        }
      }
    }
  }

  private triggerRandomMessage() {
    // Skip board messages during tutorial
    if (this.tutorialManager.isActive) return;

    if (this.currentUser) {
      const gameState = {
        consecutiveDitches: 0 // Basic state for random messages
      };
      const messageData = messageManager.getMessageWithPriority(this.currentUser, gameState);
      if (messageData) {
        this.renderer.showPositionedMessage(messageData.text, messageData.message);

        // Send board message to passive player in multiplayer
        if (this.isMultiplayer && this.isMyTurn && this.matchControls) {
          const timestamp = this.isRecordingPath
            ? (performance.now() - this.pathRecordingStartTime) / 1000
            : 0;
          this.matchControls.sendMatchMessage({
            kind: 'board_message',
            round: this.round,
            ballIndex: this.ballsPerTurn - this.ballsLeft,
            text: messageData.text,
            messageData: messageData.message,
            timestamp
          } as BoardMessage);
        }
      }
    }
  }

  // ============================================================================
  // RENDERER HANDLER IMPLEMENTATIONS
  // ============================================================================

  private handleForgetUser(): void {
    forgetUserLocal();
    this.lobbyManager.leaveLobby();
  }

  private async handleUploadProfileImage(userId: string, file: File): Promise<string | null> {
    const filePath = `public/avatars/${userId}/${file.name}`;
    const { data, error } = await sb.storage
      .from('skrollrollr_profiles')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
       });

    if (error) {
      console.error('Error uploading file:', error);
      return null;
    }

    const { data: signedUrlData, error: signedUrlError } = await sb.storage
      .from('skrollrollr_profiles')
      .createSignedUrl(filePath, 60 * 60 * 24 * 365 * 10); // 10 year expiry

    if (signedUrlError) {
      console.error('Error creating signed URL:', signedUrlError);
      return null;
    }

    return signedUrlData.signedUrl;
  }

  private handleCurrentUserUpdated(userData: Partial<IUser>): void {
    // Update the main game's currentUser with the new data
    if (this.currentUser) {
      this.currentUser = { ...this.currentUser, ...userData };
      // Refresh presence to broadcast the update to other users
      this.lobbyManager?.refreshPresence();
    }
  }

  private handleUsernameChanged(newUsername: string): void {
    if (this.currentUser) rememberUserLocal(this.currentUser.id, newUsername);
    if (this.lobbyManager) this.lobbyManager.refreshPresence();
    if (this.isMultiplayer) {
      this.renderer.updatePlayerCards(this.player1, this.player2);
    } else {
      this.renderer.updatePlayerCards(this.currentUser);
    }
  }

  private handleRefreshLobby(): void {
    // Refresh presence to get updated stats for all users
    this.lobbyManager.refreshPresence();
    // Refresh active games list
    this.lobbyController?.refreshGames();
  }

  private handleLobbyInvite(target: PresenceMeta): void {
    const from = { userId: this.currentUser?.id, userName: this.currentUser?.userName };
    const to = { userId: target.userId, userName: target.userName };
    if (!from.userId || !from.userName) {
      console.error("Cannot send invite, current user is not defined.");
      return;
    }
    this.lobbyManager.sendInvite({
      kind:'invite',
      matchId: v4(),
      from:{ userId: this.currentUser?.id, userName: this.currentUser?.userName},
      to:{ userId: target.userId, userName: target.userName }
    });

    this.renderer.showOverlay(`Waiting for ${target.userName}...`, "❌", () => {
      this.lobbyManager.sendInvite({
        kind: 'invite_cancel',
        from: from,
        to: to
      });      
      this.renderer.hideOverlay();
      this.renderer.showLobby(this.lobbyManager.getList());
    });
  }

  // ============================================================================
  // LOBBY HANDLER IMPLEMENTATIONS
  // ============================================================================

  private handleOpponentRoll(rollInput: RollInput): void {
    // Receive world coordinates for path playback setup
    this.round = rollInput.round;
    this.physics.tiltX = rollInput.tiltX;

    // Set initial position but DON'T start the ball moving yet
    // We'll start when first path chunk arrives
    this.ball.pos = { x: rollInput.start.x, y: rollInput.start.y };
    this.ball.vel = { x: 0, y: 0 };
    this.ball.spin = 0;
    this.ball.rolling = false;  // Wait for path chunks

    // Reset path playback state for new roll
    this.receivedPath = [];
    this.isPlayingPath = false;

    // Ensure passive client uses opponent's ball color during playback
    try {
      const myIndex = this.isHost ? 1 : 0; // host is player 1, guest is player 0
      const oppIndex = myIndex === 0 ? 1 : 0;
      this.setBallColorForIndex(oppIndex);
    } catch {}

    // Stop perimeter light chaser when opponent starts rolling
    if (this.renderer.isPinballShowActive()) {
      this.renderer.stopPinballLightShow();
    }
  }

  private applyDeferredRollResult(rollResult: RollResult): void {
    console.log('[applyDeferredRollResult] Applying state updates for:', rollResult.result);

    // Guard: ignore late previous-turn results after phase flip into special phases
    // BUT: Allow timeout messages (phase transitions) through even if currentPlayer differs,
    // because sudden death resets currentPlayer to 0, so the announcement will have a different currentPlayer
    if ((this.phase === 'retribution' || this.phase === 'sudden_death') && rollResult.result !== 'timeout') {
      const rrCurrent = (rollResult.gameState && typeof rollResult.gameState.currentPlayer === 'number') ? rollResult.gameState.currentPlayer : null;
      if (rrCurrent !== null && rrCurrent !== this.currentPlayer) {
        console.log('[PHASE] applyDeferredRollResult: skipping late result after phase flip', {
          time: new Date().toISOString(),
          phase: this.phase,
          rrCurrent,
          currentPlayer: this.currentPlayer,
          rrBallIndex: (rollResult as any).ballIndex,
          rrResult: (rollResult as any).result
        });
        return;
      }
    }


    // Trigger score message for passive player if opponent scored
    if (rollResult.result === 'hole' && rollResult.pts && rollResult.pts > 0) {
      this.triggerScoreMessage(rollResult.pts);
    } else {
      // If no score, try showing a random message (with frequency check)
      this.triggerRandomMessage();
    }

    // Update scores
    if (rollResult.gameState.scores && Array.isArray(rollResult.gameState.scores) && rollResult.gameState.scores.length >= 2) {
      this.scores = [rollResult.gameState.scores[0], rollResult.gameState.scores[1]];
    }

    // (Legacy roll_result handling is now disabled; state updates via applyFinalGameState
    // already keep current_game_rolls in sync.)

    // Don't sync ballsLeft when entering retribution/sudden death or when it becomes my turn as those phases reset ballsLeft to full turn count
    const willBeMyTurn = (this.isHost && rollResult.gameState.currentPlayer === 1) ||
                        (!this.isHost && rollResult.gameState.currentPlayer === 0);

    const previousPhase = this.phase;
      if (rollResult.gameState.phase) {
        this.phase = rollResult.gameState.phase;
      }
      const isEnteringSpecialPhase = previousPhase !== this.phase && (this.phase === 'retribution' || this.phase === 'sudden_death');

      if (previousPhase !== 'retribution' && this.phase === 'retribution') {
        // REMOVED: Old guard system
    // this.dropOpponentResultsUntil = performance.now() + 1500;
        this.pendingTurnTransition = false;
        console.log('[PHASE] passive: received retribution phase from opponent', { time: new Date().toISOString(), previousPhase, newPhase: this.phase, scores: this.scores.slice(), currentPlayer: this.currentPlayer });
        if (this.pendingTurnTransitionTimer) { clearTimeout(this.pendingTurnTransitionTimer); this.pendingTurnTransitionTimer = null; }
        // Reset skeech state and clear board lighting when entering retribution (passive side included)
        this.isSkeechPossible = false;
        this.skeechSuccess = false;
        this.wildcardHit = null;
        this.turnHits = [];
        try { this.renderer.clearBoardLighting(); } catch {}
        this.renderer.showRetributionOverlay();
      } else if (previousPhase !== 'sudden_death' && this.phase === 'sudden_death') {
        this.renderer.hideRetributionOverlay();
        this.renderer.showSuddenDeathOverlay();
      }

      // Sync ballsLeft unless we're entering special phase or it's a turn transition
      if (!isEnteringSpecialPhase) {
        this.ballsLeft = rollResult.gameState.ballsLeft;
      }

      this.currentPlayer = rollResult.gameState.currentPlayer;
      this.round = rollResult.gameState.round;
      this.turnHits = [...rollResult.gameState.turnHits];
      this.renderer.round = this.round;
      this.isMyTurn = this.computeIsMyTurnForCurrentPlayer();
      this.renderer.setIsMyTurn(this.isMyTurn);
      // Ensure ball color matches the new turn owner on both clients
      this.setBallColorForCurrentPlayer();
      const passivePlayerName = this.currentPlayer === 0 ? this.player1?.userName || 'Player 1' : this.player2?.userName || 'Player 2';
      const isPassivePlayerCurrentUser = this.isMyTurn;
      this.renderer.setTurnIndicator(true, passivePlayerName, isPassivePlayerCurrentUser);

      // DEFERRED: Phase transitions handled in handlePathPlaybackComplete to avoid interrupting playback
      // Store phase transition flags for later
      this.pendingPhaseTransition = previousPhase !== this.phase;
      this.pendingSuddenDeathTransition = this.phase === 'sudden_death' && willBeMyTurn && rollResult.gameState.ballsLeft === this.ballsPerTurn;
      if (rollResult.gameState.suddenDeathScores) {
        this.suddenDeathScores = [rollResult.gameState.suddenDeathScores[0], rollResult.gameState.suddenDeathScores[1]];
      }
      if (rollResult.gameState.suddenDeathRound) {
        const wasNewSuddenDeathRound = rollResult.gameState.suddenDeathRound > this.suddenDeathRound;
        this.suddenDeathRound = rollResult.gameState.suddenDeathRound;

        // Show sudden death continuation message if this is a new round
        if (wasNewSuddenDeathRound && this.phase === 'sudden_death') {
          const activePlayerName = this.isMyTurn ? this.currentUser.userName : this.opponent?.userName;
          this.renderer.showSuddenDeathOnBoard(this.suddenDeathRound, activePlayerName ?? '', 4000);
        }
      }
      // Sync skeech-related state, but do NOT re-enable during special phase transitions
      if (!isEnteringSpecialPhase) {
        if (rollResult.gameState.isSkeechPossible !== undefined) {
          this.isSkeechPossible = rollResult.gameState.isSkeechPossible;
        }
        if (rollResult.gameState.skeechSuccess !== undefined) {
          this.skeechSuccess = rollResult.gameState.skeechSuccess;
        }
        if (rollResult.gameState.wildcardHit !== undefined) {
          this.wildcardHit = rollResult.gameState.wildcardHit;
        }
      } else {
        this.isSkeechPossible = false;
        this.skeechSuccess = false;
        this.wildcardHit = null;
      }
      const hasWinCondition = this.scores[0] >= this.target || this.scores[1] >= this.target;

      if (hasWinCondition) {
        // Determine phase transition based on current phase and multiplayer status
        if (this.phase === 'normal') {
          // Normal game, someone just won -> enter retribution
          this.phase = 'retribution';
           this.pendingTurnTransition = false;
           if (this.pendingTurnTransitionTimer) { clearTimeout(this.pendingTurnTransitionTimer); this.pendingTurnTransitionTimer = null; }
      try { this.renderer.clearBoardLighting(); } catch {}
          this.ballsLeft = this.ballsPerTurn;          
        } else if (this.phase === 'retribution') {          
          // This is handled by the active player when retribution starts          
        } else if (this.phase === 'sudden_death') {       
          // This is handled by the active player when sudden death starts          
        }
        // Note: For retribution and sudden_death phases, the active player determines the outcome and sends the final phase to passive player
      }      
      if (this.phase === 'over') {
        // Passive side: ensure we persist game completion and tournament stats for this user as well
        try { this.finishGameSave(); } catch {}
        let winner: IUser | null, loser: IUser | null;
        if (previousPhase === 'sudden_death') {
          winner = this.suddenDeathScores[0] > this.suddenDeathScores[1] ? this.player1 : this.player2;
          loser = winner === this.player1 ? this.player2 : this.player1;
        } else {
          winner = this.scores[0] >= this.target ? this.player1 : this.player2;
          loser = winner === this.player1 ? this.player2 : this.player1;
        }

        setTimeout(() => {
          const finalScores = previousPhase === 'sudden_death'
            ? [this.suddenDeathScores[0], this.suddenDeathScores[1]]
            : [this.scores[0], this.scores[1]];
          let winnerScore, loserScore;
          if (winner === this.player1) {
              winnerScore = finalScores[0];
              loserScore = finalScores[1];
          } else {
              winnerScore = finalScores[1];
              loserScore = finalScores[0];
          }
          this.renderer.endGame(winner!, loser || null, winnerScore, loserScore, this.currentUser);
        }, this.skeechSuccess ? 1000 : 0);
        return;
      }
      
      const receivedPhase = rollResult.gameState.phase;
      if (receivedPhase === 'sudden_death' && previousPhase !== 'sudden_death') {
        this.startSuddenDeath();
      } else if (receivedPhase === 'over' && this.phase === 'retribution') {
        // Passive side: also persist completion so both players' tournament stats are saved
        try { this.finishGameSave(); } catch {}
        const originalWinner = this.scores[0] === 21 ? 0 : 1;
        const winner = originalWinner === 0 ? this.player1 : this.player2;
        const loser = originalWinner === 0 ? this.player2 : this.player1;
        this.triggerRetributionFailedMessage();
        setTimeout(() => {
          const winnerScore = winner === this.player1 ? this.scores[0] : this.scores[1];
          const loserScore = loser === this.player1 ? this.scores[0] : this.scores[1];
          this.renderer.endGame(winner!, loser || null, winnerScore, loserScore, this.currentUser);
        }, 2000);
      } else if (receivedPhase === 'over' && this.phase === 'sudden_death') {
        // Passive side: also persist completion so both players' tournament stats are saved
        try { this.finishGameSave(); } catch {}
        const winnerPlayer = this.suddenDeathScores[0] > this.suddenDeathScores[1] ? 0 : 1;
        const loserPlayer = this.suddenDeathScores[0] < this.suddenDeathScores[1] ? 0 : 1;
        const winner = winnerPlayer === 0 ? this.player1 : this.player2;
        const loser = winnerPlayer === 0 ? this.player2 : this.player1;
        this.renderer.endGame(winner!, loser || null, this.suddenDeathScores[winnerPlayer], this.suddenDeathScores[loserPlayer], this.currentUser);
      }
    this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
    const displayScores = this.phase === 'sudden_death' ? this.suddenDeathScores : this.scores;
    this.renderer.updateHUD(this.currentPlayer, displayScores, this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
    const hostPlayerName = this.currentPlayer === 0 ? this.player1?.userName || 'Player 1' : this.player2?.userName || 'Player 2';
    const isHostPlayerCurrentUser = this.isMyTurn;
    this.renderer.setTurnIndicator(true, hostPlayerName, isHostPlayerCurrentUser);
    // Skip roll history since passive player is just syncing state, not rolling
    this.saveGameStateToDb();

    // Check if opponent's turn just ended and it's now my turn
    const isTurnEnd = rollResult.gameState.ballsLeft === this.ballsPerTurn && this.isMyTurn;
    const shouldStartMyTurn = isTurnEnd && this.phase !== 'retribution' && this.phase !== 'sudden_death';

    console.log('[applyDeferredRollResult] Turn transition check:', {
      'rollResult.gameState.ballsLeft': rollResult.gameState.ballsLeft,
      'this.ballsPerTurn': this.ballsPerTurn,
      'this.isMyTurn': this.isMyTurn,
      'isTurnEnd': isTurnEnd,
      'this.phase': this.phase,
      'shouldStartMyTurn': shouldStartMyTurn,
      'rollResult.gameState.currentPlayer': rollResult.gameState.currentPlayer,
      'this.currentPlayer': this.currentPlayer,
      'rollResult.result': rollResult.result
    });

    if (shouldStartMyTurn) {
      console.log('[applyDeferredRollResult] Turn transition - starting my turn');
      this.ballsLeft = this.ballsPerTurn; // Reset for new turn

      // CRITICAL: Set isMyTurn flag so renderer allows input
      this.isMyTurn = true;
      this.renderer.setIsMyTurn(true);

      // Set rack to full count and reserve one light for ball at launch
      this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
      this.renderer.setReserveAtLaunch(true);
      this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
      this.renderer.setTurnIndicator(true, this.currentUser.userName, true);

      // Update tilt direction for the new active player
      this.physics.setTiltDirection(this.currentPlayer);
      this.physics.jitterTilt();

      // Reset ball and set deadline (this player is now active, not watching playback)
      this.resetBall();      
    } else if (this.isMyTurn && rollResult.gameState.ballsLeft === this.ballsPerTurn) {
      // Even if shouldStartMyTurn is false (e.g., special phase), if it's my turn and balls are reset,
      // we still need to set up the UI properly
      console.log('[applyDeferredRollResult] Setting up turn (special phase or fallback)');

      // Ensure ballsLeft is synced
      if (this.ballsLeft === this.ballsPerTurn) {
        this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
        this.renderer.setReserveAtLaunch(true);

        // Update tilt direction for the new active player
        this.physics.setTiltDirection(this.currentPlayer);
        this.physics.jitterTilt();

        // Reset ball
        this.resetBall();
        
      }
    }

    // NOTE: Ball resets are handled by handlePathPlaybackComplete after sink animation completes (for passive player watching opponent)
  }

  private handleOpponentQuit(opponentName: string): void {
    this.lastForfeitTime = performance.now();
    console.log('Forfeit received at time:', this.lastForfeitTime, 'from:', opponentName);
    this.renderer.showOpponentQuit(opponentName);
  }

  private handleReadyForNext(ready: ReadyForNext): void {
    // Observers don't participate in turn coordination
    if (this.isObserver) {
      return;
    }
    if (ready.userId !== this.currentUser.id) {
      this.opponentReady = true;

      // If we have a pending turn transition (4th ball completed), execute it now
      if (this.pendingTurnTransition) {
        if (this.pendingTurnTransitionTimer) { clearTimeout(this.pendingTurnTransitionTimer); this.pendingTurnTransitionTimer = null; }
        this.pendingTurnTransition = false;
        // Always call handleTurnTransition - it will properly handle retribution/sudden death endings
        this.handleTurnTransition();
        // Notify opponent of the turn transition by sending updated game state
        // Use a special "state-only" path chunk with no ball snapshots
        if (this.matchControls) {
          const gameState = this.getCurrentGameState();
          gameState.lastRollPts = undefined;

          // Send turn transition state via special path chunk
          const turnTransitionChunk: PathChunk = {
            kind: 'path_chunk',
            round: this.round,
            ballIndex: 0, // New turn starting
            snapshots: [], // No ball movement for turn transition
            isComplete: true,
            finalGameState: gameState
          };
          this.matchControls.sendPathChunk(turnTransitionChunk);
        }
      }
      if (this.waitingForOpponentReady) {
        this.waitingForOpponentReady = false;        
      }
    }
  }

  private startPathBatching(): void {
    // Schedule first batch after INITIAL_DELAY
    this.nextBatchDelay = PATH_BATCH_CONFIG.INITIAL_DELAY;
    this.lastPathBatchIndex = 0;
    this.scheduleNextPathBatch();
  }

  private scheduleNextPathBatch(): void {
    if (this.pathBatchTimer !== null) {
      clearTimeout(this.pathBatchTimer);
    }

    this.pathBatchTimer = window.setTimeout(() => {
      this.sendPathBatch();
    }, this.nextBatchDelay * 1000);
  }

  private sendPathBatch(): void {
    if (!this.isMultiplayer || !this.matchControls) return;

    const currentIndex = this.recordedPath.length;
    const snapshots = this.recordedPath.slice(this.lastPathBatchIndex, currentIndex);
    const isComplete = !this.isRecordingPath;

    // Don't return early if this is the final batch - we need to send the game state even if no new snapshots
    if (snapshots.length === 0 && !isComplete) return;

    // Always capture game state to sync PPR and other values between rolls
    // For intermediate rolls, this ensures PPR stays in sync during the turn
    // For final rolls, this is the authoritative state sent to the opponent
    const gameState = this.getCurrentGameState();   
    
    const chunk: PathChunk = {
      kind: 'path_chunk',
      round: this.round,
      ballIndex: this.ballsPerTurn - this.ballsLeft,
      snapshots,
      isComplete,
      // Include game state in ALL path chunks to sync PPR and scores in real-time
      // Name it finalGameState for consistency with handler, but it's sent in every chunk now
      finalGameState: gameState
    };

    this.matchControls.sendPathChunk(chunk);
    this.lastPathBatchIndex = currentIndex;

    // Schedule next batch if still recording
    if (this.isRecordingPath) {
      this.nextBatchDelay = Math.max(
        PATH_BATCH_CONFIG.MIN_BUFFER,
        this.nextBatchDelay - PATH_BATCH_CONFIG.INTERVAL_SHRINK
      );
      this.scheduleNextPathBatch();
    } else {
      // Recording stopped - clear timer
      this.pathBatchTimer = null;
    }
  }

  private handlePathPlaybackComplete(rollResult: RollResult | null): void {
    // If rollResult is null (cleared by timeout message), skip processing
    if (!rollResult) {      
      return;
    }

    // Start sink animation ONLY for holes and ditches (not 'stop')
    if (rollResult.result === 'hole' && 'cx' in rollResult && 'cy' in rollResult && 'pts' in rollResult) {
      // Type assertion after runtime check
      const cx = rollResult.cx as number;
      const cy = rollResult.cy as number;
      const pts = rollResult.pts as number;

      // Show score floater
      const { play: playPx } = this.board.rects(this.renderer.canvas, this.renderer.dpr);
      const { play: playW } = this.world.worldRects();
      const screenPos = this.world.worldToScreen({ x: cx, y: cy }, playPx, playW);
      this.renderer.notifyScored(screenPos.x, screenPos.y, pts);

      // Move ball to hole center immediately to avoid snap/delay
      this.ball.pos.x = cx;
      this.ball.pos.y = cy;

      // Start sink animation
      this.physics.sink = {
        active: true,
        cx,
        cy,
        r: this.ball.r,
        t: 0,
        dur: 0.35,
        pts
      };
    } else if (rollResult.result === 'ditch') {
      // Start ditch sink animation
      this.physics.sink = {
        active: true,
        cx: this.ball.pos.x,
        cy: this.ball.pos.y,
        r: this.ball.r,
        t: 0,
        dur: 0.28,
        pts: 0
      };
    }
    // For 'stop' result, don't start any sink animation - just reset ball immediately

    // Apply ALL deferred state updates NOW (after path playback completes)
    this.applyDeferredRollResult(rollResult);

    // Wait for sink animation to complete, then handle transitions and reset
    const sinkDuration = rollResult.result === 'ditch' ? 280 : (rollResult.result === 'hole' ? 350 : 0);
    setTimeout(() => {
      // Handle phase transitions NOW (after playback and sink completed)
      if (this.pendingPhaseTransition && this.phase === 'retribution') {
        this.ballsLeft = this.ballsPerTurn;
        // Initialize balls-left indicator before animating
        this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
        this.resetBall();

        if (this.isMyTurn) {
          const retributionPlayerName = this.currentUser.userName;
          this.renderer.showRetributionOnBoard(retributionPlayerName, 4000);
          this.renderer.setTurnIndicator(true, this.currentUser.userName, true);          
        }

        this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
        this.pendingPhaseTransition = false;
      } else if (this.pendingSuddenDeathTransition) {
        this.ballsLeft = this.ballsPerTurn;
        // Initialize balls-left indicator before animating
        this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
        this.resetBall();
        this.resetTurnState();
        this.pendingSuddenDeathTransition = false;
      } else {
        // Normal ball reset
        if (this.pendingBallReset) {
          this.pendingBallReset = false;
          this.resetBall();
        } else {
          this.resetBall();
        }
      }

      // Send ready signal to active player AFTER everything is done (observers never send signals)
      if (!this.isObserver && this.matchControls) {
        console.log('[handlePathPlaybackComplete] Sending ReadyForNext - ballsLeft:', this.ballsLeft, 'round:', this.round);
        this.matchControls.sendReadyForNext({
          kind: 'ready_for_next',
          round: this.round,
          ballIndex: 4 - this.ballsLeft,
          userId: this.currentUser.id
        });
      }
    }, sinkDuration);
  }

  private handlePathChunk(chunk: PathChunk): void {
    // Observers should always process path chunks (they're always passive viewers)
    // Regular players only process when it's not their turn
    const shouldProcessPath = this.isMultiplayer && (this.isObserver || !this.isMyTurn);

    if (shouldProcessPath) {
      // Cancel any pending wait timer once path arrives
      if (this.playbackWaitTimer !== null) { clearTimeout(this.playbackWaitTimer as any); this.playbackWaitTimer = null; }

      const logPrefix = this.isObserver ? '[OBSERVER]' : '[PASSIVE PLAYER]';
      
      // Handle special case: empty snapshots = turn transition (no ball movement)
      if (chunk.snapshots.length === 0 && chunk.isComplete && chunk.finalGameState) {
        const mergedState = this.mergeFinalGameState(this.pendingFinalState, chunk.finalGameState);

        // If path playback is still active, we need to wait for it to complete first
        if (this.isPlayingPath && this.receivedPath.length > 0) {
          this.pendingFinalState = mergedState;
          return;
        }
        // Clear any stale pending state before applying new state
        this.pendingFinalState = null;
        // Apply immediately - no path playback needed
        this.applyFinalGameState(mergedState);
        return;
      }

      // Append received snapshots to our path
      this.receivedPath.push(...chunk.snapshots);

      // Store final game state if this is the last chunk
      if (chunk.isComplete && chunk.finalGameState) {
        this.pendingFinalState = this.mergeFinalGameState(this.pendingFinalState, chunk.finalGameState);
      }

      // Start playback on first chunk
      if (!this.isPlayingPath && this.receivedPath.length > 0) {
        this.isPlayingPath = true;
        this.ball.rolling = true;
        this.pathPlaybackStartTime = performance.now();
      }
    }
  }

  private handleBoardMessage(message: BoardMessage): void {
    // Passive player receives board messages from active player
    if (this.isMultiplayer && !this.isMyTurn) {      

      // Add to queue for timed display during path playback
      this.pendingBoardMessages.push(message);

      // Sort by timestamp to ensure correct display order
      this.pendingBoardMessages.sort((a, b) => a.timestamp - b.timestamp);
    }
  }

  private updatePathPlayback(): void {
    if (!this.isPlayingPath || this.receivedPath.length === 0) return;

    const now = performance.now();
    const elapsedSec = (now - this.pathPlaybackStartTime) / 1000;

    // Display board messages at their scheduled timestamps
    for (let i = this.pendingBoardMessages.length - 1; i >= 0; i--) {
      const msg = this.pendingBoardMessages[i];
      if (elapsedSec >= msg.timestamp && !this.displayedMessageTimestamps.has(msg.timestamp)) {
        this.renderer.showPositionedMessage(msg.text, msg.messageData);
        this.displayedMessageTimestamps.add(msg.timestamp);
        this.pendingBoardMessages.splice(i, 1);
      }
    }

    // Check if we've reached or passed the final snapshot
    const lastSnapshot = this.receivedPath[this.receivedPath.length - 1];
    if (elapsedSec >= lastSnapshot.t) {
      // We've reached the end of the path - trigger completion immediately
      this.ball.pos.x = lastSnapshot.x;
      this.ball.pos.y = lastSnapshot.y;
      this.ball.vel.x = lastSnapshot.vx;
      this.ball.vel.y = lastSnapshot.vy;
      this.ball.spin = lastSnapshot.spin;
      if (lastSnapshot.r !== undefined) {
        this.ball.r = lastSnapshot.r;
      }
      this.ball.rolling = false;
      this.isPlayingPath = false;
      this.receivedPath = [];

      // Clean up board message queues
      this.pendingBoardMessages = [];
      this.displayedMessageTimestamps.clear();

      // Apply final game state if available (new path-based sync)
      if (this.pendingFinalState) {
        const fs = this.pendingFinalState;
        this.pendingFinalState = null;

        // Show sink animation for holes (scoring or moat), score floater only for scoring balls
        let delayMs = 0;
        const isHole = fs.lastRollResult === 'hole' || fs.lastRollResult === 'ditch';

        if (isHole) {
          // Show score floater only for scoring balls
          if (fs.lastRollPts !== undefined && fs.lastRollPts > 0) {
            try {
              const { play: playPx } = this.board.rects(this.renderer.canvas, this.renderer.dpr);
              const { play: playW } = this.world.worldRects();
              const screenPos = this.world.worldToScreen({ x: lastSnapshot.x, y: lastSnapshot.y }, playPx, playW);
              this.renderer.notifyScored(screenPos.x, screenPos.y, fs.lastRollPts);
            } catch {}
          }

          // Start sink animation for all holes (scoring or moat)
          this.physics.sink = {
            active: true,
            cx: lastSnapshot.x,
            cy: lastSnapshot.y,
            r: this.ball.r,
            t: 0,
            dur: 0.35,
            pts: fs.lastRollPts || 0
          };
          delayMs = 350;
        }

        // Apply game state immediately to update UI (scores, ball count, etc.)
        // but pass flag to delay ball reset if sink animation is playing
        this.applyFinalGameState(fs, delayMs);
      }
      // Fall back to old roll result handling for backward compatibility
      else if (this.pendingRollResult) {
        this.handlePathPlaybackComplete(this.pendingRollResult);
        this.pendingRollResult = null;
      }
      return;
    }

    // Find the two snapshots to interpolate between
    let prevSnapshot: BallSnapshot | null = null;
    let nextSnapshot: BallSnapshot | null = null;

    for (let i = 0; i < this.receivedPath.length; i++) {
      if (this.receivedPath[i].t <= elapsedSec) {
        prevSnapshot = this.receivedPath[i];
      }
      if (this.receivedPath[i].t > elapsedSec) {
        nextSnapshot = this.receivedPath[i];
        break;
      }
    }

    if (!prevSnapshot && nextSnapshot) {
      // Before first snapshot - use first snapshot
      this.ball.pos.x = nextSnapshot.x;
      this.ball.pos.y = nextSnapshot.y;
      this.ball.vel.x = nextSnapshot.vx;
      this.ball.vel.y = nextSnapshot.vy;
      this.ball.spin = nextSnapshot.spin;
      if (nextSnapshot.r !== undefined) {
        this.ball.r = nextSnapshot.r;
      }
    } else if (prevSnapshot && nextSnapshot) {
      // Interpolate between two snapshots
      const t = (elapsedSec - prevSnapshot.t) / (nextSnapshot.t - prevSnapshot.t);
      const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

      this.ball.pos.x = lerp(prevSnapshot.x, nextSnapshot.x, t);
      this.ball.pos.y = lerp(prevSnapshot.y, nextSnapshot.y, t);
      this.ball.vel.x = lerp(prevSnapshot.vx, nextSnapshot.vx, t);
      this.ball.vel.y = lerp(prevSnapshot.vy, nextSnapshot.vy, t);
      this.ball.spin = lerp(prevSnapshot.spin, nextSnapshot.spin, t);

      // Interpolate ball radius for bounce animation
      if (prevSnapshot.r !== undefined && nextSnapshot.r !== undefined) {
        this.ball.r = lerp(prevSnapshot.r, nextSnapshot.r, t);        
      }
    }
  }

  private applyFinalGameState(state: GameStateSync, delayBallResetMs: number = 0): void {
    const beforeBallsLeft = this.ballsLeft;
    const previousPhase = this.phase;

    // Update all game state immediately
    this.scores = [state.scores[0], state.scores[1]];
    this.ballsLeft = state.ballsLeft;
    this.currentPlayer = state.currentPlayer;
    this.round = state.round;
    this.turnHits = [...state.turnHits];
    this.isSkeechPossible = state.isSkeechPossible || false;
    this.skeechSuccess = state.skeechSuccess || false;
    this.wildcardHit = state.wildcardHit || null;
    this.phase = state.phase || 'normal';

    if (state.suddenDeathScores) {
      this.suddenDeathScores = [state.suddenDeathScores[0], state.suddenDeathScores[1]];
    }
    if (state.suddenDeathRound !== undefined) {
      this.suddenDeathRound = state.suddenDeathRound;
    }
    if (state.bustHistory) {
      this.bustHistory = state.bustHistory.slice();
    }
    if (state.phaseHistory) {
      this.phaseHistory = state.phaseHistory.slice();
    }

    // Update PPR from received game state (sync both players' PPR across clients)
    if (state.player1PPR !== undefined && this.player1) {
      this.player1.avgPPR = state.player1PPR;
      if (this.currentUser.id === this.player1.id) {
        this.currentUser.avgPPR = state.player1PPR;
      }
    }
    if (state.player2PPR !== undefined && this.player2) {
      this.player2.avgPPR = state.player2PPR;
      if (this.currentUser.id === this.player2.id) {
        this.currentUser.avgPPR = state.player2PPR;
      }
    }

    // CRITICAL: Only append opponent's rolls when game is still active (not over)
    // Once phase='over', all rolls have been recorded and we should not append any more
    // This prevents corruption from stale/duplicate roll data in finalGameState
    if (previousPhase !== 'over' && state.phase !== 'over') {
      if (typeof state.lastRollPlayer === 'number' && typeof state.lastRollPts === 'number') {
        this.appendRollToPlayer(state.lastRollPlayer, state.lastRollPts);
      }
    }

    // Update player cards with new PPR values
    // Pass tournament-specific W-L records to renderer for round-robin tournaments
    if (this.currentTournamentType === 'round_robin') {
      this.renderer.setTournamentPlayerRecords(this.tournamentPlayerRecords);
    }
    this.renderer.updatePlayerCards(this.player1, this.player2);

    // Update UI immediately
    this.renderer.round = this.round;
    const wasMyTurn = this.isMyTurn;
    // Observers are always passive - never set isMyTurn to true
    if (!this.isObserver) {
      this.isMyTurn = this.computeIsMyTurnForCurrentPlayer();
    }
    this.renderer.setIsMyTurn(this.isMyTurn);
    this.setBallColorForCurrentPlayer();

    // console.log('[applyFinalGameState] Passive player turn setup:', {
    //   currentPlayer: this.currentPlayer,
    //   isHost: this.isHost,
    //   isMyTurn: this.isMyTurn,
    //   wasMyTurn,
    //   phase: this.phase
    // });

    // Refresh HUD/widgets so passive player sees current round cell update
    this.renderer.updateHUD(
      this.currentPlayer,
      this.getDisplayScores(),
      this.turnHits,
      this.isSkeechPossible,
      this.skeechSuccess,
      this.wildcardHit,
      this.player1 || undefined,
      this.player2 || undefined,
      this.players,
      this.phase,
      this.bustHistory
    );

    // CRITICAL: If turn switched to ME, clear any lingering sink animation from opponent's roll
    // IMMEDIATELY (before next physics.step() runs) to prevent physics callbacks
    // from firing onSink/nextBall with opponent's pts
    if (!wasMyTurn && this.isMyTurn && this.physics.sink.active) {
      // console.log('[applyFinalGameState] IMMEDIATE: Clearing opponent sink animation (pts:', this.physics.sink.pts, ') on turn switch');
      this.physics.sink.active = false;
      this.physics.sink.pts = 0;
    }

    const activePlayerName = this.currentPlayer === 0
      ? this.player1?.userName || 'Player 1'
      : this.player2?.userName || 'Player 2';
    const isActivePlayerMe = this.isMyTurn;
    this.renderer.setTurnIndicator(true, activePlayerName, isActivePlayerMe);

    // Always update ball count display immediately to keep UI in sync
    this.renderer.setBallsLeft(Math.max(0, this.ballsLeft));
    // console.log('[applyFinalGameState] Called renderer.setBallsLeft with:', this.ballsLeft);

    // Update HUD immediately
    this.renderer.updateHUD(
      this.currentPlayer,
      this.getDisplayScores(),
      this.turnHits,
      this.isSkeechPossible,
      this.skeechSuccess,
      this.wildcardHit,
      this.player1 || undefined,
      this.player2 || undefined,
      this.players,
      this.phase,
      this.bustHistory
    );

    // Handle phase transitions
    if (previousPhase !== this.phase) {
      this.handlePhaseTransition(previousPhase, this.phase);
    }

    // Handle turn setup - delay if sink animation is playing
    const setupTurn = () => {      

      if (this.isMyTurn && this.ballsLeft === this.ballsPerTurn) {
        // It's now my turn (first ball of my turn)        

        // CRITICAL: Clear any lingering sink animation from opponent's roll
        // to prevent physics.step() from firing onSink with opponent's pts
        if (this.physics.sink.active) {
          this.physics.sink.active = false;
          this.physics.sink.pts = 0;
        }

        this.setupMyTurn();
      } else if (this.isMyTurn && this.phase === 'sudden_death') {
        // Edge case: in sudden death, if we get out-of-order state (ballsLeft !== 4),
        // still reset the ball so it's not stuck in a hole        
        this.resetBall();
      } else if (!this.isMyTurn && this.ballsLeft > 0) {
        // Still opponent's turn, more balls to watch - reset ball for next roll        
        this.resetBall();
      } else {
        // console.log('[setupTurn] No action taken - conditions not met');
      }

      // Send ready signal after ball setup (observers never send signals)
      if (!this.isObserver && this.matchControls) {
        this.matchControls.sendReadyForNext({
          kind: 'ready_for_next',
          round: this.round,
          ballIndex: 4 - this.ballsLeft,
          userId: this.currentUser.id
        });
      }
    };

    if (delayBallResetMs > 0) {
      // Delay ball reset until after sink animation completes
      window.setTimeout(setupTurn, delayBallResetMs);
    } else {
      // No delay needed - setup turn immediately
      setupTurn();
    }
  }

  private handlePhaseTransition(from: string, to: string): void {
    if (to === 'retribution') {
      this.renderer.showRetributionOverlay();
      this.renderer.clearBoardLighting();

      if (this.isMyTurn) {
        this.renderer.showRetributionOnBoard(this.currentUser.userName, 4000);        
      }

      this.resetBall();
      this.renderer.setBallsLeft(this.ballsLeft);

    } else if (to === 'sudden_death') {
      this.renderer.hideRetributionOverlay();
      this.renderer.showSuddenDeathOverlay();

      const activePlayerName = this.isMyTurn ? this.currentUser.userName : this.opponent?.userName;
      this.renderer.showSuddenDeathOnBoard(this.suddenDeathRound, activePlayerName ?? '', 4000);

      this.resetBall();
      this.renderer.setBallsLeft(this.ballsLeft);

    } else if (to === 'over') {
      // Game ended - determine winner and display game over screen
      console.log('[handlePhaseTransition] Game over - determining winner', {
        suddenDeathScores: this.suddenDeathScores,
        scores: this.scores,
        phase: this.phase
      });

      // Passive player: Wait for active player's save, then refresh W-L stats
      // Active player: Their save will trigger the W-L refresh in finishGameSave()
      if (!this.isMyTurn && this.isMultiplayer && this.matchControls?.matchId) {
        this.waitForGameSaveAndRefreshStats(this.matchControls.matchId);
      }

      // In sudden death, use suddenDeathScores to determine winner
      // In normal/retribution phases, use regular scores
      const finalScores = from === 'sudden_death' ? this.suddenDeathScores : this.scores;
      const player0Score = finalScores[0];
      const player1Score = finalScores[1];

      let winner: IUser | null = null;
      let loser: IUser | null = null;
      let winnerScore = 0;
      let loserScore = 0;

      if (player1Score > player0Score) {
        winner = this.player2;
        loser = this.player1;
        winnerScore = player1Score;
        loserScore = player0Score;
      } else if (player0Score > player1Score) {
        winner = this.player1;
        loser = this.player2;
        winnerScore = player0Score;
        loserScore = player1Score;
      } else {
        // Tie - shouldn't happen in normal flow, but handle gracefully
        console.warn('[handlePhaseTransition] Game ended in a tie - this should not happen');
        winner = this.player1;
        loser = this.player2;
        winnerScore = player0Score;
        loserScore = player1Score;
      }

      // Hide overlays
      this.renderer.hideRetributionOverlay();
      this.renderer.hideSuddenDeathOverlay();

      // Display game over screen
      if (winner && loser) {
        setTimeout(() => {
          this.renderer.endGame(winner!, loser, winnerScore, loserScore, this.currentUser);
        }, 100);
      }
    }
  }

  private async waitForGameSaveAndRefreshStats(gameId: string): Promise<void> {
    console.log('[waitForGameSaveAndRefreshStats] Passive player waiting for active player to save game');

    const maxAttempts = 10;
    const delayMs = 500;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, delayMs));

      try {
        // Check if game has been marked completed
        const game = await this.repo.getGameById(gameId);

        if (game && game.status === 'completed') {
          console.log('[waitForGameSaveAndRefreshStats] Game marked completed, refreshing W-L stats');

          // Fetch fresh W-L stats for both players
          if (this.player1 && this.player2) {
            // For round robin tournaments, reload standings to get updated W-L
            if (this.currentTournamentType === 'round_robin' && this.currentTournamentContext) {
              console.log('[waitForGameSaveAndRefreshStats] Refreshing round-robin W-L stats from tournament standings');
              try {
                const tournament = await (this.repo as any).sb?.getTournamentById(this.currentTournamentContext.tournamentId);
                if (tournament?.round_robin_data?.standings) {
                  const standings = tournament.round_robin_data.standings;
                  standings.forEach((entry: any) => {
                    this.tournamentPlayerRecords[entry.userId] = {
                      wins: entry.wins ?? 0,
                      losses: entry.losses ?? 0
                    };
                  });
                  console.log('[waitForGameSaveAndRefreshStats] Updated tournament W-L records:', this.tournamentPlayerRecords);

                  // Update renderer with fresh tournament records
                  this.renderer.setTournamentPlayerRecords(this.tournamentPlayerRecords);
                  this.renderer.updatePlayerCards(this.player1, this.player2);
                }
              } catch (e) {
                console.error('[waitForGameSaveAndRefreshStats] Failed to refresh tournament W-L', e);
              }
            } else if (!this.currentTournamentContext) {
              // For non-tournament games, calculate W-L from all multiplayer games
              console.log('[waitForGameSaveAndRefreshStats] Refreshing W-L stats for non-tournament game');
              const p1WL = await this.repo.calculateWinLoss(this.player1.id, { excludeTournaments: true, excludeAI: true });
              const p2WL = await this.repo.calculateWinLoss(this.player2.id, { excludeTournaments: true, excludeAI: true });

              this.player1.wins = p1WL.wins;
              this.player1.losses = p1WL.losses;
              this.player1.gamesPlayed = p1WL.gamesPlayed;
              this.player2.wins = p2WL.wins;
              this.player2.losses = p2WL.losses;
              this.player2.gamesPlayed = p2WL.gamesPlayed;

              console.log('[waitForGameSaveAndRefreshStats] Updated W-L stats:', {
                player1: { name: this.player1.userName, ...p1WL },
                player2: { name: this.player2.userName, ...p2WL }
              });

              // Update player card display
              this.renderer.updatePlayerCards(this.player1, this.player2);
            }
            // Bracket tournaments don't show W-L, so no update needed
          }

          return; // Success - exit
        }

        console.log(`[waitForGameSaveAndRefreshStats] Attempt ${attempt + 1}/${maxAttempts}: Game not completed yet`);
      } catch (error) {
        console.error(`[waitForGameSaveAndRefreshStats] Error checking game status:`, error);
      }
    }

    console.warn('[waitForGameSaveAndRefreshStats] Timeout waiting for game save - active player may have disconnected');
  }

  private appendRollToPlayer(playerIndex: 0 | 1, rollValue: number) {
    const player = playerIndex === 0 ? this.player1 : this.player2;
    if (!player) return;
    if (!player.current_game_rolls) {
      player.current_game_rolls = [];
    }

    // PROTECTION: For passive players and observers, check if we're receiving a state sync for a roll we already have
    // Track expected roll count based on game state (ballsPerTurn=4, so each turn should have 1-4 rolls)
    // The issue is that state syncs can arrive multiple times with the same lastRollPts
    if (!this.isMyTurn) {
      // Calculate expected rolls for current turn
      const expectedRollsThisTurn = this.ballsPerTurn - this.ballsLeft;
      const currentPlayerRolls = player.current_game_rolls.length;

      // If we already have enough rolls for this turn state, skip appending
      // (We'll get the authoritative history when turn transitions)
      if (currentPlayerRolls >= expectedRollsThisTurn && expectedRollsThisTurn > 0) {
        try {
          console.log('[appendRollToPlayer] SKIPPED - Already have expected rolls for this turn state', {
            playerIndex,
            rollValue,
            currentRolls: currentPlayerRolls,
            expectedRolls: expectedRollsThisTurn,
            ballsLeft: this.ballsLeft
          });
        } catch {}
        return;
      }
    }

    player.current_game_rolls.push(rollValue);
    try {
      console.log('[appendRollToPlayer]', {
        playerIndex,
        rollValue,
        newLength: player.current_game_rolls.length,
        historyPreview: player.current_game_rolls.slice(-8),
        isLocalPlayer: player.id === this.currentUser.id,
        isPassiveClient: !this.isMyTurn && !this.isObserver,
        round: this.round,
        phase: this.phase
      });
    } catch {}
    // IMPORTANT: Only recalculate PPR if we're the active player
    // Passive players and observers receive authoritative PPR in the game state
    // and should NOT recalculate it from roll history (which might be incomplete/stale)
    if (this.isMyTurn) {
      recalcAverages(player);
    }
    // Keep shortcuts in sync for convenience
    if (this.currentUser.id === player.id) {
      this.currentUser.current_game_rolls = [...player.current_game_rolls];
    }
    if (this.opponent && this.opponent.id === player.id) {
      this.opponent.current_game_rolls = player.current_game_rolls;
    }
  }

  private recordPhaseEntry(phase: 'retribution' | 'sudden_death', round: number, suddenDeathRound?: number) {
    const key = `${phase}:${round}:${suddenDeathRound ?? 0}`;
    if (this.phaseHistory.some(entry => `${entry.phase}:${entry.round}:${entry.suddenDeathRound ?? 0}` === key)) {
      return;
    }
    this.phaseHistory.push({ phase, round, suddenDeathRound });
  }

  private mergePhaseHistory(previous?: GameStateSync['phaseHistory'], incoming?: GameStateSync['phaseHistory']): GameStateSync['phaseHistory'] | undefined {
    if (!previous && !incoming) return undefined;
    const map = new Map<string, { phase: 'retribution' | 'sudden_death'; round: number; suddenDeathRound?: number }>();
    (previous || []).concat(incoming || []).forEach(entry => {
      const key = `${entry.phase}:${entry.round}:${entry.suddenDeathRound ?? 0}`;
      if (!map.has(key)) {
        map.set(key, entry);
      }
    });
    return Array.from(map.values());
  }

  private recordBustForCurrentPlayer() {
    const entry = { player: this.currentPlayer, round: this.round };
    const exists = this.bustHistory.some(e => e.player === entry.player && e.round === entry.round);
    if (!exists) {
      this.bustHistory.push(entry);
    }
  }

  private mergeBustHistory(previous?: GameStateSync['bustHistory'], incoming?: GameStateSync['bustHistory']): GameStateSync['bustHistory'] | undefined {
    if (!previous && !incoming) return undefined;
    const map = new Map<string, { player: 0 | 1; round: number }>();
    (previous || []).concat(incoming || []).forEach(entry => {
      map.set(`${entry.player}:${entry.round}`, entry);
    });
    return Array.from(map.values());
  }

  private mergeFinalGameState(previous: GameStateSync | null, incoming: GameStateSync): GameStateSync {
    if (!previous) {
      return incoming;
    }
    const merged: GameStateSync = { ...previous, ...incoming };
    if (incoming.lastRollPlayer === undefined && previous.lastRollPlayer !== undefined) {
      merged.lastRollPlayer = previous.lastRollPlayer;
      merged.lastRollPts = previous.lastRollPts;
      merged.lastRollResult = previous.lastRollResult;
    }
    merged.bustHistory = this.mergeBustHistory(previous?.bustHistory, incoming.bustHistory);
    merged.phaseHistory = this.mergePhaseHistory(previous?.phaseHistory, incoming.phaseHistory);
    return merged;
  }

  private setupMyTurn(): void {
    console.log('[setupMyTurn] Player roll history state:', {
      currentUser: this.currentUser.userName,
      currentPlayer: this.currentPlayer,
      player1: {
        name: this.player1?.userName,
        rolls: this.player1?.current_game_rolls
      },
      player2: {
        name: this.player2?.userName,
        rolls: this.player2?.current_game_rolls
      },
      opponent: {
        name: this.opponent?.userName,
        rolls: this.opponent?.current_game_rolls
      }
    });

    this.renderer.setBallsLeft(this.ballsLeft);
    this.renderer.setReserveAtLaunch(true);
    this.physics.setTiltDirection(this.currentPlayer);
    this.physics.jitterTilt();
    this.resetBall();    
  }

  private autoRollTestBall() {
  if (!this.testMode) return;

  this.testStats.totalRolls++;

  // Reset ball position
  this.resetBall();

  // Use test parameters from the UI controls
  const vx = this.testParams.vx;
  const vy = this.testParams.vy;
  const spin = this.testParams.spin;
  const tiltOffset = this.testParams.tiltX;

  this.ball.vel = { x: vx, y: vy };
  this.ball.spin = spin;
  this.ball.rolling = true;
  this.renderer.updateNextBallButtonState();

  // Use consistent tilt: base tilt (randomized when entering test mode) + slider offset
  this.physics.tiltX = this.testBaseTilt + tiltOffset;

  // Update speed widget for test rolls
  const totalSpeed = Math.hypot(this.ball.vel.x, this.ball.vel.y);
  this.renderer.updateSpeedWidgets(totalSpeed);
}

  // Test mode: Track rimming stats for this roll
  private trackTestRollStats() {
    if (!this.testMode) return;

    const orbitCount = this.physics.maxOrbitsThisRoll;

    // Track Apollo 13: 0.75-1 orbit AND sinking into different hole
    if (orbitCount >= 0.75 && orbitCount <= 1.0) {
      const isDifferentHole = this.physics.rimmingHoleCenter && this.physics.finalSinkHoleCenter &&
        (this.physics.rimmingHoleCenter.cx !== this.physics.finalSinkHoleCenter.cx ||
         this.physics.rimmingHoleCenter.cy !== this.physics.finalSinkHoleCenter.cy);

      if (isDifferentHole) {
        this.testStats.apollo13Count++;
      }
    }

    // Track rolls with >0.25 orbit
    if (orbitCount > 0.25) {
      this.testStats.rollsWithMoreThan025Orbit++;

      // If ball had orbits and didn't sink, it must have escaped
      if (this.physics.didSink || this.physics.finalSinkHoleCenter) {
        this.testStats.rollsWithMoreThan025OrbitThatSank++;
      } else {
        // Ball rimmed but didn't sink = escaped
        this.testStats.rollsWithMoreThan025OrbitThatEscaped++;
      }
    }

    // Track rolls with >1 orbit
    if (orbitCount > 1.0) {
      this.testStats.rollsWithMoreThan1Orbit++;

      // If ball had orbits and didn't sink, it must have escaped
      if (this.physics.didSink || this.physics.finalSinkHoleCenter) {
        this.testStats.rollsWithMoreThan1OrbitThatSank++;
      } else {
        // Ball rimmed but didn't sink = escaped
        this.testStats.rollsWithMoreThan1OrbitThatEscaped++;
      }
    }

    // Track railroads
    if (this.physics.wasRailroad) {
      this.testStats.railroadCount++;
    }
  }

  private handleReached21(method: 'skeech' | 'traditional'): void {
    const isSkeech = method === 'skeech';

    if (this.phase === 'normal') {
      console.log('[PHASE] handleReached21: entering retribution from normal', { time: new Date().toISOString(), method, scores: this.scores.slice(), currentPlayer: this.currentPlayer, round: this.round, turnHits: [...this.turnHits] });
      // Always move to retribution when someone reaches 21 from normal play,
      // even in AI challenge (non-multiplayer) games.
      this.phase = 'retribution';
      this.recordPhaseEntry('retribution', this.round);
      console.log('[PHASE] handleReached21: phase set to retribution (active)', { time: new Date().toISOString(), isHost: this.isHost, isMultiplayer: this.isMultiplayer });
      // Cancel any pending end-of-turn transition when entering retribution
      this.pendingTurnTransition = false;
      if (this.pendingTurnTransitionTimer) { clearTimeout(this.pendingTurnTransitionTimer); this.pendingTurnTransitionTimer = null; }
      try { this.renderer.clearBoardLighting(); } catch {}
      // Clear skeech tracker immediately when entering retribution
      this.isSkeechPossible = false;
      this.skeechSuccess = false;
      this.wildcardHit = null;
      this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
    } else if (this.phase === 'retribution') {
      // Cancel any pending turn-transition that was scheduled at end of last ball
      // to avoid immediately ending retribution before the player rolls.
      this.pendingTurnTransition = false;
      if (this.pendingTurnTransitionTimer) { clearTimeout(this.pendingTurnTransitionTimer); this.pendingTurnTransitionTimer = null; }
      this.phase = 'sudden_death';
      this.recordPhaseEntry('sudden_death', this.round, this.suddenDeathRound);
      try { this.renderer.clearBoardLighting(); } catch {}
      // Clear skeech tracker immediately when entering sudden death
      this.isSkeechPossible = false;
      this.skeechSuccess = false;
      this.wildcardHit = null;
      this.renderer.updateHUD(this.currentPlayer, this.getDisplayScores(), this.turnHits, this.isSkeechPossible, this.skeechSuccess, this.wildcardHit, this.player1 || undefined, this.player2 || undefined, this.players, this.phase, this.bustHistory);
    } else if (this.phase === 'sudden_death') {
      if (this.currentPlayer === 0) {
        this.handleTurnTransition();
        return;
      } else {
        this.evaluateSuddenDeathRound();
        return;
      }
    }

    if (isSkeech) {
      const skeechTriggers: Record<Phase, string> = {
        'over': 'skeechAchieved',
        'retribution': 'skeechLeadsToRetribution',
        'sudden_death': 'skeechTiesSuddenDeath',
        'normal': 'skeechAchieved'
      };
      const skeechTrigger = skeechTriggers[this.phase];

      const gameState: GameState = {
        consecutiveDitches: 0,
        skeechAchieved: skeechTrigger === 'skeechAchieved',
        skeechLeadsToRetribution: skeechTrigger === 'skeechLeadsToRetribution',
        skeechTiesSuddenDeath: skeechTrigger === 'skeechTiesSuddenDeath',
        skeechEndsSuddenDeath: skeechTrigger === 'skeechEndsSuddenDeath'
      };

      messageManager.clearCurrentMessage();
      this.renderer.clearPinballMessages();
      const messageData = messageManager.getMessageWithPriority(this.currentUser, gameState);
      if (messageData) {
        this.renderer.showPositionedMessage(messageData.text, messageData.message);
      }
    }

    if (this.phase === 'over') {
      setTimeout(() => {
        this.finishGameSave();
        const winner = this.currentUser;
        const loser = this.players === 2 ? (this.currentUser.id === this.player1?.id ? this.player2 : this.player1) : null;
        this.renderer.endGame(winner, loser, this.scores[0], 0, this.currentUser);
      }, 0);
    } else if (this.phase === 'retribution') {
      const delay = isSkeech ? 1000 : 2000;
      setTimeout(() => {
        this.startRetributionPhase();

        if (this.isMultiplayer) {
          // Send a fresh phase transition message, NOT reusing old myRollResult which contains
          // stale ballIndex/result/pts from the ball that triggered retribution.
          // Reusing causes the passive player to process the old scoring data as if they scored it themselves.
          const phaseTransitionMessage: RollResult = {
            kind: 'roll_result',
            round: this.round,
            ballIndex: 0, // New turn starting (retribution player's first ball)
            result: 'timeout', // Use 'timeout' as a neutral result that won't trigger scoring
            gameState: {
              scores: [this.scores[0], this.scores[1]],
              ballsLeft: this.ballsLeft,
              currentPlayer: this.currentPlayer,
              round: this.round,
              turnHits: [...this.turnHits],
              isSkeechPossible: this.isSkeechPossible,
              skeechSuccess: this.skeechSuccess,
              wildcardHit: this.wildcardHit,
              phase: this.phase,
              suddenDeathScores: [this.suddenDeathScores[0], this.suddenDeathScores[1]],
              suddenDeathRound: this.suddenDeathRound
            }
          };
          // REMOVED: State now sent with path chunk finalGameState
          // if (this.matchControls) {
          //   this.matchControls.sendRollResult(phaseTransitionMessage);
          // }
        }
      }, delay);
    } else if (this.phase === 'sudden_death') {
      setTimeout(() => {
        this.renderer.hideRetributionOverlay();
        this.renderer.showSuddenDeathOverlay();
        this.startSuddenDeath();

        if (this.isMultiplayer) {
          this.myRollResult.gameState = {
            scores: [this.scores[0], this.scores[1]],
            ballsLeft: this.ballsLeft,
            currentPlayer: this.currentPlayer,
            round: this.round,
            turnHits: [...this.turnHits],
            isSkeechPossible: this.isSkeechPossible,
            skeechSuccess: this.skeechSuccess,
            wildcardHit: this.wildcardHit,
            phase: this.phase,
            suddenDeathScores: [this.suddenDeathScores[0], this.suddenDeathScores[1]],
            suddenDeathRound: this.suddenDeathRound
          };
          // REMOVED: State now sent with path chunk finalGameState
          // if (this.matchControls) {
          //   this.matchControls.sendRollResult(this.myRollResult);
          // }
        }
      }, 2000);
    }
  }
}
const sk = new Skeech();
sk.init();
